import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  UtensilsCrossed, LayoutGrid, Menu, Bell, BellRing, BellOff, MessageSquare, QrCode, Settings,
  LogOut, Plus, Trash2, Edit2, Send, GripVertical, Users, Droplets,
  ClipboardList, Receipt, CheckCircle, AlertCircle, History, BarChart3,
  Eye, EyeOff, Move, RotateCcw, Inbox, Upload, ImageIcon, MoreHorizontal, Star,
  Lock, Unlock, ChevronDown, ChevronRight, Clock, ShieldCheck, UserPlus, UserMinus, Check, Printer, X, Download, Languages
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { requestNotificationPermission, showAlert } from '@/utils/notifier';
import { formatPrice, currencyFromCountryCode } from '@/utils/currency';
import { countryFromPhone } from '@/utils/country';
import CurrencyPicker from '@/components/CurrencyPicker';
import { LANGUAGES } from '@/utils/i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws');

const ICON_MAP = { Bell, Droplets, ClipboardList, Receipt, AlertCircle, Users, CheckCircle, UtensilsCrossed, Menu };
function getIcon(name) { return ICON_MAP[name] || Bell; }

function isLightColor(hex) {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
}

// Mix a hex color toward black (factor 0..1) to get a darker shade — used as
// a card border so it visually anchors to the marker's fill on the layout.
function darkenColor(hex, factor = 0.35) {
  const c = (hex || '#FFFFFF').replace('#', '');
  if (c.length !== 6) return '#0EA5E9'; // fallback sky-500
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const m = (n) => Math.round(n * (1 - factor)).toString(16).padStart(2, '0');
  return `#${m(r)}${m(g)}${m(b)}`;
}
// Mix a hex color toward white to get a lighter tint — used as the seats fill.
function tintColor(hex, factor = 0.78) {
  const c = (hex || '#FFFFFF').replace('#', '');
  if (c.length !== 6) return '#E0F2FE'; // fallback sky-100
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const m = (n) => Math.round(n + (255 - n) * factor).toString(16).padStart(2, '0');
  return `#${m(r)}${m(g)}${m(b)}`;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('tables');
  const [restaurant, setRestaurant] = useState(null);
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [callReasons, setCallReasons] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [chatMessages, setChatMessages] = useState({});
  const [selectedChatTable, setSelectedChatTable] = useState(null);
  const [alertTables, setAlertTables] = useState(new Set());
  const [notifications, setNotifications] = useState([]);
  const [trialPopup, setTrialPopup] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [staffAccess, setStaffAccess] = useState(null);
  const [focusTableId, setFocusTableId] = useState(null);
  const role = user?.role || 'host';
  const isStaff = role === 'staff';
  const goToLiveCallsForTable = useCallback((tableId) => {
    setFocusTableId(tableId);
    setActiveTab('calls');
  }, []);
  // -------------------- Voice (TTS) Announcer --------------------
  // Reads "Table {label}" out loud whenever a new alert (active call) appears,
  // and re-speaks every `tts_repeat_interval` seconds up to `tts_repeat_count`
  // times until the call is served/dismissed. Uses the browser's free
  // SpeechSynthesis API so no third-party integration is required.
  const speechTimersRef = useRef(new Map()); // call_id -> Set<timeout id>
  const announcedCallIdsRef = useRef(new Set());
  const ttsEnabled = !!restaurant?.tts_enabled;
  const ttsVoice = restaurant?.tts_voice || '';
  const ttsRepeatCount = Math.max(1, Math.min(5, restaurant?.tts_repeat_count || 3));
  const ttsRepeatInterval = Math.max(5, Math.min(180, restaurant?.tts_repeat_interval || 30));
  const speakLabel = useCallback((label) => {
    if (!('speechSynthesis' in window)) return;
    try {
      const utter = new SpeechSynthesisUtterance(`Table ${label}`);
      const voices = window.speechSynthesis.getVoices();
      const match = voices.find(v => v.voiceURI === ttsVoice) || voices.find(v => v.name === ttsVoice);
      if (match) utter.voice = match;
      utter.rate = 0.95;
      utter.pitch = 1.0;
      utter.volume = 1.0;
      // Cancel anything mid-flight so adjacent table alerts don't overlap.
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    } catch { /* speechSynthesis failures are non-fatal */ }
  }, [ttsVoice]);
  // Watch activeCalls for new alerts and (re)schedule speech for each.
  useEffect(() => {
    if (!ttsEnabled) return;
    const live = activeCalls.filter(c => c.status === 'active');
    const liveIds = new Set(live.map(c => c.id));
    // Cancel timers for calls that are no longer active.
    for (const [cid, timers] of speechTimersRef.current.entries()) {
      if (!liveIds.has(cid)) {
        timers.forEach(t => clearTimeout(t));
        speechTimersRef.current.delete(cid);
      }
    }
    // Schedule speech for any newly-arrived active call we haven't announced.
    live.forEach(call => {
      if (announcedCallIdsRef.current.has(call.id)) return;
      announcedCallIdsRef.current.add(call.id);
      // Speak immediately, then schedule repeats while still-active.
      speakLabel(call.table_label);
      const timers = new Set();
      for (let i = 1; i < ttsRepeatCount; i++) {
        const t = setTimeout(() => {
          // Re-check if still active before speaking (state may have changed).
          if (speechTimersRef.current.has(call.id)) speakLabel(call.table_label);
        }, i * ttsRepeatInterval * 1000);
        timers.add(t);
      }
      speechTimersRef.current.set(call.id, timers);
    });
  }, [activeCalls, ttsEnabled, ttsRepeatCount, ttsRepeatInterval, speakLabel]);
  // Cleanup all timers and any in-flight speech on unmount.
  useEffect(() => () => {
    for (const timers of speechTimersRef.current.values()) timers.forEach(t => clearTimeout(t));
    speechTimersRef.current.clear();
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  }, []);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const alertPollRef = useRef(null);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');

  const fetchData = useCallback(async () => {
    try {
      const [rest, tbl, cats, items, reasons, calls, notifs, subStatus, siteS] = await Promise.all([
        axios.get(`${API}/restaurant`, { withCredentials: true }),
        axios.get(`${API}/tables`, { withCredentials: true }),
        axios.get(`${API}/menu-categories`, { withCredentials: true }),
        axios.get(`${API}/menu-items`, { withCredentials: true }),
        axios.get(`${API}/call-reasons`, { withCredentials: true }),
        axios.get(`${API}/calls`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/subscription/status`, { withCredentials: true }),
        axios.get(`${API}/site-settings`),
      ]);
      setRestaurant(rest.data);
      setTables(tbl.data);
      setCategories(cats.data);
      setMenuItems(items.data);
      setCallReasons(reasons.data);
      setActiveCalls(calls.data);
      setNotifications(notifs.data);
      setSiteSettings(siteS.data || {});
      // Check trial popup
      const sub = subStatus.data;
      setSubscription(sub);
      if (sub.status === 'trial' && sub.show_trial_popup) {
        setTrialPopup(sub);
      }
    } catch (err) {
      console.error('Failed to fetch data', err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load staff_access toggles so we can hide tabs for staff role.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/access-control`, { withCredentials: true });
        if (!cancelled) setStaffAccess(data?.staff_access || null);
      } catch { /* fallback: keep null */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep-alive: silently refresh the JWT access token every 30 minutes while
  // the host dashboard is open. Prevents silent session timeouts — host stays
  // signed in until they explicitly log out.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      } catch { /* ignore — if refresh fails, the user's next protected call will 401 and redirect */ }
    };
    // Run once right after mount, then every 30 min.
    tick();
    const id = setInterval(() => { if (!cancelled) tick(); }, 30 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Confirm before closing the host portal (accidental tab-close safeguard).
  useEffect(() => {
    const onBeforeUnload = (e) => {
      // Modern browsers ignore the custom string and show their own prompt,
      // but we MUST set preventDefault + returnValue for the prompt to appear.
      e.preventDefault();
      e.returnValue = 'Are you sure you want to close the host portal?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Request browser notification permission once on mount (requires HTTPS which preview domain has)
  useEffect(() => {
    requestNotificationPermission().then(p => setNotifPerm(p)).catch(() => {});
  }, []);

  const enableAlerts = async () => {
    const p = await requestNotificationPermission();
    setNotifPerm(p);
    if (p === 'granted') {
      toast.success('Alerts enabled — you\'ll get popups and sound for new calls');
      try {
        showAlert({ title: 'OLAFE alerts enabled', body: 'You will be notified of table calls and messages.', tag: 'olafe-test' });
      } catch {}
    } else if (p === 'denied') {
      toast.error('Alerts blocked. Allow notifications in browser settings to receive popups.');
    }
  };

  // WebSocket for receiving real-time notifications
  useEffect(() => {
    if (!restaurant) return;
    let ws;
    let reconnectTimer;
    const connect = () => {
      ws = new WebSocket(`${WS_BASE}/api/ws/host/${restaurant.id}`);
      wsRef.current = ws;
      ws.onopen = () => console.log('Host WS connected');
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'new_call') {
            setActiveCalls(prev => {
              if (prev.find(c => c.id === msg.call.id)) return prev;
              return [...prev, msg.call];
            });
            // Mark table as alerting
            setAlertTables(prev => new Set([...prev, msg.call.table_id]));
            toast.info(`Table ${msg.call.table_label}: ${msg.call.reason_label}`);
            // OS-level popup + sound (works when tab minimized)
            showAlert({
              title: `Table ${msg.call.table_label} is calling`,
              body: msg.call.reason_label || 'Service requested',
              tag: `call-${msg.call.id}`,
            });
          } else if (msg.type === 'call_completed') {
            // Mark the card as completed (Done/Served) but keep it visible
            // until the host resets the table.
            setActiveCalls(prev => prev.map(c => c.id === msg.call_id ? { ...c, status: msg.new_status || 'dismissed' } : c));
          } else if (msg.type === 'call_dismissed') {
            // Backwards-compat with older backend events.
            setActiveCalls(prev => prev.map(c => c.id === msg.call_id ? { ...c, status: 'dismissed' } : c));
          } else if (msg.type === 'new_message') {
            const m = msg.message;
            setChatMessages(prev => {
              const existing = prev[m.table_id] || [];
              if (existing.find(e => e.id === m.id)) return prev;
              return { ...prev, [m.table_id]: [...existing, m] };
            });
            // Mark table as alerting if message is from customer
            if (m.sender_type === 'customer') {
              setAlertTables(prev => new Set([...prev, m.table_id]));
              // OS-level popup + sound for new customer message
              showAlert({
                title: `New message from Table ${m.table_label || ''}`.trim(),
                body: (m.content || '').slice(0, 120),
                tag: `msg-${m.table_id}`,
              });
            }
          } else if (msg.type === 'table_reset') {
            // Clear alert and chat for the reset table
            setAlertTables(prev => { const n = new Set(prev); n.delete(msg.table_id); return n; });
            setChatMessages(prev => ({ ...prev, [msg.table_id]: [] }));
            setActiveCalls(prev => prev.filter(c => c.table_id !== msg.table_id));
          } else if (msg.type === 'admin_message') {
            setNotifications(prev => [msg.message, ...prev]);
            toast.info(`Admin: ${msg.message.subject}`);
          }
        } catch {}
      };
      ws.onclose = () => {
        reconnectTimer = setTimeout(connect, 3000);
      };
      ws.onerror = () => {};
    };
    connect();
    return () => {
      clearTimeout(reconnectTimer);
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) ws.close();
    };
  }, [restaurant]);

  // Polling fallback for active calls + alerts
  useEffect(() => {
    if (!restaurant) return;
    const poll = async () => {
      try {
        const [callsRes, alertsRes] = await Promise.all([
          axios.get(`${API}/calls`, { withCredentials: true }),
          axios.get(`${API}/tables/alerts`, { withCredentials: true }),
        ]);
        setActiveCalls(callsRes.data);
        setAlertTables(new Set(alertsRes.data.alert_table_ids));
      } catch {}
    };
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [restaurant]);

  const loadChat = async (tableId) => {
    if (!restaurant) return;
    try {
      const { data } = await axios.get(`${API}/chat/${restaurant.id}/${tableId}`);
      setChatMessages(prev => ({ ...prev, [tableId]: data }));
      setSelectedChatTable(tableId);
      // Clear alert for this table when host opens chat
      setAlertTables(prev => { const n = new Set(prev); n.delete(tableId); return n; });
      setActiveTab('chat');
    } catch (err) {
      console.error(err);
    }
  };

  // Send host message via REST API (reliable)
  const sendHostMessage = async (content) => {
    if (!restaurant || !selectedChatTable) return;
    try {
      const { data } = await axios.post(`${API}/chat/${restaurant.id}/${selectedChatTable}`, {
        content,
        sender_type: 'host',
        sender_name: user?.name || 'Host'
      });
      // Optimistically add message (dedup in WS handler)
      setChatMessages(prev => {
        const existing = prev[selectedChatTable] || [];
        if (existing.find(m => m.id === data.id)) return prev;
        return { ...prev, [selectedChatTable]: [...existing, data] };
      });
    } catch {
      toast.error('Failed to send message');
    }
  };

  const dismissCall = async (callId) => {
    try {
      const call = activeCalls.find(c => c.id === callId);
      await axios.put(`${API}/calls/${callId}/dismiss`, {}, { withCredentials: true });
      // Keep the card visible — flip status to "dismissed" so UI renders the
      // "Done" completed state. Card disappears only on table reset.
      setActiveCalls(prev => prev.map(c => c.id === callId ? { ...c, status: 'dismissed', dismissed_at: new Date().toISOString() } : c));
      if (call) {
        const remaining = activeCalls.filter(c => c.id !== callId && c.table_id === call.table_id && c.status === 'active');
        if (remaining.length === 0) {
          setAlertTables(prev => { const n = new Set(prev); n.delete(call.table_id); return n; });
        }
      }
    } catch { toast.error('Failed to dismiss'); }
  };

  const markServed = async (callId) => {
    try {
      const call = activeCalls.find(c => c.id === callId);
      await axios.put(`${API}/calls/${callId}/serve`, {}, { withCredentials: true });
      setActiveCalls(prev => prev.map(c => c.id === callId ? { ...c, status: 'served', served_at: new Date().toISOString() } : c));
      if (call) {
        const remaining = activeCalls.filter(c => c.id !== callId && c.table_id === call.table_id && c.status === 'active');
        if (remaining.length === 0) {
          setAlertTables(prev => { const n = new Set(prev); n.delete(call.table_id); return n; });
        }
      }
      toast.success('Order marked served');
    } catch { toast.error('Failed to mark served'); }
  };

  const resetTable = async (tableId) => {
    try {
      const { data } = await axios.post(`${API}/tables/${tableId}/reset`, {}, { withCredentials: true });
      toast.success(data.message);
      // Clear local state for this table
      setAlertTables(prev => { const n = new Set(prev); n.delete(tableId); return n; });
      setChatMessages(prev => ({ ...prev, [tableId]: [] }));
      setActiveCalls(prev => prev.filter(c => c.table_id !== tableId));
      if (selectedChatTable === tableId) setSelectedChatTable(null);
    } catch { toast.error('Failed to reset table'); }
  };

  const toggleAvailability = async (itemId) => {
    try {
      const { data } = await axios.put(`${API}/menu-items/${itemId}/toggle-availability`, {}, { withCredentials: true });
      setMenuItems(prev => prev.map(i => i.id === itemId ? data : i));
    } catch { toast.error('Failed to toggle'); }
  };

  const unreadNotifs = notifications.filter(n => !n.read).length;

  const markNotifRead = async (msgId) => {
    try {
      await axios.put(`${API}/notifications/${msgId}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.id === msgId ? { ...n, read: true } : n));
    } catch {}
  };

  const frontendUrl = window.location.origin;

  return (
    <div data-testid="host-dashboard" className="min-h-screen bg-stone-50 flex flex-col lg:flex-row landscape:flex-row">
      {/* Sidebar — hidden on phones in portrait; visible at lg+ AND in landscape (so phone-landscape gets the side-by-side layout). */}
      <aside data-testid="host-dashboard-sidebar" className="hidden lg:flex landscape:flex w-64 landscape:w-52 lg:w-64 bg-white border-r border-stone-200 flex-shrink-0 flex-col sticky top-0 h-screen">
        <div className="p-5 border-b border-stone-200 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-orange-600 rounded-lg flex items-center justify-center">
              <UtensilsCrossed className="text-white" size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>CallButton</p>
              <p className="text-xs text-stone-500 truncate max-w-[140px]">{restaurant?.name || 'Loading...'}</p>
            </div>
          </div>
        </div>
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto min-h-0">
          {[
            { id: 'tables', icon: LayoutGrid, label: 'Tables', capability: 'tables' },
            { id: 'menu', icon: Menu, label: 'Menu', capability: 'food_menu' },
            { id: 'reasons', icon: Bell, label: 'Call Reasons', staffOnlyIfGranted: 'call_reasons', capability: 'call_reasons' },
            { id: 'calls', icon: AlertCircle, label: 'Live Calls', badge: activeCalls.filter(c => c.status === 'active').length, capability: 'live_calls' },
            { id: 'history', icon: History, label: 'Performance', staffOnlyIfGranted: 'performance', capability: 'performance' },
            { id: 'chat', icon: MessageSquare, label: 'Chat', capability: 'chat' },
            { id: 'qrcodes', icon: QrCode, label: 'QR Codes', staffOnlyIfGranted: 'qr_codes', capability: 'qr_codes' },
            { id: 'paging', icon: Printer, label: 'Paging', capability: 'paging' },
            { id: 'notifications', icon: Inbox, label: 'Inbox', badge: unreadNotifs, staffOnlyIfGranted: 'inbox', capability: 'inbox' },
            { id: 'access', icon: ShieldCheck, label: 'Access Control', hostOnly: true, capability: 'access_control' },
            { id: 'settings', icon: Settings, label: 'Settings', staffOnlyIfGranted: 'settings', capability: 'settings' },
          ].filter(item => {
            if (item.hostOnly && isStaff) return false;
            if (isStaff && item.staffOnlyIfGranted && !(staffAccess?.[item.staffOnlyIfGranted])) return false;
            // Package-level capability gate. Backend returns `package_features`
            // with all-true for grandfathered restaurants so this never silently
            // hides tabs for existing clients.
            if (item.capability && restaurant?.package_features && restaurant.package_features[item.capability] === false) return false;
            return true;
          }).map(item => (
            <button
              key={item.id}
              data-testid={`sidebar-${item.id}`}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === item.id
                  ? 'bg-orange-50 text-orange-700'
                  : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
              }`}
            >
              <item.icon size={18} />
              <span className="flex-1 text-left">{item.label}</span>
              {item.badge > 0 && (
                <Badge className="bg-orange-600 text-white text-xs px-2 py-0.5">{item.badge}</Badge>
              )}
            </button>
          ))}
        </nav>
        {/* Footer block — hidden in landscape on phones/tablets (< xl) so the
            top nav has full breathing room. Rotate to portrait to access
            Alerts / Sign Out, or use the desktop sidebar (≥ 1280px). */}
        <div className="p-3 border-t border-stone-200 space-y-1 shrink-0 bg-white landscape:max-xl:hidden">
          {/* Signed-in user — shown above the action buttons so the host always knows
              which account is active, especially when staff and host log in on shared devices. */}
          {user && (
            <div data-testid="sidebar-user-card" className="flex items-center gap-2 px-2 py-2 mb-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold uppercase shrink-0 ${
                isStaff ? 'bg-sky-100 text-sky-700' : 'bg-orange-100 text-orange-700'
              }`}>
                {(user.name || user.email || '?').trim().charAt(0)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-stone-800 truncate" title={user.name || user.email}>{user.name || user.email}</p>
                <p className="text-[10px] uppercase tracking-wide text-stone-400">{isStaff ? 'Staff' : user.role || 'Host'}</p>
              </div>
            </div>
          )}
          <button
            onClick={enableAlerts}
            data-testid="enable-alerts-btn"
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              notifPerm === 'granted'
                ? 'text-green-700 hover:bg-green-50'
                : notifPerm === 'denied'
                  ? 'text-stone-400 hover:bg-stone-50'
                  : 'text-orange-700 hover:bg-orange-50'
            }`}
            title={notifPerm === 'denied' ? 'Notifications blocked — enable in browser settings' : ''}
          >
            {notifPerm === 'granted' ? <BellRing size={18} /> : notifPerm === 'denied' ? <BellOff size={18} /> : <Bell size={18} />}
            <span className="flex-1 text-left">
              {notifPerm === 'granted' ? 'Alerts on' : notifPerm === 'denied' ? 'Alerts blocked' : 'Enable alerts'}
            </span>
          </button>
          <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-stone-500 hover:text-red-600 hover:bg-red-50 transition-all">
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
        {/* Compact icon-only footer — shown ONLY in landscape on phones/tablets
            (< xl). Replaces the full block above so the top nav stays roomy
            while Alerts and Sign Out remain one tap away. */}
        <div className="hidden landscape:max-xl:flex p-2 border-t border-stone-200 shrink-0 bg-white items-center justify-center gap-1">
          <button
            onClick={enableAlerts}
            data-testid="landscape-enable-alerts-btn"
            title={notifPerm === 'granted' ? 'Alerts on' : notifPerm === 'denied' ? 'Alerts blocked' : 'Enable alerts'}
            className={`p-2 rounded-lg transition-colors ${
              notifPerm === 'granted' ? 'text-green-600 hover:bg-green-50'
              : notifPerm === 'denied' ? 'text-stone-400 hover:bg-stone-50'
              : 'text-orange-600 hover:bg-orange-50'
            }`}
          >
            {notifPerm === 'granted' ? <BellRing size={18} /> : notifPerm === 'denied' ? <BellOff size={18} /> : <Bell size={18} />}
          </button>
          <button
            onClick={logout}
            data-testid="landscape-logout-btn"
            title="Sign Out"
            className="p-2 rounded-lg text-stone-500 hover:text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      {/* Compact top header — visible on phone/tablet portrait, hidden at lg+ and in landscape (where the sidebar takes over) */}
      <div className="lg:hidden landscape:hidden sticky top-0 z-40 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center">
            <UtensilsCrossed className="text-white" size={16} />
          </div>
          <div>
            <p className="text-sm font-bold text-stone-900 leading-tight" style={{ fontFamily: 'Outfit' }}>{restaurant?.name || 'Loading...'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={enableAlerts}
            data-testid="mobile-enable-alerts-btn"
            className={`transition-colors p-1.5 ${
              notifPerm === 'granted' ? 'text-green-600' : notifPerm === 'denied' ? 'text-stone-400' : 'text-orange-600'
            }`}
            title={notifPerm === 'granted' ? 'Alerts on' : notifPerm === 'denied' ? 'Alerts blocked' : 'Enable alerts'}
          >
            {notifPerm === 'granted' ? <BellRing size={18} /> : notifPerm === 'denied' ? <BellOff size={18} /> : <Bell size={18} />}
          </button>
          <button onClick={logout} data-testid="mobile-logout-btn" className="text-stone-400 hover:text-red-500 transition-colors p-1.5">
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* Main content — extra bottom padding when the bottom nav is visible (mobile/tablet portrait), normal padding once the sidebar is in place */}
      <main className="flex-1 w-full p-4 md:p-6 lg:p-8 overflow-y-auto pb-24 lg:pb-8 landscape:pb-8">
        {activeTab === 'tables' && <TablesPanel tables={tables} setTables={setTables} alertTables={alertTables} resetTable={resetTable} loadChat={loadChat} restaurant={restaurant} setRestaurant={setRestaurant} siteSettings={siteSettings} activeCalls={activeCalls} goToLiveCallsForTable={goToLiveCallsForTable} isStaff={isStaff} />}
        {activeTab === 'menu' && <MenuPanel categories={categories} setCategories={setCategories} menuItems={menuItems} setMenuItems={setMenuItems} toggleAvailability={toggleAvailability} currency={restaurant?.currency || 'USD'} isStaff={isStaff} />}
        {activeTab === 'reasons' && <CallReasonsPanel callReasons={callReasons} setCallReasons={setCallReasons} />}
        {activeTab === 'calls' && <LiveCallsPanel activeCalls={activeCalls} dismissCall={dismissCall} markServed={markServed} loadChat={loadChat} restaurantCurrency={restaurant?.currency || 'USD'} focusTableId={focusTableId} clearFocusTableId={() => setFocusTableId(null)} resetTable={resetTable} tables={tables} />}
        {activeTab === 'history' && <CallHistoryPanel restaurant={restaurant} setRestaurant={setRestaurant} />}
        {activeTab === 'chat' && <ChatPanel tables={tables} chatMessages={chatMessages} selectedChatTable={selectedChatTable} setSelectedChatTable={setSelectedChatTable} sendHostMessage={sendHostMessage} loadChat={loadChat} restaurant={restaurant} />}
        {activeTab === 'qrcodes' && <QRCodesPanel tables={tables} restaurant={restaurant} setRestaurant={setRestaurant} frontendUrl={frontendUrl} siteSettings={siteSettings} />}
        {activeTab === 'paging' && <PagingPanel restaurant={restaurant} setRestaurant={setRestaurant} frontendUrl={frontendUrl} isStaff={isStaff} staffAccess={staffAccess} />}
        {activeTab === 'notifications' && <NotificationsPanel notifications={notifications} markNotifRead={markNotifRead} />}
        {activeTab === 'access' && !isStaff && <AccessControlPanel staffAccess={staffAccess} setStaffAccess={setStaffAccess} />}
        {activeTab === 'settings' && <SettingsPanel restaurant={restaurant} setRestaurant={setRestaurant} subscription={subscription} />}
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav activeTab={activeTab} setActiveTab={setActiveTab} activeCalls={activeCalls} unreadNotifs={unreadNotifs} isStaff={isStaff} staffAccess={staffAccess} restaurant={restaurant} />

      {/* Trial Popup */}
      <Dialog open={!!trialPopup} onOpenChange={(open) => { if (!open) { axios.post(`${API}/subscription/dismiss-trial-popup`, {}, { withCredentials: true }).catch(() => {}); setTrialPopup(null); } }}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center" style={{ fontFamily: 'Outfit' }}>
              {trialPopup?.trial_expired ? 'Trial Expired' : 'Trial Period Reminder'}
            </DialogTitle>
            <p className="text-center text-sm text-stone-500">
              {trialPopup?.trial_expired
                ? 'Your free trial has ended. Subscribe to continue using all features.'
                : `You have ${trialPopup?.trial_days_remaining} day${trialPopup?.trial_days_remaining !== 1 ? 's' : ''} remaining in your free trial.`
              }
            </p>
          </DialogHeader>
          <div className="py-4 text-center">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${trialPopup?.trial_expired ? 'bg-red-100' : 'bg-orange-100'}`}>
              <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: trialPopup?.trial_expired ? '#DC2626' : '#EA580C' }}>
                {trialPopup?.trial_days_remaining || 0}
              </span>
            </div>
            <p className="text-sm text-stone-500">days remaining</p>
            {!trialPopup?.trial_expired && (
              <p className="text-xs text-stone-400 mt-2">We'll remind you again in 7 days.</p>
            )}
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button data-testid="trial-subscribe-btn" onClick={() => { axios.post(`${API}/subscription/dismiss-trial-popup`, {}, { withCredentials: true }).catch(() => {}); setTrialPopup(null); setPaymentDialogOpen(true); }} className="w-full bg-orange-600 hover:bg-orange-700 text-white">
              Subscribe Now
            </Button>
            {!trialPopup?.trial_expired && (
              <Button data-testid="trial-continue-btn" variant="ghost" onClick={() => { axios.post(`${API}/subscription/dismiss-trial-popup`, {}, { withCredentials: true }).catch(() => {}); setTrialPopup(null); }} className="w-full text-stone-500">
                Continue Trial
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Instructions Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Payment Instructions</DialogTitle>
            <p className="text-sm text-stone-500">Complete your subscription to continue</p>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-stone-50 rounded-xl text-sm text-stone-700 whitespace-pre-line leading-relaxed">
              {siteSettings.payment_instructions || 'Please contact us to complete your payment.'}
            </div>
            {siteSettings.payment_whatsapp && (
              <a href={siteSettings.payment_whatsapp} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Chat on WhatsApp for Payment
              </a>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ==================== MOBILE BOTTOM NAV ====================
function MobileBottomNav({ activeTab, setActiveTab, activeCalls, unreadNotifs, isStaff, staffAccess, restaurant }) {
  const [moreOpen, setMoreOpen] = useState(false);

  // Primary tabs shown in bottom nav (max 5)
  // Package capability gate — hides nav items the host's package doesn't unlock.
  // Returns true if the item is allowed (or has no capability key); false if
  // the package_features explicitly disables it. Grandfathered hosts get
  // all-true so existing clients are unaffected.
  const pkgAllows = (capability) => {
    if (!capability) return true;
    const pf = restaurant?.package_features;
    if (!pf) return true;
    return pf[capability] !== false;
  };

  // If the host (or staff) lands on a tab they no longer have a package
  // entitlement for, snap back to Tables (or the first allowed tab) so the
  // app never renders an empty / forbidden panel.
  useEffect(() => {
    const tabCapability = {
      tables: 'tables', menu: 'food_menu', reasons: 'call_reasons',
      calls: 'live_calls', history: 'performance', chat: 'chat',
      qrcodes: 'qr_codes', paging: 'paging', notifications: 'inbox',
      access: 'access_control', settings: 'settings',
    };
    const cap = tabCapability[activeTab];
    if (cap && !pkgAllows(cap)) {
      const fallback = ['tables', 'menu', 'calls', 'chat', 'history', 'settings']
        .map(id => ({ id, cap: tabCapability[id] }))
        .find(x => pkgAllows(x.cap));
      if (fallback) setActiveTab(fallback.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.package_features, activeTab]);
  const primaryTabs = [
    { id: 'tables', icon: LayoutGrid, label: 'Tables', capability: 'tables' },
    { id: 'calls', icon: AlertCircle, label: 'Calls', badge: activeCalls.filter(c => c.status === 'active').length, capability: 'live_calls' },
    { id: 'chat', icon: MessageSquare, label: 'Chat', capability: 'chat' },
    { id: 'menu', icon: Menu, label: 'Menu', capability: 'food_menu' },
    { id: 'more', icon: MoreHorizontal, label: 'More' },
  ].filter(item => pkgAllows(item.capability));

  // Secondary tabs shown in "More" sheet
  const moreTabs = [
    { id: 'reasons', icon: Bell, label: 'Call Reasons', staffOnlyIfGranted: 'call_reasons', capability: 'call_reasons' },
    { id: 'history', icon: History, label: 'Call History', staffOnlyIfGranted: 'performance', capability: 'performance' },
    { id: 'qrcodes', icon: QrCode, label: 'QR Codes', staffOnlyIfGranted: 'qr_codes', capability: 'qr_codes' },
    { id: 'paging', icon: Printer, label: 'Paging', capability: 'paging' },
    { id: 'notifications', icon: Inbox, label: 'Inbox', badge: unreadNotifs, staffOnlyIfGranted: 'inbox', capability: 'inbox' },
    { id: 'access', icon: ShieldCheck, label: 'Access Control', hostOnly: true, capability: 'access_control' },
    { id: 'settings', icon: Settings, label: 'Settings', staffOnlyIfGranted: 'settings', capability: 'settings' },
  ].filter(item => {
    if (item.hostOnly && isStaff) return false;
    if (isStaff && item.staffOnlyIfGranted && !(staffAccess?.[item.staffOnlyIfGranted])) return false;
    if (!pkgAllows(item.capability)) return false;
    return true;
  });

  const isMoreActive = moreTabs.some(t => t.id === activeTab);

  return (
    <>
      {/* Bottom Nav — mobile only */}
      {/* Bottom nav — visible only on phone/tablet portrait. Hidden at lg+ and in any landscape orientation (sidebar handles those). */}
      <nav data-testid="mobile-bottom-nav" className="lg:hidden landscape:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-stone-200 safe-bottom">
        <div className="flex items-center justify-around px-1 py-1">
          {primaryTabs.map(item => {
            const isActive = item.id === 'more' ? isMoreActive || moreOpen : activeTab === item.id;
            return (
              <button
                key={item.id}
                data-testid={`mobile-nav-${item.id}`}
                onClick={() => {
                  if (item.id === 'more') {
                    setMoreOpen(!moreOpen);
                  } else {
                    setActiveTab(item.id);
                    setMoreOpen(false);
                  }
                }}
                className={`relative flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-all min-w-[56px] ${
                  isActive ? 'text-orange-600' : 'text-stone-400'
                }`}
              >
                <item.icon size={20} strokeWidth={isActive ? 2.5 : 1.5} />
                <span className={`text-[10px] mt-0.5 ${isActive ? 'font-semibold' : 'font-normal'}`}>{item.label}</span>
                {item.badge > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-red-600 text-white text-[9px] font-bold px-1">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* More Menu Overlay */}
      {moreOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-40 bg-black/30" onClick={() => setMoreOpen(false)} />
          <div data-testid="mobile-more-menu" className="md:hidden fixed bottom-[68px] left-3 right-3 z-50 bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden animate-fade-in-up">
            <div className="p-2 grid grid-cols-3 gap-1">
              {moreTabs.map(item => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    data-testid={`mobile-more-${item.id}`}
                    onClick={() => { setActiveTab(item.id); setMoreOpen(false); }}
                    className={`relative flex flex-col items-center justify-center py-3 px-2 rounded-xl transition-all ${
                      isActive ? 'bg-orange-50 text-orange-600' : 'text-stone-500 hover:bg-stone-50'
                    }`}
                  >
                    <item.icon size={20} />
                    <span className="text-[11px] mt-1 font-medium">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="absolute top-1.5 right-2 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-red-600 text-white text-[9px] font-bold px-1">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}


// ==================== TABLES PANEL (DRAG AND DROP + RESIZE + LAYOUT IMAGE) ====================
function TablesPanel({ tables, setTables, alertTables, resetTable, loadChat, restaurant, setRestaurant, siteSettings, activeCalls = [], goToLiveCallsForTable, isStaff = false }) {
  // Live "now" tick so wait-time labels in the tooltip update without a refetch.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  // Build per-table summary from activeCalls so tooltip + click navigation get
  // the same data the LiveCallsPanel header shows.
  const summaryByTable = useMemo(() => {
    const m = new Map();
    for (const c of activeCalls) {
      if (!c.table_id) continue;
      const e = m.get(c.table_id) || { active: 0, total: 0, oldest: null, newest: null, reasons: [], items: [] };
      e.total += 1;
      if (c.status === 'active') e.active += 1;
      const t = new Date(c.created_at || 0).getTime();
      if (!e.oldest || t < e.oldest) e.oldest = t;
      if (!e.newest || t > e.newest) e.newest = t;
      if (c.is_order && Array.isArray(c.order_items)) {
        for (const it of c.order_items) e.items.push(`${it.qty}× ${it.name}`);
      } else if (c.reason_label) {
        e.reasons.push(c.reason_label);
      }
      m.set(c.table_id, e);
    }
    return m;
  }, [activeCalls]);

  const renderTooltip = (tableId) => {
    const s = summaryByTable.get(tableId);
    if (!s || s.total === 0) return <span className="text-stone-400">No active calls.</span>;
    const oldestMin = s.oldest ? Math.max(0, Math.floor((nowMs - s.oldest) / 60000)) : null;
    const newestMin = s.newest ? Math.max(0, Math.floor((nowMs - s.newest) / 60000)) : null;
    const uniqReasons = Array.from(new Set(s.reasons)).slice(0, 3);
    const uniqItems = Array.from(new Set(s.items)).slice(0, 3);
    return (
      <div className="space-y-1.5 text-left">
        <div className="flex items-center gap-2">
          <span className="font-bold text-orange-700">{s.active} active</span>
          {s.total > s.active && <span className="text-stone-500">&middot; {s.total - s.active} completed</span>}
        </div>
        {(newestMin != null && oldestMin != null) && (
          <div className="text-xs text-stone-600">
            Time: new <span className="font-semibold">{newestMin} min</span>, OLD <span className={`font-semibold ${oldestMin >= 5 ? 'text-red-600' : 'text-stone-700'}`}>{oldestMin} min</span>
          </div>
        )}
        {uniqReasons.length > 0 && (
          <div className="text-xs text-stone-600">
            <span className="text-stone-400 uppercase tracking-wider text-[10px]">Reasons</span>
            <div className="font-medium">{uniqReasons.join(', ')}</div>
          </div>
        )}
        {uniqItems.length > 0 && (
          <div className="text-xs text-stone-600">
            <span className="text-stone-400 uppercase tracking-wider text-[10px]">Order items</span>
            <div className="font-medium">{uniqItems.join(', ')}</div>
          </div>
        )}
        <div className="text-[10px] text-orange-600 font-medium pt-1 border-t border-stone-200">Click to open in Live Calls →</div>
      </div>
    );
  };
  const TABLE_MIN = siteSettings?.table_min_size || 30;
  const TABLE_MAX = siteSettings?.table_max_size || 200;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [form, setForm] = useState({ label: '', x: 0, y: 0, seats: 4, width: 80, height: 80, color: '#FFFFFF', counter_placement: 'right' });
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [resetConfirmTable, setResetConfirmTable] = useState(null);
  // Right-click context menu position + target table. Closing = ctxMenu === null.
  const [ctxMenu, setCtxMenu] = useState(null); // { table, x, y } | null
  const openTableContextMenu = (e, t) => {
    // Staff have no editing privileges; the menu is host-only. The "locked"
    // state of the layout is irrelevant — we WANT the host to be able to
    // edit tables even when the layout is locked (locking only prevents
    // accidental drag, not deliberate config changes).
    if (isStaff) return;
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ table: t, x: e.clientX, y: e.clientY });
  };
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    // Any click anywhere (including the menu items themselves — they call
    // the handler then this listener closes) dismisses the menu.
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', close, true);
    window.addEventListener('blur', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('blur', close);
    };
  }, [ctxMenu]);
  const [uploadingLayout, setUploadingLayout] = useState(false);
  const [layoutImgSrc, setLayoutImgSrc] = useState(null);
  const [layoutFit, setLayoutFit] = useState('fit'); // 'fit' | '16:9'
  const [layoutScale, setLayoutScale] = useState(1); // table tile scale factor — currentContainerWidth / referenceWidth
  const [layoutSettings, setLayoutSettings] = useState({ fit: 'contain', opacity: 40, scale: 100, offsetX: 50, offsetY: 50 });
  const [lockedHost, setLockedHost] = useState(false);
  // Staff are always treated as "locked" — they can never edit/move/resize/add/delete
  // tables regardless of the host's lock state. The actual host lock pill still
  // reflects `lockedHost` so staff can see whether the host has locked.
  const locked = lockedHost || isStaff;
  const [layoutAspect, setLayoutAspect] = useState(null); // natural aspect of uploaded image
  const layoutRef = useRef(null);
  const savingLayoutRef = useRef(false);
  const tapTimerRef = useRef({}); // { [tableId]: { count, timer } } — for touch double-tap detection

  const TABLE_COLORS = [
    '#FFFFFF', '#FEF3C7', '#DCFCE7', '#DBEAFE', '#F3E8FF',
    '#FFE4E6', '#FFEDD5', '#E0F2FE', '#D1FAE5', '#FDE68A',
    '#FDA4AF', '#A5B4FC', '#86EFAC', '#FCD34D', '#C4B5FD',
  ];

  const openNew = () => { setEditingTable(null); setForm({ label: '', x: 25, y: 25, seats: 4, width: 80, height: 80, color: '#FFFFFF', counter_placement: 'right' }); setDialogOpen(true); };
  const openEdit = (t) => { setEditingTable(t); setForm({ label: t.label, x: t.x, y: t.y, seats: t.seats, width: t.width || 80, height: t.height || 80, color: t.color || '#FFFFFF', counter_placement: t.counter_placement || 'right' }); setDialogOpen(true); };

  // Load layout background image
  useEffect(() => {
    if (!restaurant?.layout_image_path) { setLayoutImgSrc(null); setLayoutAspect(null); return; }
    let revoke;
    axios.get(`${API}/files/${restaurant.layout_image_path}`, { responseType: 'blob', withCredentials: true })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setLayoutImgSrc(url);
        // Measure the image's natural aspect ratio so the editor container keeps
        // the same aspect across any orientation/device — table positions (in %)
        // then stay correctly aligned in mobile portrait too.
        const probe = new Image();
        probe.onload = () => {
          if (probe.naturalWidth && probe.naturalHeight) {
            setLayoutAspect(probe.naturalWidth / probe.naturalHeight);
          }
        };
        probe.src = url;
      })
      .catch(() => { setLayoutImgSrc(null); setLayoutAspect(null); });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [restaurant?.layout_image_path]);

  // Load layout settings from restaurant
  useEffect(() => {
    if (restaurant?.layout_settings) {
      setLayoutSettings(prev => ({ ...prev, ...restaurant.layout_settings }));
    }
  }, [restaurant?.layout_settings]);

  // Sync lock state from restaurant doc
  useEffect(() => {
    setLockedHost(!!restaurant?.layout_locked);
  }, [restaurant?.layout_locked]);

  // Persist layout-fit toggle on the restaurant; also rehydrate from doc on load
  useEffect(() => {
    if (restaurant?.layout_fit && (restaurant.layout_fit === 'fit' || restaurant.layout_fit === '16:9')) {
      setLayoutFit(restaurant.layout_fit);
    }
  }, [restaurant?.layout_fit]);

  const setLayoutFitPersist = async (next) => {
    setLayoutFit(next);
    try {
      const { data } = await axios.put(`${API}/restaurant`, { layout_fit: next }, { withCredentials: true });
      setRestaurant(data);
    } catch { /* non-blocking */ }
  };

  // View mode toggle: "layout" (host-designed positions) vs "grid" (equal-size
  // tiles auto-flowed by table number). Persisted on the restaurant doc so it
  // survives reloads and switches.
  const [viewMode, setViewMode] = useState('layout');
  useEffect(() => {
    if (restaurant?.layout_view_mode === 'grid' || restaurant?.layout_view_mode === 'layout') {
      setViewMode(restaurant.layout_view_mode);
    }
  }, [restaurant?.layout_view_mode]);
  const setViewModePersist = async (next) => {
    setViewMode(next);
    try {
      const { data } = await axios.put(`${API}/restaurant`, { layout_view_mode: next }, { withCredentials: true });
      setRestaurant(data);
    } catch { /* non-blocking */ }
  };
  // Grid-mode filter: when true, only tables with an active call / alert /
  // pending chat message are rendered. Persisted on the restaurant doc so the
  // host's choice (All vs Only Active) sticks until they manually change it.
  const [onlyActive, setOnlyActive] = useState(false);
  useEffect(() => {
    if (restaurant?.layout_grid_filter === 'active') setOnlyActive(true);
    else if (restaurant?.layout_grid_filter === 'all') setOnlyActive(false);
  }, [restaurant?.layout_grid_filter]);
  const setOnlyActivePersist = async (next) => {
    setOnlyActive(next);
    try {
      const { data } = await axios.put(`${API}/restaurant`, { layout_grid_filter: next ? 'active' : 'all' }, { withCredentials: true });
      setRestaurant(data);
    } catch { /* non-blocking */ }
  };

  // Track the layout container width so we can scale table tiles proportionally.
  // Reference width = 1000px (the width at which saved t.width / t.height pixels map 1:1).
  // When the container is narrower or wider, tables scale by the same factor as the layout image.
  const REFERENCE_LAYOUT_WIDTH = 1000;
  useEffect(() => {
    const el = layoutRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width || el.clientWidth || REFERENCE_LAYOUT_WIDTH;
      setLayoutScale(Math.max(0.35, w / REFERENCE_LAYOUT_WIDTH));
    });
    ro.observe(el);
    // initial measurement
    setLayoutScale(Math.max(0.35, (el.clientWidth || REFERENCE_LAYOUT_WIDTH) / REFERENCE_LAYOUT_WIDTH));
    return () => ro.disconnect();
  }, [layoutFit, layoutImgSrc]);

  // ===== Manual container sizing =====
  // Host-controlled override for the editor container's pixel width / height.
  // Acts as a fallback for the rare cases where the Fit Window / 16:9 auto-
  // detection produces a container that doesn't suit the host's screen
  // (zoomed-in browsers, ultra-wide monitors, side-by-side windows, etc).
  // Persisted per-restaurant in localStorage so the choice survives reloads
  // without round-tripping to the backend on every drag.
  const manualSizeKey = restaurant?.id ? `table_layout_size:${restaurant.id}` : null;
  const [manualSize, setManualSize] = useState({ w: null, h: null });
  // Re-hydrate from localStorage once the restaurant id is available.
  useEffect(() => {
    if (!manualSizeKey) return;
    try {
      const raw = localStorage.getItem(manualSizeKey);
      if (raw) {
        const p = JSON.parse(raw);
        setManualSize({
          w: Number.isFinite(Number(p.w)) && Number(p.w) > 0 ? Number(p.w) : null,
          h: Number.isFinite(Number(p.h)) && Number(p.h) > 0 ? Number(p.h) : null,
        });
      }
    } catch { /* ignore */ }
  }, [manualSizeKey]);

  // Drag-to-resize state for the container edges. axis = 'w' | 'h'.
  const [containerResize, setContainerResize] = useState(null);
  const onContainerResizeStart = (axis, e) => {
    if (locked) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = layoutRef.current?.getBoundingClientRect();
    setContainerResize({
      axis,
      startX: e.clientX, startY: e.clientY,
      startW: rect?.width || 0,
      startH: rect?.height || 0,
    });
  };
  useEffect(() => {
    if (!containerResize) return;
    const onMove = (ev) => {
      setManualSize(prev => {
        const next = { ...prev };
        if (containerResize.axis === 'w') {
          next.w = Math.max(320, Math.round(containerResize.startW + (ev.clientX - containerResize.startX)));
        }
        if (containerResize.axis === 'h') {
          next.h = Math.max(180, Math.round(containerResize.startH + (ev.clientY - containerResize.startY)));
        }
        return next;
      });
    };
    const onUp = () => {
      setContainerResize(null);
      // Persist the final size — read directly from state via the setter
      // pattern so we always save the latest value the user landed on.
      setManualSize(latest => {
        if (manualSizeKey) {
          try {
            if (!latest.w && !latest.h) localStorage.removeItem(manualSizeKey);
            else localStorage.setItem(manualSizeKey, JSON.stringify(latest));
          } catch { /* ignore */ }
        }
        return latest;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [containerResize, manualSizeKey]);

  const resetManualSize = () => {
    setManualSize({ w: null, h: null });
    if (manualSizeKey) {
      try { localStorage.removeItem(manualSizeKey); } catch { /* ignore */ }
    }
  };

  const toggleLock = async () => {
    if (isStaff) return;  // staff cannot toggle the host's lock
    const next = !lockedHost;
    setLockedHost(next);
    try {
      const { data } = await axios.put(`${API}/restaurant`, { layout_locked: next }, { withCredentials: true });
      setRestaurant(data);
      toast.success(next ? 'Layout locked — drag and resize disabled' : 'Layout unlocked');
    } catch {
      // revert UI on failure
      setLockedHost(!next);
      toast.error('Failed to update lock state');
    }
  };

  // Save layout settings to backend (debounced)
  const saveLayoutSettings = useCallback(async (settings) => {
    if (savingLayoutRef.current) return;
    savingLayoutRef.current = true;
    try {
      await axios.put(`${API}/restaurant`, { layout_settings: settings }, { withCredentials: true });
    } catch {}
    savingLayoutRef.current = false;
  }, []);

  const updateLayoutSetting = (key, value) => {
    setLayoutSettings(prev => {
      const next = { ...prev, [key]: value };
      // Debounce save
      clearTimeout(window._layoutSaveTimer);
      window._layoutSaveTimer = setTimeout(() => saveLayoutSettings(next), 500);
      return next;
    });
  };

  const uploadLayoutImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLayout(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: uploadData } = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const { data } = await axios.put(`${API}/restaurant`, { layout_image_path: uploadData.path }, { withCredentials: true });
      setRestaurant(data);
      toast.success('Layout image uploaded');
    } catch { toast.error('Upload failed'); }
    setUploadingLayout(false);
  };

  const removeLayoutImage = async () => {
    try {
      const { data } = await axios.put(`${API}/restaurant`, { layout_image_path: '' }, { withCredentials: true });
      setRestaurant(data);
      setLayoutImgSrc(null);
      toast.success('Layout image removed');
    } catch { toast.error('Failed'); }
  };

  const handleSave = async () => {
    try {
      if (editingTable) {
        const { data } = await axios.put(`${API}/tables/${editingTable.id}`, form, { withCredentials: true });
        setTables(prev => prev.map(t => t.id === editingTable.id ? data : t));
        toast.success('Table updated');
      } else {
        const { data } = await axios.post(`${API}/tables`, form, { withCredentials: true });
        setTables(prev => [...prev, data]);
        toast.success('Table created');
      }
      setDialogOpen(false);
    } catch { toast.error('Failed to save table'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/tables/${id}`, { withCredentials: true });
      setTables(prev => prev.filter(t => t.id !== id));
      toast.success('Table deleted');
    } catch { toast.error('Failed to delete'); }
  };

  // Drag handlers
  const handleMouseDown = (e, table) => {
    if (locked) return;                                 // lock blocks drag
    if (e.target.closest('[data-resize-handle]')) return; // Don't drag if resizing
    e.preventDefault();
    e.stopPropagation();
    setDragging({ tableId: table.id });
  };

  const handleMouseMove = useCallback((e) => {
    if (!layoutRef.current) return;
    const rect = layoutRef.current.getBoundingClientRect();

    if (dragging) {
      const newX = Math.max(3, Math.min(97, ((e.clientX - rect.left) / rect.width) * 100));
      const newY = Math.max(3, Math.min(97, ((e.clientY - rect.top) / rect.height) * 100));
      // Sub-percent precision (2 decimals) so dragging feels smooth instead of
      // jumping in 1 % "snap" increments — that previously translated to ~10 px
      // gaps on a 1000 px wide container.
      setTables(prev => prev.map(t =>
        t.id === dragging.tableId ? { ...t, x: Math.round(newX * 100) / 100, y: Math.round(newY * 100) / 100 } : t
      ));
    }

    if (resizing) {
      const table = tables.find(t => t.id === resizing.tableId);
      if (!table) return;
      const tableCenterX = (table.x / 100) * rect.width;
      const tableCenterY = (table.y / 100) * rect.height;
      const dx = Math.abs(e.clientX - rect.left - tableCenterX) * 2;
      const dy = Math.abs(e.clientY - rect.top - tableCenterY) * 2;
      const newW = Math.max(TABLE_MIN, Math.min(TABLE_MAX, Math.round(dx)));
      const newH = Math.max(TABLE_MIN, Math.min(TABLE_MAX, Math.round(dy)));
      setTables(prev => prev.map(t =>
        t.id === resizing.tableId ? { ...t, width: newW, height: newH } : t
      ));
    }
  }, [dragging, resizing, tables, setTables]);

  const handleMouseUp = useCallback(async () => {
    if (dragging) {
      const table = tables.find(t => t.id === dragging.tableId);
      if (table) {
        try { await axios.put(`${API}/tables/${table.id}`, { x: table.x, y: table.y }, { withCredentials: true }); } catch {}
      }
      setDragging(null);
    }
    if (resizing) {
      const table = tables.find(t => t.id === resizing.tableId);
      if (table) {
        try { await axios.put(`${API}/tables/${table.id}`, { width: table.width, height: table.height }, { withCredentials: true }); } catch {}
      }
      setResizing(null);
    }
  }, [dragging, resizing, tables]);

  useEffect(() => {
    if (dragging || resizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }
  }, [dragging, resizing, handleMouseMove, handleMouseUp]);

  // Touch events
  const handleTouchStart = (e, table) => {
    // Detect double-tap (works only when locked, to trigger reset)
    if (locked) {
      const slot = tapTimerRef.current[table.id] || { count: 0, timer: null };
      slot.count += 1;
      if (slot.count >= 2) {
        clearTimeout(slot.timer);
        tapTimerRef.current[table.id] = { count: 0, timer: null };
        setResetConfirmTable(table);
        return;
      }
      clearTimeout(slot.timer);
      slot.timer = setTimeout(() => { tapTimerRef.current[table.id] = { count: 0, timer: null }; }, 350);
      tapTimerRef.current[table.id] = slot;
      return;
    }
    if (e.target.closest('[data-resize-handle]')) return;
    setDragging({ tableId: table.id });
  };

  const handleTouchMove = useCallback((e) => {
    if (!layoutRef.current) return;
    const touch = e.touches[0];
    const rect = layoutRef.current.getBoundingClientRect();
    if (dragging) {
      const newX = Math.max(3, Math.min(97, ((touch.clientX - rect.left) / rect.width) * 100));
      const newY = Math.max(3, Math.min(97, ((touch.clientY - rect.top) / rect.height) * 100));
      setTables(prev => prev.map(t =>
        t.id === dragging.tableId ? { ...t, x: Math.round(newX * 100) / 100, y: Math.round(newY * 100) / 100 } : t
      ));
    }
    if (resizing) {
      const table = tables.find(t => t.id === resizing.tableId);
      if (!table) return;
      const tableCenterX = (table.x / 100) * rect.width;
      const tableCenterY = (table.y / 100) * rect.height;
      const dx = Math.abs(touch.clientX - rect.left - tableCenterX) * 2;
      const dy = Math.abs(touch.clientY - rect.top - tableCenterY) * 2;
      setTables(prev => prev.map(t =>
        t.id === resizing.tableId ? { ...t, width: Math.max(TABLE_MIN, Math.min(TABLE_MAX, Math.round(dx))), height: Math.max(TABLE_MIN, Math.min(TABLE_MAX, Math.round(dy))) } : t
      ));
    }
  }, [dragging, resizing, tables, setTables]);

  useEffect(() => {
    if (dragging || resizing) {
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
      return () => { window.removeEventListener('touchmove', handleTouchMove); window.removeEventListener('touchend', handleMouseUp); };
    }
  }, [dragging, resizing, handleTouchMove, handleMouseUp]);

  const handleResizeStart = (e, table) => {
    if (locked) return;
    e.preventDefault();
    e.stopPropagation();
    setResizing({ tableId: table.id });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Tables</h2>
          {/* Host-only context line — staff sees only the header so the table
              container can claim more vertical real-estate on phones/tablets. */}
          {!isStaff && viewMode === 'layout' && (
            <p className="hidden md:block text-sm text-stone-500">
              {locked
                ? <>Layout is <span className="font-semibold text-stone-700">locked</span> — double-click to reset a table</>
                : 'Drag tables to arrange, pull corners to resize'}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Button
            data-testid="lock-layout-btn"
            variant="outline"
            onClick={toggleLock}
            className={`gap-1.5 text-sm font-medium ${lockedHost ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100' : 'border-stone-200 text-stone-700'} ${isStaff ? 'hidden' : ''}`}
            title={lockedHost ? 'Unlock to drag/resize tables' : 'Lock to prevent accidental drag'}
          >
            {lockedHost ? <Lock size={14} /> : <Unlock size={14} />}
            {lockedHost ? 'Locked' : 'Lock Layout'}
          </Button>
          {/* Staff lock-status pill removed — staff doesn't need to see this
              detail on mobile/tablet; the layout itself communicates state. */}
          {/* View-mode pill: Layout (host-designed) vs Grid (equal tiles ordered by label) */}
          <div className="flex items-center gap-1 bg-stone-100 p-0.5 rounded-lg" title="Display mode">
            <button
              data-testid="view-mode-layout-btn"
              type="button"
              onClick={() => setViewModePersist('layout')}
              className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-all ${viewMode === 'layout' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
            >Layout</button>
            <button
              data-testid="view-mode-grid-btn"
              type="button"
              onClick={() => setViewModePersist('grid')}
              className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-all ${viewMode === 'grid' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
            >Grid</button>
          </div>
          {/* Grid-only filter pill: All vs Only Active */}
          {viewMode === 'grid' && (
            <div className="flex items-center gap-1 bg-stone-100 p-0.5 rounded-lg" title="Filter grid tiles">
              <button
                data-testid="grid-filter-all-btn"
                type="button"
                onClick={() => setOnlyActivePersist(false)}
                className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-all ${!onlyActive ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
              >All</button>
              <button
                data-testid="grid-filter-active-btn"
                type="button"
                onClick={() => setOnlyActivePersist(true)}
                className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-all inline-flex items-center gap-1 ${onlyActive ? 'bg-white text-red-700 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
              >
                <Bell size={11} className={onlyActive ? 'text-red-600' : ''} /> Only Active
              </button>
            </div>
          )}
          <div className={`flex items-center gap-1 bg-stone-100 p-0.5 rounded-lg mr-2 ${(locked || viewMode === 'grid') ? 'hidden' : ''}`}>
            <button data-testid="layout-fit-fit-btn" onClick={() => setLayoutFitPersist('fit')} className={`px-2 py-1 rounded text-[11px] font-medium transition-all ${layoutFit === 'fit' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500'}`}>Fit Window</button>
            <button data-testid="layout-fit-16-9-btn" onClick={() => setLayoutFitPersist('16:9')} className={`px-2 py-1 rounded text-[11px] font-medium transition-all ${layoutFit === '16:9' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500'}`}>16:9</button>
          </div>
          {/* "Reset size" button — visible only when the host has manually
              resized the editor container. Clears the localStorage override
              and falls back to the Fit Window / 16:9 auto-sizing. */}
          {!locked && viewMode !== 'grid' && (manualSize.w || manualSize.h) && (
            <button
              type="button"
              data-testid="layout-manual-reset-btn"
              onClick={resetManualSize}
              title="Restore automatic container sizing"
              className="px-2 py-1 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-[11px] font-medium hover:bg-amber-100 mr-2"
            >
              Reset size
            </button>
          )}
          <label className={`cursor-pointer ${(locked || viewMode === 'grid') ? 'hidden' : ''}`}>
            <Input type="file" accept="image/*" onChange={uploadLayoutImage} className="hidden" disabled={uploadingLayout || locked} />
            <span data-testid="upload-layout-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 bg-white hover:bg-stone-50 text-stone-700 text-sm font-medium cursor-pointer transition-colors">
              <ImageIcon size={16} /> {uploadingLayout ? 'Uploading...' : layoutImgSrc ? 'Change Layout' : 'Upload Layout'}
            </span>
          </label>
          {layoutImgSrc && !locked && viewMode !== 'grid' && (
            <Button variant="ghost" size="sm" onClick={removeLayoutImage} className="text-xs text-red-500 hover:text-red-700">Remove</Button>
          )}
          <Button data-testid="add-table-btn" onClick={openNew} disabled={locked} className={`bg-orange-600 hover:bg-orange-700 text-white gap-2 disabled:opacity-50 disabled:pointer-events-none ${locked ? 'hidden' : ''}`}>
            <Plus size={16} /> Add Table
          </Button>
        </div>
      </div>

      {/* Layout Image Adjustment Controls — completely hidden when locked or in grid view */}
      {layoutImgSrc && !locked && viewMode !== 'grid' && (
        <div className="flex flex-wrap items-center gap-4 px-1">
          <div className="flex items-center gap-1.5 bg-stone-100 p-0.5 rounded-lg">
            {['contain', 'cover', 'fill'].map(mode => (
              <button key={mode} onClick={() => updateLayoutSetting('fit', mode)}
                className={`px-2.5 py-1 rounded text-[11px] font-medium capitalize transition-all ${layoutSettings.fit === mode ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
                {mode === 'contain' ? 'Fit' : mode === 'cover' ? 'Fill' : 'Stretch'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <span className="w-12">Opacity</span>
            <input type="range" min={10} max={100} value={layoutSettings.opacity} onChange={e => updateLayoutSetting('opacity', parseInt(e.target.value))} className="w-24 accent-orange-600" />
            <span className="w-8 text-right">{layoutSettings.opacity}%</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <span className="w-10">Scale</span>
            <input type="range" min={50} max={300} value={layoutSettings.scale} onChange={e => updateLayoutSetting('scale', parseInt(e.target.value))} className="w-24 accent-orange-600" />
            <span className="w-10 text-right">{layoutSettings.scale}%</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <span className="w-5">X</span>
            <input type="range" min={0} max={100} value={layoutSettings.offsetX} onChange={e => updateLayoutSetting('offsetX', parseInt(e.target.value))} className="w-20 accent-orange-600" />
          </div>
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <span className="w-5">Y</span>
            <input type="range" min={0} max={100} value={layoutSettings.offsetY} onChange={e => updateLayoutSetting('offsetY', parseInt(e.target.value))} className="w-20 accent-orange-600" />
          </div>
          <button onClick={() => { const reset = { fit: 'contain', opacity: 40, scale: 100, offsetX: 50, offsetY: 50 }; setLayoutSettings(reset); saveLayoutSettings(reset); }}
            className="text-[11px] text-stone-400 hover:text-stone-600 underline">Reset</button>
        </div>
      )}

      {/* Drag & Drop & Resize Layout (host-designed positions) — hidden in Grid mode. */}
      <Card className={`border-stone-200 ${viewMode === 'grid' ? 'hidden' : ''}`}>
        <CardContent className="p-4">
          <div
            ref={layoutRef}
            data-testid="table-layout-editor"
            className={`relative w-full max-h-[55vh] md:max-h-[70vh] lg:max-h-none rounded-xl border-2 border-dashed border-stone-300 overflow-hidden select-none ${dragging || resizing ? 'cursor-grabbing' : ''} ${containerResize ? 'cursor-' + (containerResize.axis === 'w' ? 'ew' : 'ns') + '-resize' : ''}`}
            style={{
              // Manual override wins over the Fit Window / 16:9 auto-sizing
              // whenever the host has dragged the right/bottom resize edge.
              // Width is capped to the parent column via maxWidth: 100% so
              // an over-eager drag never breaks the page layout.
              ...(manualSize.w ? { width: `${manualSize.w}px`, maxWidth: '100%' } : {}),
              ...(manualSize.h
                ? { height: `${manualSize.h}px`, minHeight: 0 }
                : layoutFit === 'fit'
                  ? { height: 'calc(100vh - 230px)', minHeight: '260px' }
                  : { aspectRatio: '16/9', minHeight: '220px' }),
              backgroundColor: layoutImgSrc ? 'transparent' : '#F5F5F4'
            }}
          >
            {/* Layout background image with adjustments.
                ----------------------------------------------------------------
                The img used to be positioned with width:${scale}% / left:${50-
                scale/2}% which silently broke objectFit (Fit/Fill/Stretch did
                nothing visible) AND made X/Y sliders a no-op at the default
                scale. We now render the image inside an inner wrapper that is
                always sized to fill the container, then translate+scale that
                wrapper. Result:
                  • objectFit (Fit/Fill/Stretch) maps 1:1 to contain/cover/fill.
                  • Scale slider zooms the image around the centre.
                  • X / Y sliders pan the image freely (range −50%→+50% of the
                    container; 50/50 = neutral).
            */}
            {layoutImgSrc && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity: layoutSettings.opacity / 100 }}>
                <div
                  className="absolute"
                  style={{
                    left: 0, top: 0, width: '100%', height: '100%',
                    transform: `translate(${layoutSettings.offsetX - 50}%, ${layoutSettings.offsetY - 50}%) scale(${layoutSettings.scale / 100})`,
                    transformOrigin: '50% 50%',
                  }}
                >
                  <img
                    src={layoutImgSrc}
                    alt="Restaurant layout"
                    draggable={false}
                    className="pointer-events-none w-full h-full select-none"
                    style={{ objectFit: layoutSettings.fit }}
                  />
                </div>
              </div>
            )}
            {/* Grid lines (only if no image) */}
            {!layoutImgSrc && (
              <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: 'linear-gradient(#d6d3d1 1px, transparent 1px), linear-gradient(90deg, #d6d3d1 1px, transparent 1px)',
                backgroundSize: '10% 10%'
              }} />
            )}
            {tables.map(t => {
              const isAlert = alertTables.has(t.id);
              const sumLayout = summaryByTable.get(t.id);
              const hasCallsLayout = !!sumLayout && sumLayout.total > 0;
              const w = (t.width || 80) * layoutScale;
              const h = (t.height || 80) * layoutScale;
              const tColor = t.color || '#FFFFFF';
              const textColor = isLightColor(tColor) ? '#44403C' : '#FFFFFF';
              const subColor = isLightColor(tColor) ? '#78716C' : '#E7E5E4';
              const markerEl = (
              <div
                key={t.id}
                data-testid={`table-drag-${t.id}`}
                className={`absolute group ${dragging?.tableId === t.id || resizing?.tableId === t.id ? 'z-20' : 'z-10'}`}
                style={{ left: `${Math.max(3, t.x)}%`, top: `${Math.max(3, t.y)}%`, transform: 'translate(-50%, -50%)' }}
                onMouseDown={(e) => handleMouseDown(e, t)}
                onTouchStart={(e) => handleTouchStart(e, t)}
                onContextMenu={(e) => openTableContextMenu(e, t)}
                onDoubleClick={(e) => { if (locked) { e.preventDefault(); e.stopPropagation(); setResetConfirmTable(t); } }}
                onClick={(e) => {
                  // Single click → navigate to Live Calls (only when locked so we
                  // don't fight with drag-to-move when unlocked).
                  if (locked && hasCallsLayout && goToLiveCallsForTable) {
                    e.preventDefault();
                    e.stopPropagation();
                    goToLiveCallsForTable(t.id);
                  }
                }}
              >
                <div
                  className={`rounded-xl border-2 shadow-sm flex flex-col items-center justify-center transition-all relative ${locked ? 'cursor-default' : 'cursor-grab'} ${
                    dragging?.tableId === t.id ? 'border-orange-500 shadow-lg' :
                    resizing?.tableId === t.id ? 'border-blue-500 shadow-lg' :
                    isAlert ? 'border-red-500 shadow-lg animate-table-alert' :
                    locked ? 'border-stone-300/70 group-hover:border-amber-300' :
                    'border-stone-200/60 group-hover:border-orange-400 group-hover:shadow-md'
                  }`}
                  style={{ width: `${w}px`, height: `${h}px`, backgroundColor: isAlert ? undefined : tColor }}
                  title={locked ? 'Layout locked — double-click to reset this table' : undefined}
                >
                  {isAlert ? (
                    <Bell size={14} className="text-red-600 mb-0.5 animate-bounce" />
                  ) : locked ? (
                    <Lock size={12} style={{ color: subColor }} className="mb-0.5 opacity-70" />
                  ) : (
                    <Move size={12} style={{ color: subColor }} className="mb-0.5" />
                  )}
                  <span className="text-xs font-bold truncate px-1" style={{ color: isAlert ? '#991B1B' : textColor }}>{t.label}</span>
                  {/* Resize handle — hidden when locked */}
                  {!locked && (
                    <div
                      data-resize-handle="true"
                      data-testid={`table-resize-${t.id}`}
                      onMouseDown={(e) => handleResizeStart(e, t)}
                      className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: 'linear-gradient(135deg, transparent 50%, #a8a29e 50%)' }}
                    />
                  )}
                </div>
                {isAlert && (
                  <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center animate-ping-slow">
                    <div className="w-2.5 h-2.5 bg-red-500 rounded-full" />
                  </div>
                )}
                {/* Live P/N/T counter badge — visible whenever this table has unattended (active) calls. */}
                {hasCallsLayout && (t.counter_placement || 'right') !== 'hidden' && sumLayout.active > 0 && (() => {
                  const placement = t.counter_placement || 'right';
                  const oldestMin = sumLayout.oldest ? Math.max(0, Math.floor((nowMs - sumLayout.oldest) / 60000)) : 0;
                  const newestMin = sumLayout.newest ? Math.max(0, Math.floor((nowMs - sumLayout.newest) / 60000)) : 0;
                  const totalActive = sumLayout.active;
                  const pad = (n) => String(n).padStart(2, '0');
                  const oldCritical = oldestMin >= 5;
                  let posCls = '';
                  if (placement === 'right') posCls = 'left-full top-1/2 -translate-y-1/2 ml-1.5 items-start text-left';
                  else if (placement === 'left') posCls = 'right-full top-1/2 -translate-y-1/2 mr-1.5 items-end text-right';
                  else if (placement === 'top') posCls = 'bottom-full left-1/2 -translate-x-1/2 mb-1 items-center text-center';
                  else if (placement === 'bottom') posCls = 'top-full left-1/2 -translate-x-1/2 mt-1 items-center text-center';
                  return (
                    <div
                      data-testid={`table-counter-${t.id}`}
                      className={`absolute z-30 pointer-events-none flex flex-col gap-0 text-[11px] font-bold leading-tight tracking-wide select-none ${posCls}`}
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      <span className={`whitespace-nowrap ${oldCritical ? 'text-red-600 animate-pulse' : 'text-red-600'}`}>P {pad(oldestMin)}</span>
                      <span className="whitespace-nowrap text-stone-700">N {pad(newestMin)}</span>
                      <span className="whitespace-nowrap text-stone-700">T {pad(totalActive)}</span>
                    </div>
                  );
                })()}
              </div>
              );
              if (hasCallsLayout) {
                return (
                  <TooltipProvider key={t.id} delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>{markerEl}</TooltipTrigger>
                      <TooltipContent side="top" className="bg-white text-stone-800 border border-stone-200 shadow-lg p-3 max-w-xs z-30">
                        {renderTooltip(t.id)}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }
              return markerEl;
            })}
            {tables.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-stone-400 text-sm">
                {layoutImgSrc ? 'Add tables on top of your layout' : 'Upload a layout image or add tables to start'}
              </div>
            )}
            {/* Manual container resize handles — fallback for the rare cases
                where Fit Window / 16:9 don't produce a comfortable size.
                Right edge drags width, bottom edge drags height. Hidden when
                the host has locked the layout (or for staff). Each handle is
                a thin strip that lights up on hover so the host has a clear
                affordance without it dominating the editor visually. */}
            {!locked && (
              <>
                <div
                  data-testid="layout-resize-right"
                  onMouseDown={(e) => onContainerResizeStart('w', e)}
                  title="Drag to resize width"
                  aria-label="Resize layout width"
                  className="group absolute top-0 right-0 h-full w-2 cursor-ew-resize z-30 flex items-center justify-center"
                >
                  <span className="block w-1 h-12 rounded-full bg-stone-300 group-hover:bg-orange-500 group-active:bg-orange-600 transition-colors" />
                </div>
                <div
                  data-testid="layout-resize-bottom"
                  onMouseDown={(e) => onContainerResizeStart('h', e)}
                  title="Drag to resize height"
                  aria-label="Resize layout height"
                  className="group absolute bottom-0 left-0 w-full h-2 cursor-ns-resize z-30 flex items-center justify-center"
                >
                  <span className="block h-1 w-16 rounded-full bg-stone-300 group-hover:bg-orange-500 group-active:bg-orange-600 transition-colors" />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Grid mode — equal-size tiles auto-flowing in a CSS grid. Container
          height is intrinsic (rows grow as more tables are added), so 60+ or
          200+ tables are handled identically; the page just gets taller. */}
      {viewMode === 'grid' && (
        <Card className="border-stone-200 min-h-[calc(100vh-220px)] lg:min-h-[calc(100vh-180px)]">
          <CardContent className="p-4">
            <div
              data-testid="table-grid-view"
              className="grid gap-2 sm:gap-3 grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 pr-1"
            >
              {[...tables]
                .filter(t => {
                  if (!onlyActive) return true;
                  // Active = has any unattended call summary OR the marker is
                  // in the blinking/alert state (alertTables covers the local
                  // "has pending chat or fresh call" signal too).
                  const s = summaryByTable.get(t.id);
                  return (s && s.active > 0) || alertTables.has(t.id);
                })
                .sort((a, b) => {
                // Numeric-aware sort (T2 < T10) so the tiles read in table-number order.
                const al = (a.label || '').toString();
                const bl = (b.label || '').toString();
                return al.localeCompare(bl, undefined, { numeric: true, sensitivity: 'base' });
              }).map(t => {
                const isAlert = alertTables.has(t.id);
                const sumGrid = summaryByTable.get(t.id);
                const hasCallsGrid = !!sumGrid && sumGrid.total > 0;
                const tColor = t.color || '#FFFFFF';
                const textColor = isLightColor(tColor) ? '#1C1917' : '#FFFFFF';
                const subColor = isLightColor(tColor) ? '#57534E' : '#E7E5E4';
                const tileEl = (
                  <div
                    key={t.id}
                    data-testid={`table-grid-tile-${t.id}`}
                    onClick={() => { if (hasCallsGrid && goToLiveCallsForTable) goToLiveCallsForTable(t.id); }}
                    onContextMenu={(e) => openTableContextMenu(e, t)}
                    onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); setResetConfirmTable(t); }}
                    className={`relative aspect-square rounded-lg sm:rounded-xl border-2 shadow-sm flex flex-col items-center justify-between py-1 sm:py-1.5 lg:py-2 transition-all ${hasCallsGrid ? 'cursor-pointer' : 'cursor-default'} ${
                      isAlert ? 'border-red-500 shadow-lg animate-table-alert' : 'border-stone-300/70 hover:border-amber-300'
                    }`}
                    style={{ backgroundColor: isAlert ? undefined : tColor }}
                    title="Double-click to reset this table"
                  >
                    {/* Table number — colored circle pinned to the top of the tile, centered horizontally. */}
                    <div
                      className="rounded-full flex items-center justify-center font-bold leading-none shadow-sm w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-[9px] sm:text-xs lg:text-sm landscape:max-md:w-6 landscape:max-md:h-6 landscape:max-md:text-[9px]"
                      style={{
                        backgroundColor: isAlert ? '#DC2626' : darkenColor(tColor === '#FFFFFF' ? '#FEF3C7' : tColor, 0.25),
                        color: '#FFFFFF',
                      }}
                    >
                      {t.label}
                    </div>
                    {/* Live P/N/T counter — bottom of the tile, tightly stacked. */}
                    {hasCallsGrid && sumGrid.active > 0 ? (() => {
                      const oldestMin = sumGrid.oldest ? Math.max(0, Math.floor((nowMs - sumGrid.oldest) / 60000)) : 0;
                      const newestMin = sumGrid.newest ? Math.max(0, Math.floor((nowMs - sumGrid.newest) / 60000)) : 0;
                      const pad = (n) => String(n).padStart(2, '0');
                      const oldCritical = oldestMin >= 5;
                      return (
                        <div
                          data-testid={`table-grid-counter-${t.id}`}
                          className="flex flex-col items-center gap-0 pointer-events-none text-[9px] sm:text-[11px] lg:text-xs landscape:max-md:text-[8px] font-bold leading-none"
                          style={{ fontVariantNumeric: 'tabular-nums', color: isAlert ? '#991B1B' : textColor }}
                        >
                          <span className={`whitespace-nowrap ${oldCritical ? 'text-red-600 animate-pulse' : 'text-red-600'}`}>P {pad(oldestMin)}</span>
                          <span className="whitespace-nowrap">N {pad(newestMin)}</span>
                          <span className="whitespace-nowrap">T {pad(sumGrid.active)}</span>
                        </div>
                      );
                    })() : <span className="text-[9px] opacity-60" style={{ color: subColor }}>idle</span>}
                    {/* Alert dot — top-left when this tile is in alarm state. */}
                    {isAlert && (
                      <div className="absolute -top-1.5 -left-1.5 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center animate-ping-slow">
                        <div className="w-2.5 h-2.5 bg-red-500 rounded-full" />
                      </div>
                    )}
                  </div>
                );
                if (hasCallsGrid) {
                  return (
                    <TooltipProvider key={t.id} delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>{tileEl}</TooltipTrigger>
                        <TooltipContent side="top" className="bg-white text-stone-800 border border-stone-200 shadow-lg p-3 max-w-xs z-30">
                          {renderTooltip(t.id)}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                }
                return tileEl;
              })}
              {tables.length === 0 && (
                <div className="col-span-full text-center text-stone-400 text-sm py-8">
                  No tables yet. Switch to Layout mode and add tables.
                </div>
              )}
              {tables.length > 0 && onlyActive && !tables.some(t => (summaryByTable.get(t.id)?.active > 0) || alertTables.has(t.id)) && (
                <div className="col-span-full text-center text-emerald-600 text-sm py-8">
                  All clear — no tables currently have active calls or alerts.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* The dense list of table-setting cards (Edit / Reset / Delete) that
          previously rendered below the layout was removed per UX request —
          right-click any table in the Layout or Grid view to access those
          actions inline via the context menu. The full-form "Edit Table"
          dialog is still triggered from the menu's Edit action below. */}

      {/* Right-click context menu — replaces the old below-canvas
          "table settings list". Renders only when the host right-clicks a
          table (Layout or Grid view). Positioned absolutely at the cursor
          and auto-dismisses on any outside click via the useEffect above. */}
      {ctxMenu && (
        <div
          data-testid={`table-context-menu-${ctxMenu.table.id}`}
          className="fixed z-50 min-w-[170px] rounded-lg border border-stone-200 bg-white shadow-xl py-1.5 text-sm"
          style={{
            // Clamp so it never goes off the right/bottom edge.
            left:  Math.min(ctxMenu.x, (typeof window !== 'undefined' ? window.innerWidth : 1024) - 180),
            top:   Math.min(ctxMenu.y, (typeof window !== 'undefined' ? window.innerHeight : 768) - 200),
          }}
          // Stop the document-level mousedown listener from closing on inside clicks BEFORE the menu item handler fires.
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 text-xs font-bold text-stone-500 uppercase tracking-wider border-b border-stone-100">
            {ctxMenu.table.label}
          </div>
          <button
            type="button"
            data-testid={`ctx-edit-${ctxMenu.table.id}`}
            onClick={() => { openEdit(ctxMenu.table); setCtxMenu(null); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-stone-700 hover:bg-stone-50 hover:text-orange-600 transition-colors"
          >
            <Edit2 size={14} /> Edit table…
          </button>
          <button
            type="button"
            data-testid={`ctx-reset-${ctxMenu.table.id}`}
            onClick={() => { setResetConfirmTable(ctxMenu.table); setCtxMenu(null); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-amber-700 hover:bg-amber-50 transition-colors"
          >
            <RotateCcw size={14} /> Reset for new customers
          </button>
          {!isStaff && (
            <>
              <div className="my-1 border-t border-stone-100" />
              <button
                type="button"
                data-testid={`ctx-delete-${ctxMenu.table.id}`}
                onClick={() => { handleDelete(ctxMenu.table.id); setCtxMenu(null); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 size={14} /> Delete table
              </button>
            </>
          )}
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={!!resetConfirmTable} onOpenChange={(open) => { if (!open) setResetConfirmTable(null); }}>
        <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Outfit' }}>Reset {resetConfirmTable?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will start a fresh session for this table. All chat messages will be deleted and active calls will be dismissed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-reset-table-btn"
              onClick={() => { resetTable(resetConfirmTable.id); setResetConfirmTable(null); }}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              Reset Table
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{editingTable ? 'Edit Table' : 'Add Table'}</DialogTitle>
            <p className="text-sm text-stone-500">{editingTable ? 'Update table details' : 'Add a new table to your layout'}</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Table Label</Label>
              <Input data-testid="table-label-input" value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g. Table 1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Seats</Label>
                <Input type="number" value={form.seats} onChange={e => setForm(p => ({ ...p, seats: parseInt(e.target.value) || 1 }))} min={1} max={20} />
              </div>
              <div className="space-y-2">
                <Label>X Position (%)</Label>
                <Input type="number" value={form.x} onChange={e => setForm(p => ({ ...p, x: parseFloat(e.target.value) || 0 }))} min={0} max={100} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Y Position (%)</Label>
                <Input type="number" value={form.y} onChange={e => setForm(p => ({ ...p, y: parseFloat(e.target.value) || 0 }))} min={0} max={100} />
              </div>
              <div className="space-y-2">
                <Label>Width (px)</Label>
                <Input type="number" value={form.width} onChange={e => setForm(p => ({ ...p, width: parseInt(e.target.value) || TABLE_MIN }))} min={TABLE_MIN} max={TABLE_MAX} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Height (px)</Label>
              <div className="flex items-center gap-3">
                <Input type="range" min={TABLE_MIN} max={TABLE_MAX} value={form.height} onChange={e => setForm(p => ({ ...p, height: parseInt(e.target.value) }))} className="flex-1" />
                <span className="text-sm text-stone-500 w-12 text-right">{form.height}px</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Table Color</Label>
              <div className="flex flex-wrap gap-2">
                {TABLE_COLORS.map(c => (
                  <button key={c} onClick={() => setForm(p => ({ ...p, color: c }))}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${form.color === c ? 'border-orange-600 scale-110 shadow-md' : 'border-stone-200 hover:border-stone-300'}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
                <label className="w-8 h-8 rounded-lg border-2 border-dashed border-stone-300 flex items-center justify-center cursor-pointer overflow-hidden relative hover:border-stone-400" title="Custom color">
                  <input type="color" value={form.color || '#FFFFFF'} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                  <span className="text-[10px] text-stone-400">+</span>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Live counter placement</Label>
              <p className="text-[11px] text-stone-500 -mt-1">When this table has unattended calls, show a 3-line live badge — <span className="text-red-600 font-semibold">P</span> oldest min, <span className="font-semibold">N</span> newest min, <span className="font-semibold">T</span> total — placed at the chosen side of the table.</p>
              <div className="grid grid-cols-5 gap-1.5">
                {[
                  { v: 'right', label: 'Right' },
                  { v: 'left', label: 'Left' },
                  { v: 'top', label: 'Top' },
                  { v: 'bottom', label: 'Bottom' },
                  { v: 'hidden', label: 'Hide' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`counter-placement-${opt.v}`}
                    onClick={() => setForm(p => ({ ...p, counter_placement: opt.v }))}
                    className={`text-[11px] py-1.5 rounded border transition-colors ${form.counter_placement === opt.v ? 'border-orange-600 bg-orange-50 text-orange-700 font-semibold' : 'border-stone-200 text-stone-600 hover:border-stone-300'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-3 bg-stone-50 rounded-lg text-center">
              <p className="text-[10px] text-stone-400 mb-1">Preview</p>
              <div className="inline-flex items-center justify-center border-2 border-stone-300 rounded-xl" style={{ width: `${Math.min(form.width, 150)}px`, height: `${Math.min(form.height, 100)}px`, backgroundColor: form.color || '#FFFFFF' }}>
                <span className="text-xs font-bold" style={{ color: isLightColor(form.color || '#FFFFFF') ? '#44403C' : '#FFFFFF' }}>{form.label || '?'}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-table-btn" onClick={handleSave} className="bg-orange-600 hover:bg-orange-700 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== MENU PANEL (with availability toggle) ====================
function MenuPanel({ categories, setCategories, menuItems, setMenuItems, toggleAvailability, currency, isStaff = false }) {
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', sort_order: 0, description: '', image_path: null, visible: true });
  const [itemForm, setItemForm] = useState({ category_id: '', name: '', description: '', price: 0, image_path: null, available: true });
  const [uploading, setUploading] = useState(false);
  const [collapsedCats, setCollapsedCats] = useState({}); // { [catId]: true } — when missing entry, default = collapsed
  const isCatCollapsed = (id) => collapsedCats[id] !== false; // default-collapsed unless explicitly opened

  const openNewCat = () => { setEditingCat(null); setCatForm({ name: '', sort_order: categories.length, description: '', image_path: null, visible: true, kpi_target_minutes: 15 }); setCatDialogOpen(true); };
  const openEditCat = (c) => { setEditingCat(c); setCatForm({ name: c.name, sort_order: c.sort_order, description: c.description || '', image_path: c.image_path || null, visible: c.visible !== false, kpi_target_minutes: c.kpi_target_minutes ?? 15 }); setCatDialogOpen(true); };

  const toggleCatCollapsed = (id) => setCollapsedCats(p => ({ ...p, [id]: !isCatCollapsed(id) ? true : false }));

  const toggleCatVisibility = async (cat) => {
    const next = !(cat.visible !== false); // current effective → flip
    try {
      const { data } = await axios.put(`${API}/menu-categories/${cat.id}`, {
        name: cat.name,
        sort_order: cat.sort_order,
        description: cat.description || '',
        image_path: cat.image_path || null,
        visible: next,
        kpi_target_minutes: cat.kpi_target_minutes ?? 15,
      }, { withCredentials: true });
      setCategories(prev => prev.map(c => c.id === cat.id ? data : c));
      toast.success(next ? 'Category visible to customers' : 'Category hidden from customers');
    } catch { toast.error('Failed to update'); }
  };

  const uploadCatImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setCatForm(prev => ({ ...prev, image_path: data.path }));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const saveCat = async () => {
    try {
      if (editingCat) {
        const { data } = await axios.put(`${API}/menu-categories/${editingCat.id}`, catForm, { withCredentials: true });
        setCategories(prev => prev.map(c => c.id === editingCat.id ? data : c));
      } else {
        const { data } = await axios.post(`${API}/menu-categories`, catForm, { withCredentials: true });
        setCategories(prev => [...prev, data]);
      }
      setCatDialogOpen(false);
      toast.success('Category saved');
    } catch { toast.error('Failed to save'); }
  };

  const deleteCat = async (id) => {
    try {
      await axios.delete(`${API}/menu-categories/${id}`, { withCredentials: true });
      setCategories(prev => prev.filter(c => c.id !== id));
      setMenuItems(prev => prev.filter(i => i.category_id !== id));
      toast.success('Category deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const openNewItem = (catId) => {
    setEditingItem(null);
    setItemForm({ category_id: catId, name: '', description: '', price: 0, image_path: null, available: true });
    setItemDialogOpen(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({ category_id: item.category_id, name: item.name, description: item.description, price: item.price, image_path: item.image_path, available: item.available !== false });
    setItemDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setItemForm(prev => ({ ...prev, image_path: data.path }));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const saveItem = async () => {
    try {
      if (editingItem) {
        const { data } = await axios.put(`${API}/menu-items/${editingItem.id}`, itemForm, { withCredentials: true });
        setMenuItems(prev => prev.map(i => i.id === editingItem.id ? data : i));
      } else {
        const { data } = await axios.post(`${API}/menu-items`, itemForm, { withCredentials: true });
        setMenuItems(prev => [...prev, data]);
      }
      setItemDialogOpen(false);
      toast.success('Menu item saved');
    } catch { toast.error('Failed to save'); }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/menu-items/${id}`, { withCredentials: true });
      setMenuItems(prev => prev.filter(i => i.id !== id));
      toast.success('Item deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Menu</h2>
          <p className="text-sm text-stone-500 mt-1">{isStaff ? 'Show or hide categories and items on the customer menu.' : 'Manage categories and items with images'}</p>
        </div>
        {!isStaff && (
          <Button data-testid="add-category-btn" onClick={openNewCat} className="bg-orange-600 hover:bg-orange-700 text-white gap-2">
            <Plus size={16} /> Add Category
          </Button>
        )}
      </div>

      {categories.length === 0 && (
        <Card className="border-stone-200 border-dashed">
          <CardContent className="py-12 text-center">
            <Menu className="mx-auto text-stone-300 mb-3" size={40} />
            <p className="text-stone-500">No menu categories yet. Create one to start adding items.</p>
          </CardContent>
        </Card>
      )}

      {categories.map(cat => {
        const isCollapsed = isCatCollapsed(cat.id);
        const isVisible = cat.visible !== false;
        const itemCount = menuItems.filter(i => i.category_id === cat.id).length;
        return (
        <Card key={cat.id} className={`border-stone-200 ${!isVisible ? 'opacity-70' : ''}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                data-testid={`category-toggle-${cat.id}`}
                onClick={() => toggleCatCollapsed(cat.id)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left group"
              >
                {isCollapsed ? <ChevronRight size={16} className="text-stone-400 shrink-0" /> : <ChevronDown size={16} className="text-stone-400 shrink-0" />}
                {cat.image_path ? (
                  <ImageThumb path={cat.image_path} />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="text-stone-300" size={18} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg font-semibold text-stone-800 truncate" style={{ fontFamily: 'Outfit' }}>{cat.name}</CardTitle>
                    <Badge variant="outline" className="text-[10px]">{itemCount}</Badge>
                    {!isVisible && <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-700">Hidden</Badge>}
                  </div>
                  {cat.description && <p className="text-xs text-stone-500 mt-0.5 italic truncate">{cat.description}</p>}
                </div>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <div className="flex items-center gap-1.5 mr-1" title={isVisible ? 'Visible to customers' : 'Hidden from customers'}>
                  {isVisible ? <Eye size={14} className="text-green-600" /> : <EyeOff size={14} className="text-stone-400" />}
                  <Switch
                    data-testid={`category-visibility-${cat.id}`}
                    checked={isVisible}
                    onCheckedChange={() => toggleCatVisibility(cat)}
                    className="data-[state=checked]:bg-green-600"
                  />
                </div>
                {!isStaff && (
                  <>
                    <Button variant="ghost" size="icon" onClick={() => openEditCat(cat)} className="h-8 w-8" data-testid={`edit-category-${cat.id}`}><Edit2 size={14} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteCat(cat.id)} className="h-8 w-8 text-red-500"><Trash2 size={14} /></Button>
                    <Button data-testid={`add-item-${cat.id}`} variant="outline" size="sm" onClick={() => openNewItem(cat.id)} className="gap-1 text-xs">
                      <Plus size={12} /> Item
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          {!isCollapsed && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                {menuItems.filter(i => i.category_id === cat.id).map(item => (
                <div key={item.id} className={`flex items-center gap-4 p-3 rounded-xl border border-stone-100 transition-colors ${item.available === false ? 'bg-stone-100 opacity-60' : 'bg-stone-50/50 hover:bg-stone-50'}`}>
                  {item.image_path ? (
                    <ImageThumb path={item.image_path} />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-stone-200 flex items-center justify-center flex-shrink-0">
                      <UtensilsCrossed className="text-stone-400" size={20} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-stone-900 truncate">{item.name}</p>
                      {item.available === false && (
                        <Badge variant="outline" className="text-xs border-red-200 text-red-600">Unavailable</Badge>
                      )}
                    </div>
                    <p className="text-xs text-stone-500 truncate">{item.description}</p>
                    <p className="text-sm font-bold text-orange-600 mt-0.5">{formatPrice(item.price, currency)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5" title={item.available !== false ? 'Available' : 'Unavailable'}>
                      {item.available !== false ? <Eye size={14} className="text-green-600" /> : <EyeOff size={14} className="text-stone-400" />}
                      <Switch
                        data-testid={`toggle-availability-${item.id}`}
                        checked={item.available !== false}
                        onCheckedChange={() => toggleAvailability(item.id)}
                        className="data-[state=checked]:bg-green-600"
                      />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => openEditItem(item)} disabled={isStaff} className={`h-8 w-8 ${isStaff ? 'hidden' : ''}`}><Edit2 size={14} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteItem(item.id)} disabled={isStaff} className={`h-8 w-8 text-red-500 ${isStaff ? 'hidden' : ''}`}><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
              {menuItems.filter(i => i.category_id === cat.id).length === 0 && (
                <p className="text-sm text-stone-400 py-4 text-center">No items in this category</p>
              )}
              </div>
            </CardContent>
          )}
        </Card>
        );
      })}

      {/* Category Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{editingCat ? 'Edit Category' : 'Add Category'}</DialogTitle>
            <p className="text-sm text-stone-500">Menu categories organize your dishes</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input data-testid="category-name-input" value={catForm.name} onChange={e => setCatForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Appetizers" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input data-testid="category-description-input" value={catForm.description} onChange={e => setCatForm(p => ({ ...p, description: e.target.value }))} placeholder="Light bites to start your meal..." />
            </div>
            <div className="space-y-2">
              <Label>Image</Label>
              <Input type="file" accept="image/*" onChange={uploadCatImage} disabled={uploading} data-testid="category-image-input" />
              {uploading && <p className="text-xs text-stone-500">Uploading...</p>}
              {catForm.image_path && (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-green-600">Image set</p>
                  <button type="button" onClick={() => setCatForm(p => ({ ...p, image_path: null }))} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Order Fulfilment Target (minutes)</Label>
              <Input
                type="number"
                min={1}
                max={120}
                value={catForm.kpi_target_minutes}
                onChange={e => setCatForm(p => ({ ...p, kpi_target_minutes: parseInt(e.target.value || '0', 10) }))}
                data-testid="category-kpi-target-input"
              />
              <p className="text-xs text-stone-500">Used by Performance graphs to flag late orders for items in this category.</p>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50 border border-stone-200">
              <div>
                <p className="text-sm font-medium text-stone-800">Visible to customers</p>
                <p className="text-xs text-stone-500">When off, this category and its items are hidden from the QR menu</p>
              </div>
              <Switch
                data-testid="category-visible-switch"
                checked={catForm.visible}
                onCheckedChange={(v) => setCatForm(p => ({ ...p, visible: v }))}
                className="data-[state=checked]:bg-green-600"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-category-btn" onClick={saveCat} className="bg-orange-600 hover:bg-orange-700 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Dialog */}
      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{editingItem ? 'Edit Item' : 'Add Item'}</DialogTitle>
            <p className="text-sm text-stone-500">Add dish details and photo</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Item Name</Label>
              <Input data-testid="item-name-input" value={itemForm.name} onChange={e => setItemForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Caesar Salad" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={itemForm.description} onChange={e => setItemForm(p => ({ ...p, description: e.target.value }))} placeholder="Crispy romaine with..." />
            </div>
            <div className="space-y-2">
              <Label>Price ($)</Label>
              <Input type="number" step="0.01" value={itemForm.price} onChange={e => setItemForm(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div className="space-y-2">
              <Label>Image</Label>
              <Input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
              {uploading && <p className="text-xs text-stone-500">Uploading...</p>}
              {itemForm.image_path && <p className="text-xs text-green-600">Image uploaded</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-item-btn" onClick={saveItem} className="bg-orange-600 hover:bg-orange-700 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImageThumb({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    const loadImage = async () => {
      try {
        const response = await axios.get(`${API}/files/${path}`, { responseType: 'blob', withCredentials: true });
        const url = URL.createObjectURL(response.data);
        revoke = url;
        setSrc(url);
      } catch {
        setSrc(null);
      }
    };
    loadImage();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!src) return <div className="w-16 h-16 rounded-lg bg-stone-200 flex-shrink-0" />;
  return <img src={src} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />;
}

// ==================== CALL REASONS PANEL ====================
function CallReasonsPanel({ callReasons, setCallReasons }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: '', icon: 'Bell', sort_order: 0 });

  const icons = ['Bell', 'Droplets', 'ClipboardList', 'Receipt', 'AlertCircle', 'Users', 'CheckCircle', 'UtensilsCrossed'];

  const openNew = () => { setEditing(null); setForm({ label: '', icon: 'Bell', sort_order: callReasons.length }); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ label: r.label, icon: r.icon, sort_order: r.sort_order }); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) {
        const { data } = await axios.put(`${API}/call-reasons/${editing.id}`, form, { withCredentials: true });
        setCallReasons(prev => prev.map(r => r.id === editing.id ? data : r));
      } else {
        const { data } = await axios.post(`${API}/call-reasons`, form, { withCredentials: true });
        setCallReasons(prev => [...prev, data]);
      }
      setDialogOpen(false);
      toast.success('Saved');
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/call-reasons/${id}`, { withCredentials: true });
      setCallReasons(prev => prev.filter(r => r.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Call Reasons</h2>
          <p className="text-sm text-stone-500 mt-1">Customize what customers can request</p>
        </div>
        <Button data-testid="add-reason-btn" onClick={openNew} className="bg-orange-600 hover:bg-orange-700 text-white gap-2">
          <Plus size={16} /> Add Reason
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {callReasons.map(r => {
          const Icon = getIcon(r.icon);
          return (
            <Card key={r.id} className="border-stone-200 hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Icon className="text-orange-600" size={20} />
                  </div>
                  <p className="font-semibold text-stone-900">{r.label}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(r)} className="h-8 w-8"><Edit2 size={14} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} className="h-8 w-8 text-red-500"><Trash2 size={14} /></Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{editing ? 'Edit Reason' : 'Add Reason'}</DialogTitle>
            <p className="text-sm text-stone-500">Customize what your customers can request</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Label</Label>
              <Input data-testid="reason-label-input" value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g. More Napkins" />
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2">
                {icons.map(iconName => {
                  const Ic = getIcon(iconName);
                  return (
                    <button key={iconName} onClick={() => setForm(p => ({ ...p, icon: iconName }))}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center border-2 transition-all ${
                        form.icon === iconName ? 'border-orange-600 bg-orange-50' : 'border-stone-200 hover:border-stone-300'
                      }`}>
                      <Ic size={18} className={form.icon === iconName ? 'text-orange-600' : 'text-stone-500'} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-reason-btn" onClick={handleSave} className="bg-orange-600 hover:bg-orange-700 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== LIVE CALLS PANEL ====================
function LiveCallsPanel({ activeCalls, dismissCall, markServed, loadChat, restaurantCurrency = 'USD', focusTableId = null, clearFocusTableId = () => {}, resetTable = () => {}, tables = [] }) {
  const activeCount = activeCalls.filter(c => c.status === 'active').length;
  const completedCount = activeCalls.length - activeCount;
  // Collapsed-by-default: host expands a table to see its calls. Persist across
  // re-renders via a Set keyed by table_id.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (tableId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(tableId)) next.delete(tableId); else next.add(tableId);
      return next;
    });
  };
  // When the host clicks a table from the Tables tab, auto-expand that group
  // and scroll it into view, then clear the focus token.
  useEffect(() => {
    if (!focusTableId) return;
    setExpanded(prev => {
      if (prev.has(focusTableId)) return prev;
      const next = new Set(prev);
      next.add(focusTableId);
      return next;
    });
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="live-table-group-${focusTableId}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      clearFocusTableId();
    }, 80);
    return () => clearTimeout(timer);
  }, [focusTableId, clearFocusTableId]);
  // Live "now" tick so wait-time labels update every 30s without a full refetch.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  // Confirmation state for "Reset Table" — opens an AlertDialog so a stray
  // click doesn't wipe a table's call history.
  const [resetConfirm, setResetConfirm] = useState(null); // { table_id, table_label }
  // Column count is breakpoint-driven so each tile lives in a fixed column.
  // Expansion in column N only pushes that column's siblings; never reflows
  // across columns (which is what CSS grid / multi-column layouts both do).
  const [colCount, setColCount] = useState(() => {
    if (typeof window === 'undefined') return 3;
    const w = window.innerWidth;
    if (w >= 1280) return 3;
    if (w >= 768) return 2;
    return 1;
  });
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setColCount(w >= 1280 ? 3 : w >= 768 ? 2 : 1);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Group by table: each group shows the table label + its calls (newest first).
  // Table groups are ordered by the newest active call first, so tables with
  // fresh unattended alerts always bubble to the top.
  const grouped = useMemo(() => {
    const byTable = new Map();
    for (const c of activeCalls) {
      if (!byTable.has(c.table_id)) {
        byTable.set(c.table_id, {
          table_id: c.table_id,
          table_label: c.table_label || 'Table',
          calls: [],
          active: 0,
        });
      }
      const g = byTable.get(c.table_id);
      g.calls.push(c);
      if (c.status === 'active') g.active += 1;
    }
    // Sort calls within each group: active first, then newest first.
    for (const g of byTable.values()) {
      g.calls.sort((a, b) => {
        const aA = a.status === 'active' ? 1 : 0;
        const bA = b.status === 'active' ? 1 : 0;
        if (aA !== bA) return bA - aA;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      const activeCalls = g.calls.filter(c => c.status === 'active');
      g.latestActiveAt = activeCalls.length > 0 ? activeCalls[0].created_at : 0;
      // Oldest active = earliest created_at among active calls (the one waiting longest)
      g.oldestActiveAt = activeCalls.length > 0
        ? activeCalls.reduce((old, c) => (!old || new Date(c.created_at) < new Date(old) ? c.created_at : old), null)
        : 0;
      g.latestAnyAt = g.calls[0]?.created_at || 0;
    }
    // Sort groups: any-active groups first; within that bucket, oldest active
    // call surfaces first so hosts can prioritize who's been waiting longest.
    return Array.from(byTable.values()).sort((a, b) => {
      const aAct = a.active > 0 ? 1 : 0;
      const bAct = b.active > 0 ? 1 : 0;
      if (aAct !== bAct) return bAct - aAct;
      if (aAct === 1) {
        // both have active calls — oldest active first
        return new Date(a.oldestActiveAt || 0) - new Date(b.oldestActiveAt || 0);
      }
      return new Date(b.latestAnyAt || 0) - new Date(a.latestAnyAt || 0);
    });
  }, [activeCalls]);

  return (
    <div className="space-y-6 landscape:max-xl:space-y-3">
      <div>
        <h2 className="text-2xl font-bold text-stone-900 landscape:max-xl:text-xl" style={{ fontFamily: 'Outfit' }}>Live Calls</h2>
        <p className="text-sm text-stone-500 mt-1">
          <span data-testid="live-active-count">{activeCount}</span> active
          {completedCount > 0 && (
            <> &middot; <span data-testid="live-completed-count" className="text-emerald-700">{completedCount} completed</span> (clear by resetting the table)</>
          )}
        </p>
      </div>

      {activeCalls.length === 0 && (
        <Card className="border-stone-200 border-dashed">
          <CardContent className="py-12 text-center">
            <Bell className="mx-auto text-stone-300 mb-3" size={40} />
            <p className="text-stone-500">No active calls. Customers will appear here when they press the call button.</p>
          </CardContent>
        </Card>
      )}

      {/* Three independent flex columns. Each group is permanently assigned to
          a column (round-robin), so expanding any group only pushes its column-
          mates down — never reflows across columns. No internal maxHeight: the
          rows flow naturally and the parent <main> handles scroll, so more rows
          stay visible on tablets/landscape instead of being capped to ~2. */}
      <div
        data-testid="live-calls-scroll"
        className="flex gap-4 pr-1 items-start"
      >
        {Array.from({ length: colCount }).map((_, colIdx) => {
          const colGroups = grouped.filter((_, i) => i % colCount === colIdx);
          return (
            <div key={colIdx} className="flex-1 min-w-0 space-y-4 landscape:max-xl:space-y-1.5">
              {colGroups.map(group => {
          const isExpanded = expanded.has(group.table_id);
          // Durations (in minutes) for the newest and oldest active calls.
          const newestMin = group.latestActiveAt ? Math.max(0, Math.floor((nowMs - new Date(group.latestActiveAt).getTime()) / 60000)) : null;
          const oldestMin = group.oldestActiveAt ? Math.max(0, Math.floor((nowMs - new Date(group.oldestActiveAt).getTime()) / 60000)) : null;
          // "Old" threshold — turn red if the oldest active call has been waiting longer than this.
          const oldIsCritical = oldestMin != null && oldestMin >= 5;
          return (
          <div key={group.table_id} data-testid={`live-table-group-${group.table_id}`} className="space-y-2 landscape:max-xl:space-y-1">
            <div
              className={`w-full flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 landscape:max-xl:px-2 landscape:max-xl:py-1 landscape:max-xl:gap-x-1.5 landscape:max-xl:gap-y-0 rounded-lg border transition-colors text-left ${
                group.active > 0
                  ? 'border-orange-200 bg-orange-50 hover:bg-orange-100'
                  : group.calls.length > 0
                    ? 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100'
                    : 'border-stone-200 bg-stone-50 hover:bg-stone-100'
              }`}
            >
              <button
                type="button"
                data-testid={`live-table-group-toggle-${group.table_id}`}
                aria-expanded={isExpanded}
                onClick={() => toggleExpanded(group.table_id)}
                className="flex flex-wrap items-center gap-x-2 gap-y-1 flex-1 min-w-0 text-left"
              >
                {isExpanded
                  ? <ChevronDown size={16} className="text-stone-500 shrink-0" />
                  : <ChevronRight size={16} className="text-stone-500 shrink-0" />}
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-stone-200 text-stone-800 text-sm font-bold tracking-wide uppercase">
                  {group.table_label}
                </span>
                <span className="text-sm text-stone-500 font-medium">
                  {group.calls.length} call{group.calls.length === 1 ? '' : 's'}
                </span>
                {group.active > 0 && newestMin != null && oldestMin != null && (
                  <span
                    data-testid={`live-table-times-${group.table_id}`}
                    className="ml-1 sm:ml-3 text-xs sm:text-sm text-stone-600 font-medium inline-flex items-center gap-1 sm:whitespace-nowrap"
                  >
                    <Clock size={14} className="text-stone-400" />
                    {/* Mobile: ultra-compact "new/OLD" format so the ACTIVE
                        badge fits on the same row, eliminating the wrapped line.
                        Desktop / sm+: full "Time: new X min, OLD Y min" label. */}
                    <span className="hidden sm:inline">Time:</span>
                    <span className="hidden sm:inline">new <span className="font-bold text-stone-800">{newestMin}</span> min,</span>
                    <span className="hidden sm:inline">OLD <span className={`font-bold ${oldIsCritical ? 'text-red-600' : 'text-stone-800'}`}>{oldestMin}</span> min</span>
                    <span className="sm:hidden whitespace-nowrap">
                      <span className="font-bold text-stone-800">{newestMin}</span>
                      /
                      <span className={`font-bold ${oldIsCritical ? 'text-red-600' : 'text-stone-800'}`}>{oldestMin}</span>
                      m
                    </span>
                  </span>
                )}
                {group.active > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-orange-600 text-white text-[10px] sm:text-xs font-bold uppercase tracking-wide whitespace-nowrap">
                    <Bell size={12} /> {group.active} active
                  </span>
                )}
                {group.active === 0 && (
                  <span className="text-xs uppercase tracking-wide text-emerald-700 font-semibold inline-flex items-center gap-1">
                    <Check size={12} /> All served
                  </span>
                )}
              </button>
              {/* Reset Table — host action only, shown once every call on this
                  table is served. Confirmation gate to prevent accidents. */}
              {group.active === 0 && group.calls.length > 0 && (
                <button
                  type="button"
                  data-testid={`live-reset-table-btn-${group.table_id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setResetConfirm({ table_id: group.table_id, table_label: group.table_label });
                  }}
                  className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-red-300 bg-white hover:bg-red-50 text-red-600 text-xs font-bold uppercase tracking-wide transition-colors"
                  title="Clear the call history for this table — use when the customer leaves"
                >
                  <RotateCcw size={12} /> Reset Table
                </button>
              )}
            </div>
            {isExpanded && (
              <div className="space-y-2 pl-2">
                {group.calls.map(call => {
          const Icon = getIcon(call.reason_icon);
          const isActive = call.status === 'active';
          const isServed = call.status === 'served';
          const cardCls = isActive
            ? 'border-orange-200 bg-orange-50 animate-pulse-ring'
            : isServed
              ? 'border-emerald-200 bg-emerald-50/50 opacity-90'
              : 'border-stone-200 bg-stone-50 opacity-90';
          const iconBg = isActive ? 'bg-orange-600' : isServed ? 'bg-emerald-600' : 'bg-stone-500';
          const reasonClr = isActive ? 'text-orange-700' : isServed ? 'text-emerald-700' : 'text-stone-600';
          return (
            <Card
              key={call.id}
              data-testid={isActive ? 'table-call-alert' : 'table-call-completed'}
              data-status={call.status}
              className={cardCls}
            >
              <CardContent className="p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
                      <Icon className="text-white" size={24} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-stone-900 truncate text-lg">{call.table_label}</p>
                      <p className={`text-base truncate ${reasonClr}`}>{call.reason_label}</p>
                      <p className="text-sm text-stone-500 mt-0.5">by {call.customer_name}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0 items-center">
                    <Button variant="outline" size="sm" onClick={() => loadChat(call.table_id)} className="gap-1">
                      <MessageSquare size={14} /> Chat
                    </Button>
                    {isActive ? (
                      call.is_order ? (
                        <Button data-testid={`mark-served-btn-${call.id}`} size="sm" onClick={() => markServed(call.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1">
                          <CheckCircle size={14} /> Mark Served
                        </Button>
                      ) : (
                        <Button data-testid={`done-btn-${call.id}`} size="sm" onClick={() => dismissCall(call.id)} className="bg-green-600 hover:bg-green-700 text-white gap-1">
                          <CheckCircle size={14} /> Done
                        </Button>
                      )
                    ) : (
                      <Badge
                        data-testid={`status-badge-${call.id}`}
                        className={isServed ? 'bg-emerald-100 text-emerald-700 border-emerald-200 gap-1' : 'bg-green-100 text-green-700 border-green-200 gap-1'}
                      >
                        <CheckCircle size={12} /> {isServed ? 'Served' : 'Done'}
                      </Badge>
                    )}
                  </div>
                </div>
                {call.is_order && Array.isArray(call.order_items) && call.order_items.length > 0 && (
                  <div className={`rounded-lg bg-white border p-3 text-sm ${isActive ? 'border-orange-200' : 'border-stone-200'}`}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Order</div>
                    <ul className="space-y-1">
                      {call.order_items.map((it, idx) => (
                        <li key={idx} className="flex items-center justify-between gap-2">
                          <span className="text-stone-700 truncate"><span className="font-semibold">{it.qty}×</span> {it.name}</span>
                          <span className="text-stone-500 font-mono text-xs">{formatPrice(it.line_total ?? (it.price * it.qty), restaurantCurrency)}</span>
                        </li>
                      ))}
                    </ul>
                    {typeof call.order_total === 'number' && (
                      <div className="mt-2 pt-2 border-t border-stone-100 flex items-center justify-between">
                        <span className="text-xs font-semibold text-stone-600">Total</span>
                        <span className={`text-sm font-bold ${isActive ? 'text-orange-700' : 'text-stone-700'}`}>{formatPrice(call.order_total, restaurantCurrency)}</span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
              </div>
            )}
          </div>
          );
              })}
            </div>
          );
        })}
      </div>
      {/* Reset Table confirmation — wipes the call history for this table so
          it's a fresh slate for the next customer. Idempotent on the backend. */}
      <AlertDialog open={!!resetConfirm} onOpenChange={(open) => { if (!open) setResetConfirm(null); }}>
        <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Outfit' }}>Reset {resetConfirm?.table_label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears all served and unattended calls for this table so the next customer starts with a clean history. The action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-reset-table-btn"
              onClick={() => {
                if (resetConfirm?.table_id) resetTable(resetConfirm.table_id);
                setResetConfirm(null);
              }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Reset table
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ==================== CALL HISTORY / ANALYTICS PANEL ====================
function formatSeconds(s) {
  if (s == null || s === 0) return '0s';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

function CallHistoryPanel({ restaurant, setRestaurant }) {
  const [history, setHistory] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [kpiMinutes, setKpiMinutes] = useState(restaurant?.kpi_target_minutes || 5);
  const [savingKpi, setSavingKpi] = useState(false);
  const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } })();
  const tz = restaurant?.timezone || browserTz;

  useEffect(() => {
    if (restaurant?.kpi_target_minutes != null) setKpiMinutes(restaurant.kpi_target_minutes);
  }, [restaurant?.id, restaurant?.kpi_target_minutes]);

  const fetchHistory = useCallback(async (p) => {
    setLoading(true);
    try {
      const tzParam = encodeURIComponent(tz);
      const [h, r] = await Promise.all([
        axios.get(`${API}/calls/history?limit=200&period=${p}&tz=${tzParam}`, { withCredentials: true }),
        axios.get(`${API}/ratings/stats?period=${p}`, { withCredentials: true }),
      ]);
      setHistory(h.data);
      setRatings(r.data);
    } catch { toast.error('Failed to load history'); }
    setLoading(false);
  }, [tz]);

  useEffect(() => { fetchHistory(period); }, [period, fetchHistory]);

  useEffect(() => {
    const interval = setInterval(() => fetchHistory(period), 10000);
    return () => clearInterval(interval);
  }, [period, fetchHistory]);

  const saveKpi = async () => {
    const v = parseFloat(kpiMinutes);
    if (isNaN(v) || v <= 0) { toast.error('KPI must be a positive number'); return; }
    try {
      setSavingKpi(true);
      const r = await axios.put(`${API}/restaurant`, { kpi_target_minutes: v }, { withCredentials: true });
      setRestaurant(r.data);
      toast.success(`KPI target saved: ${v} min`);
      fetchHistory(period);
    } catch { toast.error('Failed to save KPI'); }
    finally { setSavingKpi(false); }
  };

  const periods = [
    { id: 'today', label: 'Today' },
    { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' },
    { id: 'all', label: 'All Time' },
  ];

  if (loading && !history) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-3 border-orange-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!history) return null;

  const { stats, calls } = history;
  const resp = stats.response || { sample_count: 0, percentile_curve: [], distribution: [], by_hour: [], within_kpi_pct: 0, avg_seconds: 0, median_seconds: 0 };
  const kpiSeconds = (kpiMinutes || 5) * 60;

  return (
    <div data-testid="call-history-panel" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Performance</h2>
          <p className="text-sm text-stone-500 mt-1">How quickly your team attends customer calls</p>
        </div>
        <div data-testid="history-period-filter" className="flex gap-1 bg-stone-100 p-1 rounded-lg">
          {periods.map(p => (
            <button
              key={p.id}
              data-testid={`period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${period === p.id ? 'bg-white text-orange-700 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI target control */}
      <Card className="border-stone-200">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-stone-500">KPI Target (minutes)</Label>
            <p className="text-xs text-stone-400">Calls answered within this time count as "on target"</p>
          </div>
          <Input data-testid="kpi-target-input" type="number" min={0.5} step={0.5} value={kpiMinutes} onChange={e => setKpiMinutes(e.target.value)} className="w-28" />
          <Button data-testid="kpi-save-btn" onClick={saveKpi} disabled={savingKpi} className="bg-orange-600 hover:bg-orange-700 text-white">
            {savingKpi ? 'Saving…' : 'Save KPI'}
          </Button>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-stone-200"><CardContent className="p-5 text-center">
          <p className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>{stats.total}</p>
          <p className="text-sm text-stone-500 mt-1">Total Calls</p>
        </CardContent></Card>
        <Card className="border-green-200 bg-green-50"><CardContent className="p-5 text-center">
          <p className="text-3xl font-bold text-green-700" style={{ fontFamily: 'Outfit' }}>{resp.within_kpi_pct}%</p>
          <p className="text-sm text-green-700 mt-1">Within {kpiMinutes}m KPI</p>
        </CardContent></Card>
        <Card className="border-blue-200 bg-blue-50"><CardContent className="p-5 text-center">
          <p className="text-3xl font-bold text-blue-700" style={{ fontFamily: 'Outfit' }}>{formatSeconds(resp.avg_seconds)}</p>
          <p className="text-sm text-blue-700 mt-1">Avg Response</p>
        </CardContent></Card>
        <Card className="border-orange-200 bg-orange-50"><CardContent className="p-5 text-center">
          <p className="text-3xl font-bold text-orange-700" style={{ fontFamily: 'Outfit' }}>{formatSeconds(resp.median_seconds)}</p>
          <p className="text-sm text-orange-700 mt-1">Median Response</p>
        </CardContent></Card>
      </div>

      {/* 3 Performance Graphs */}
      <div className="grid lg:grid-cols-3 gap-4">
        <PercentileChart data={resp.percentile_curve} kpiSeconds={kpiSeconds} sample={resp.sample_count} />
        <DistributionChart data={resp.distribution} sample={resp.sample_count} />
        <HourlyChart data={resp.by_hour} kpiSeconds={kpiSeconds} />
      </div>

      {/* Breakdown: Calls by Reason + Calls by Table */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-stone-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <BarChart3 size={16} className="text-orange-600" /> Calls by Reason
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats.by_reason.length === 0 && <p className="text-sm text-stone-400">No data yet</p>}
            {stats.by_reason.map((r, i) => {
              const maxReason = Math.max(...stats.by_reason.map(x => x.count), 1);
              return (
                <div key={i} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-700 font-medium">{r.reason || 'Unknown'}</span>
                    <span className="text-stone-500">{r.count}</span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${(r.count / maxReason) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <LayoutGrid size={16} className="text-teal-600" /> Calls by Table
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats.by_table.length === 0 && <p className="text-sm text-stone-400">No data yet</p>}
            {stats.by_table.map((t, i) => {
              const maxTable = Math.max(...stats.by_table.map(x => x.count), 1);
              return (
                <div key={i} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-700 font-medium">{t.table || 'Unknown'}</span>
                    <span className="text-stone-500">{t.count}</span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${(t.count / maxTable) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Orders KPI block (placed above Customer Ratings) */}
      <OrdersKpiBlock period={period} tz={tz} restaurantCurrency={restaurant?.currency || 'USD'} />

      {/* Customer Ratings — compact single-card layout (one row per category). */}
      {ratings && (
        <Card className="border-stone-200">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <Star size={18} className="text-yellow-500 fill-yellow-400" /> Customer Ratings
                </CardTitle>
                <p className="text-xs text-stone-500 mt-0.5">{ratings.total} rating{ratings.total !== 1 ? 's' : ''} in selected period</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-stone-500 uppercase tracking-wide">Overall</p>
                <p className="text-xl font-bold text-stone-900 leading-none" style={{ fontFamily: 'Outfit' }}>
                  {ratings.overall_avg || 0}<span className="text-sm text-stone-400 font-normal">/5</span>
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="divide-y divide-stone-100">
              {[
                { key: 'service', label: 'Service' },
                { key: 'speed', label: 'Speed' },
                { key: 'food', label: 'Food' },
                { key: 'cleanliness', label: 'Cleanliness' },
              ].map(cat => {
                const a = ratings.averages?.[cat.key] || { avg: 0, count: 0 };
                const rounded = Math.round(a.avg);
                return (
                  <div
                    key={cat.key}
                    data-testid={`rating-stat-${cat.key}`}
                    className="flex items-center justify-between py-2.5"
                  >
                    <span className="text-sm font-medium text-stone-800">{cat.label}</span>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star
                            key={s}
                            size={16}
                            className={s <= rounded ? 'fill-yellow-400 text-yellow-400' : 'text-stone-300'}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-stone-400 font-mono w-16 text-right">
                        {a.avg ? `${a.avg}/5` : '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {ratings.total === 0 && (
              <p className="text-xs text-stone-400 text-center mt-3">No ratings yet. Enable "Ask for rating on exit" in Settings to collect feedback.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Call history list */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>Recent Calls</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {calls.length === 0 && <p className="text-sm text-stone-400 text-center py-4">No call history yet</p>}
            {calls.map(call => {
              const Icon = getIcon(call.reason_icon);
              const time = new Date(call.created_at);
              const timeStr = time.toLocaleDateString([], { timeZone: tz }) + ' ' + time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: tz });
              let respStr = null;
              if (call.status === 'dismissed' && call.dismissed_at) {
                const secs = Math.max(0, (new Date(call.dismissed_at) - time) / 1000);
                respStr = formatSeconds(secs);
              }
              const onTarget = call.status === 'dismissed' && call.dismissed_at && ((new Date(call.dismissed_at) - time) / 1000) <= kpiSeconds;
              return (
                <div key={call.id} className="flex items-center gap-3 p-3 rounded-lg bg-stone-50 border border-stone-100">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${call.status === 'active' ? 'bg-orange-100' : 'bg-green-100'}`}>
                    <Icon size={16} className={call.status === 'active' ? 'text-orange-600' : 'text-green-600'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800">{call.table_label} &mdash; {call.reason_label}</p>
                    <p className="text-xs text-stone-500">{call.customer_name} &middot; {timeStr}{respStr ? ` · attended in ${respStr}` : ''}</p>
                  </div>
                  {call.status === 'active' ? (
                    <Badge className="bg-orange-100 text-orange-700 border-orange-200">active</Badge>
                  ) : (
                    <Badge className={onTarget ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}>{onTarget ? 'on target' : 'late'}</Badge>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
function PercentileChart({ data, kpiSeconds, sample }) {
  // data: [{ p: 50, seconds }, { p: 75, seconds }, ...]
  const points = data && data.length ? data : [{ p: 50, seconds: 0 }, { p: 75, seconds: 0 }, { p: 90, seconds: 0 }, { p: 95, seconds: 0 }, { p: 99, seconds: 0 }];
  const maxY = Math.max(kpiSeconds * 1.5, ...points.map(p => p.seconds), 60);
  const w = 320, h = 180, pad = { l: 36, r: 8, t: 12, b: 26 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const x = (p) => pad.l + ((p.p - 50) / 49) * innerW; // 50..99 → 0..innerW
  const y = (s) => pad.t + innerH - (Math.min(s, maxY) / maxY) * innerH;
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p)} ${y(p.seconds)}`).join(' ');
  const kpiY = y(kpiSeconds);
  return (
    <Card className="border-stone-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <BarChart3 size={16} className="text-orange-600" /> Response time by percentile
        </CardTitle>
        <p className="text-xs text-stone-500">{sample} attended calls</p>
      </CardHeader>
      <CardContent>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
          {/* Y grid */}
          {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
            <g key={i}>
              <line x1={pad.l} y1={pad.t + innerH * f} x2={w - pad.r} y2={pad.t + innerH * f} stroke="#e5e7eb" strokeWidth="0.5" />
              <text x={pad.l - 4} y={pad.t + innerH * f + 3} fontSize="8" textAnchor="end" fill="#9ca3af">{formatSeconds(maxY * (1 - f))}</text>
            </g>
          ))}
          {/* KPI target line */}
          {kpiY >= pad.t && kpiY <= pad.t + innerH && (
            <g>
              <line x1={pad.l} y1={kpiY} x2={w - pad.r} y2={kpiY} stroke="#16a34a" strokeWidth="1" strokeDasharray="3 2" />
              <text x={w - pad.r - 2} y={kpiY - 3} fontSize="8" textAnchor="end" fill="#16a34a" fontWeight="700">KPI {formatSeconds(kpiSeconds)}</text>
            </g>
          )}
          {/* Line path */}
          <path d={path} fill="none" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {/* Points */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={x(p)} cy={y(p.seconds)} r="3" fill="#ea580c" />
              <text x={x(p)} y={y(p.seconds) - 6} fontSize="8" textAnchor="middle" fill="#ea580c" fontWeight="700">{formatSeconds(p.seconds)}</text>
              <text x={x(p)} y={pad.t + innerH + 14} fontSize="8" textAnchor="middle" fill="#6b7280">P{p.p}</text>
            </g>
          ))}
        </svg>
      </CardContent>
    </Card>
  );
}

function DistributionChart({ data, sample }) {
  const points = data && data.length ? data : [];
  const max = Math.max(1, ...points.map(p => p.count));
  const w = 320, h = 180, pad = { l: 28, r: 8, t: 12, b: 26 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const bw = innerW / Math.max(1, points.length) * 0.7;
  const gap = innerW / Math.max(1, points.length) * 0.3;
  return (
    <Card className="border-stone-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <BarChart3 size={16} className="text-teal-600" /> Response time distribution
        </CardTitle>
        <p className="text-xs text-stone-500">{sample} attended calls</p>
      </CardHeader>
      <CardContent>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
          {[0, 0.5, 1].map((f, i) => (
            <g key={i}>
              <line x1={pad.l} y1={pad.t + innerH * (1 - f)} x2={w - pad.r} y2={pad.t + innerH * (1 - f)} stroke="#e5e7eb" strokeWidth="0.5" />
              <text x={pad.l - 4} y={pad.t + innerH * (1 - f) + 3} fontSize="8" textAnchor="end" fill="#9ca3af">{Math.round(max * f)}</text>
            </g>
          ))}
          {points.map((b, i) => {
            const bh = (b.count / max) * innerH;
            const bx = pad.l + i * (bw + gap) + gap / 2;
            const by = pad.t + innerH - bh;
            const colors = ['#10b981', '#22c55e', '#eab308', '#f97316', '#ef4444'];
            return (
              <g key={i}>
                <rect x={bx} y={by} width={bw} height={bh} fill={colors[i] || '#0d9488'} rx="2" />
                {b.count > 0 && <text x={bx + bw / 2} y={by - 3} fontSize="8" textAnchor="middle" fill="#374151" fontWeight="700">{b.count}</text>}
                <text x={bx + bw / 2} y={pad.t + innerH + 14} fontSize="8" textAnchor="middle" fill="#6b7280">{b.label}</text>
              </g>
            );
          })}
        </svg>
      </CardContent>
    </Card>
  );
}

function HourlyChart({ data, kpiSeconds }) {
  const hours = data || [];
  const maxAvg = Math.max(kpiSeconds * 1.2, ...hours.map(h => h.avg_seconds), 30);
  const w = 320, h = 180, pad = { l: 28, r: 8, t: 12, b: 26 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const bw = innerW / 24 * 0.75;
  const gap = innerW / 24 * 0.25;
  const kpiY = pad.t + innerH - (kpiSeconds / maxAvg) * innerH;
  const totalCalls = hours.reduce((s, h) => s + h.count, 0);
  return (
    <Card className="border-stone-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <BarChart3 size={16} className="text-violet-600" /> Avg response by hour
        </CardTitle>
        <p className="text-xs text-stone-500">{totalCalls} attended calls</p>
      </CardHeader>
      <CardContent>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
          {[0, 0.5, 1].map((f, i) => (
            <g key={i}>
              <line x1={pad.l} y1={pad.t + innerH * (1 - f)} x2={w - pad.r} y2={pad.t + innerH * (1 - f)} stroke="#e5e7eb" strokeWidth="0.5" />
              <text x={pad.l - 4} y={pad.t + innerH * (1 - f) + 3} fontSize="8" textAnchor="end" fill="#9ca3af">{formatSeconds(maxAvg * f)}</text>
            </g>
          ))}
          {kpiY >= pad.t && kpiY <= pad.t + innerH && (
            <g>
              <line x1={pad.l} y1={kpiY} x2={w - pad.r} y2={kpiY} stroke="#16a34a" strokeWidth="1" strokeDasharray="3 2" />
              <text x={w - pad.r - 2} y={kpiY - 3} fontSize="8" textAnchor="end" fill="#16a34a" fontWeight="700">KPI</text>
            </g>
          )}
          {hours.map((hh, i) => {
            const bh = (hh.avg_seconds / maxAvg) * innerH;
            const bx = pad.l + i * (innerW / 24) + gap / 2;
            const by = pad.t + innerH - bh;
            const overKpi = hh.avg_seconds > kpiSeconds;
            return (
              <g key={i}>
                <rect x={bx} y={by} width={bw} height={bh} fill={hh.count === 0 ? '#e5e7eb' : overKpi ? '#ef4444' : '#8b5cf6'} rx="1" />
                {i % 3 === 0 && <text x={bx + bw / 2} y={pad.t + innerH + 14} fontSize="8" textAnchor="middle" fill="#6b7280">{i}</text>}
              </g>
            );
          })}
          <text x={pad.l + innerW / 2} y={h - 2} fontSize="8" textAnchor="middle" fill="#9ca3af">hour of day (0-23)</text>
        </svg>
      </CardContent>
    </Card>
  );
}

// ==================== ORDERS KPI BLOCK (3 graphs by category, timezone-aware) ====================
function OrdersKpiBlock({ period, tz, restaurantCurrency = 'USD' }) {
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchKpi = useCallback(async () => {
    setLoading(true);
    try {
      const tzParam = encodeURIComponent(tz || 'UTC');
      const { data } = await axios.get(`${API}/orders/kpi?period=${period}&tz=${tzParam}`, { withCredentials: true });
      setKpi(data);
    } catch { /* silent */ }
    setLoading(false);
  }, [period, tz]);

  useEffect(() => { fetchKpi(); }, [fetchKpi]);
  useEffect(() => {
    const i = setInterval(fetchKpi, 15000);
    return () => clearInterval(i);
  }, [fetchKpi]);

  if (loading && !kpi) {
    return (
      <Card data-testid="orders-kpi-block" className="border-stone-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Receipt size={18} className="text-emerald-600" /> Orders KPI
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }
  if (!kpi) return null;

  const cats = kpi.by_category || [];
  const hours = kpi.hourly || [];

  return (
    <div data-testid="orders-kpi-block" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold text-stone-900 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Receipt size={18} className="text-emerald-600" /> Orders KPI
          </h3>
          <p className="text-xs text-stone-500">Per-category fulfilment metrics &middot; timezone <span className="font-mono">{tz}</span></p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-stone-200"><CardContent className="p-4 text-center">
          <p data-testid="orders-kpi-total" className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>{kpi.total_orders}</p>
          <p className="text-xs text-stone-500 mt-1">Total Orders</p>
        </CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-4 text-center">
          <p data-testid="orders-kpi-served" className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Outfit' }}>{kpi.served}</p>
          <p className="text-xs text-emerald-700 mt-1">Served</p>
        </CardContent></Card>
        <Card className="border-amber-200 bg-amber-50"><CardContent className="p-4 text-center">
          <p data-testid="orders-kpi-open" className="text-2xl font-bold text-amber-700" style={{ fontFamily: 'Outfit' }}>{kpi.open}</p>
          <p className="text-xs text-amber-700 mt-1">In progress</p>
        </CardContent></Card>
        <Card className="border-blue-200 bg-blue-50"><CardContent className="p-4 text-center">
          <p data-testid="orders-kpi-within" className="text-2xl font-bold text-blue-700" style={{ fontFamily: 'Outfit' }}>{kpi.overall_within_target_pct}%</p>
          <p className="text-xs text-blue-700 mt-1">Within target</p>
        </CardContent></Card>
      </div>

      {/* 3 Graphs */}
      <div className="grid lg:grid-cols-3 gap-4">
        <OrdersByCategoryChart cats={cats} />
        <OrdersFulfilmentChart cats={cats} />
        <OrdersHourlyChart hours={hours} />
      </div>

      {/* Per-category breakdown table */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <BarChart3 size={16} className="text-emerald-600" /> Category breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cats.length === 0 ? (
            <p className="text-sm text-stone-400 text-center py-4">No orders in selected period yet.</p>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wide text-stone-400 font-medium px-2">
                <div className="col-span-3">Category</div>
                <div className="col-span-2 text-right">Ordered</div>
                <div className="col-span-2 text-right">Served</div>
                <div className="col-span-2 text-right">Avg (min)</div>
                <div className="col-span-1 text-right">Target</div>
                <div className="col-span-2 text-right">Within %</div>
              </div>
              {cats.map((c, i) => {
                const onTarget = c.within_target_pct >= 80;
                return (
                  <div key={i} data-testid={`orders-kpi-row-${i}`} className="grid grid-cols-12 gap-2 items-center text-sm bg-stone-50 border border-stone-100 rounded-md px-2 py-1.5">
                    <div className="col-span-3 font-medium text-stone-800 truncate">{c.name}</div>
                    <div className="col-span-2 text-right text-stone-700">{c.ordered_count}</div>
                    <div className="col-span-2 text-right text-stone-700">{c.served_count}</div>
                    <div className="col-span-2 text-right text-stone-700">{c.avg_minutes ?? '—'}</div>
                    <div className="col-span-1 text-right text-stone-500">{c.target_minutes}m</div>
                    <div className={`col-span-2 text-right font-semibold ${onTarget ? 'text-emerald-700' : 'text-red-600'}`}>{c.within_target_pct}%</div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function OrdersByCategoryChart({ cats }) {
  const top = (cats || []).slice(0, 8);
  const maxV = Math.max(1, ...top.map(c => c.ordered_count || 0));
  return (
    <Card className="border-stone-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <BarChart3 size={16} className="text-emerald-600" /> Orders by category
        </CardTitle>
        <p className="text-xs text-stone-500">{top.length === 0 ? 'No data yet' : `Top ${top.length} categories`}</p>
      </CardHeader>
      <CardContent>
        {top.length === 0 ? (
          <div className="h-[140px] flex items-center justify-center text-xs text-stone-400">No orders yet</div>
        ) : (
          <div className="space-y-2">
            {top.map((c, i) => {
              const w = (c.ordered_count / maxV) * 100;
              const servedW = c.ordered_count > 0 ? (c.served_count / c.ordered_count) * w : 0;
              return (
                <div key={i} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-stone-700 font-medium truncate">{c.name}</span>
                    <span className="text-stone-500 ml-2">{c.served_count}/{c.ordered_count}</span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden relative">
                    <div className="absolute inset-y-0 left-0 bg-stone-300 rounded-full" style={{ width: `${w}%` }} />
                    <div className="absolute inset-y-0 left-0 bg-emerald-500 rounded-full" style={{ width: `${servedW}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OrdersFulfilmentChart({ cats }) {
  const top = (cats || []).filter(c => c.avg_minutes != null).slice(0, 8);
  const maxY = Math.max(5, ...top.map(c => Math.max(c.avg_minutes || 0, c.target_minutes || 0)));
  const w = 320, h = 180, pad = { l: 28, r: 8, t: 12, b: 36 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const slot = top.length > 0 ? innerW / top.length : innerW;
  const bw = slot * 0.5;
  return (
    <Card className="border-stone-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <BarChart3 size={16} className="text-orange-600" /> Avg fulfilment vs target
        </CardTitle>
        <p className="text-xs text-stone-500">Minutes from order to mark-served</p>
      </CardHeader>
      <CardContent>
        {top.length === 0 ? (
          <div className="h-[140px] flex items-center justify-center text-xs text-stone-400">No served orders yet</div>
        ) : (
          <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
            {[0, 0.5, 1].map((f, i) => (
              <g key={i}>
                <line x1={pad.l} y1={pad.t + innerH * (1 - f)} x2={w - pad.r} y2={pad.t + innerH * (1 - f)} stroke="#e5e7eb" strokeWidth="0.5" />
                <text x={pad.l - 4} y={pad.t + innerH * (1 - f) + 3} fontSize="8" textAnchor="end" fill="#9ca3af">{Math.round(maxY * f)}m</text>
              </g>
            ))}
            {top.map((c, i) => {
              const avg = c.avg_minutes || 0;
              const tgt = c.target_minutes || 0;
              const bx = pad.l + i * slot + (slot - bw) / 2;
              const bh = (avg / maxY) * innerH;
              const by = pad.t + innerH - bh;
              const tgtY = pad.t + innerH - (tgt / maxY) * innerH;
              const overTarget = avg > tgt;
              return (
                <g key={i}>
                  <rect x={bx} y={by} width={bw} height={bh} fill={overTarget ? '#ef4444' : '#10b981'} rx="2" />
                  <line x1={bx - 2} y1={tgtY} x2={bx + bw + 2} y2={tgtY} stroke="#16a34a" strokeWidth="1.4" strokeDasharray="3 2" />
                  <text x={bx + bw / 2} y={by - 3} fontSize="8" textAnchor="middle" fill="#374151" fontWeight="700">{avg}</text>
                  <text x={bx + bw / 2} y={pad.t + innerH + 12} fontSize="8" textAnchor="middle" fill="#6b7280">
                    <tspan>{(c.name || '').slice(0, 8)}</tspan>
                  </text>
                </g>
              );
            })}
            <text x={pad.l} y={h - 4} fontSize="8" fill="#16a34a" fontWeight="700">— target</text>
          </svg>
        )}
      </CardContent>
    </Card>
  );
}

function OrdersHourlyChart({ hours }) {
  const arr = hours && hours.length === 24 ? hours : Array(24).fill(0);
  const total = arr.reduce((s, n) => s + n, 0);
  const maxV = Math.max(1, ...arr);
  const w = 320, h = 180, pad = { l: 24, r: 8, t: 12, b: 26 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const bw = innerW / 24 * 0.75;
  const gap = innerW / 24 * 0.25;
  return (
    <Card className="border-stone-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <BarChart3 size={16} className="text-violet-600" /> Orders by hour
        </CardTitle>
        <p className="text-xs text-stone-500">{total} orders &middot; host local time</p>
      </CardHeader>
      <CardContent>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
          {[0, 0.5, 1].map((f, i) => (
            <g key={i}>
              <line x1={pad.l} y1={pad.t + innerH * (1 - f)} x2={w - pad.r} y2={pad.t + innerH * (1 - f)} stroke="#e5e7eb" strokeWidth="0.5" />
              <text x={pad.l - 4} y={pad.t + innerH * (1 - f) + 3} fontSize="8" textAnchor="end" fill="#9ca3af">{Math.round(maxV * f)}</text>
            </g>
          ))}
          {arr.map((n, i) => {
            const bh = (n / maxV) * innerH;
            const bx = pad.l + i * (innerW / 24) + gap / 2;
            const by = pad.t + innerH - bh;
            return (
              <g key={i}>
                <rect x={bx} y={by} width={bw} height={bh} fill={n === 0 ? '#e5e7eb' : '#8b5cf6'} rx="1" />
                {i % 3 === 0 && <text x={bx + bw / 2} y={pad.t + innerH + 14} fontSize="8" textAnchor="middle" fill="#6b7280">{i}</text>}
              </g>
            );
          })}
          <text x={pad.l + innerW / 2} y={h - 2} fontSize="8" textAnchor="middle" fill="#9ca3af">hour of day (0-23)</text>
        </svg>
      </CardContent>
    </Card>
  );
}



// ==================== CHAT PANEL (REST-based sending + polling) ====================
function ChatPanel({ tables, chatMessages, selectedChatTable, setSelectedChatTable, sendHostMessage, loadChat, restaurant }) {
  const [message, setMessage] = useState('');
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const msgs = chatMessages[selectedChatTable] || [];

  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      // Try to find the scrollable viewport inside ScrollArea
      const viewport = el.querySelector('[data-radix-scroll-area-viewport]') || el;
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [msgs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling for chat messages when a table is selected
  useEffect(() => {
    if (!selectedChatTable || !restaurant) return;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/chat/${restaurant.id}/${selectedChatTable}`);
        // Replace entire chat for this table
        if (data.length > 0) {
          loadChat(selectedChatTable);
        }
      } catch {}
    };
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [selectedChatTable, restaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = () => {
    if (!message.trim()) return;
    sendHostMessage(message.trim());
    setMessage('');
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <h2 className="text-xl md:text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Chat</h2>

      {/* Mobile: Show table selector if no table selected, or chat if selected */}
      {/* Desktop: Side by side */}
      <div className="flex flex-col md:flex-row gap-4 h-[calc(100vh-200px)] md:h-[calc(100vh-200px)]">
        {/* Table list — full width on mobile when no table selected, sidebar on desktop */}
        <Card className={`${selectedChatTable ? 'hidden md:block' : 'block'} md:w-64 flex-shrink-0 border-stone-200`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-stone-600">Tables</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <ScrollArea className="h-[calc(100vh-350px)] md:h-[calc(100vh-300px)]">
              {tables.map(t => (
                <button key={t.id} onClick={() => loadChat(t.id)}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-all ${
                    selectedChatTable === t.id ? 'bg-orange-50 text-orange-700 border border-orange-200' : 'hover:bg-stone-100 text-stone-700'
                  }`}>
                  <p className="font-medium text-sm">{t.label}</p>
                  <p className="text-xs text-stone-400">{(chatMessages[t.id] || []).length} messages</p>
                </button>
              ))}
              {tables.length === 0 && <p className="text-xs text-stone-400 text-center py-4">No tables yet</p>}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat area — full width on mobile when table selected */}
        <Card className={`${!selectedChatTable ? 'hidden md:flex' : 'flex'} flex-1 border-stone-200 flex-col`}>
          {selectedChatTable ? (
            <>
              <CardHeader className="pb-2 border-b border-stone-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-stone-700">
                    {tables.find(t => t.id === selectedChatTable)?.label || 'Chat'}
                  </CardTitle>
                  {/* Back button — mobile only */}
                  <button onClick={() => setSelectedChatTable(null)} className="md:hidden text-xs text-stone-500 hover:text-stone-700 px-2 py-1 rounded bg-stone-100">
                    Back to tables
                  </button>
                </div>
              </CardHeader>
              <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                <div className="space-y-3">
                  {msgs.map(m => (
                    <div key={m.id} className={`flex ${m.sender_type === 'host' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                        m.sender_type === 'host'
                          ? 'bg-orange-600 text-white rounded-br-md'
                          : 'bg-stone-100 text-stone-800 rounded-bl-md'
                      }`}>
                        <p className="text-xs font-medium opacity-70 mb-0.5">{m.sender_name}</p>
                        <p>{m.content}</p>
                      </div>
                    </div>
                  ))}
                  {msgs.length === 0 && <p className="text-center text-stone-400 text-sm py-8">No messages yet. Select a table and start chatting.</p>}
                </div>
              </ScrollArea>
              <div className="p-3 border-t border-stone-100 flex gap-2">
                <Input data-testid="chat-message-input" value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="Type a message..." className="flex-1"
                  onKeyDown={e => e.key === 'Enter' && send()} />
                <Button data-testid="chat-send-button" onClick={send} className="bg-orange-600 hover:bg-orange-700 text-white px-4">
                  <Send size={16} />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-stone-400">
              <div className="text-center">
                <MessageSquare size={40} className="mx-auto mb-3" />
                <p>Select a table to start chatting</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ==================== QR CODES PANEL ====================
function QRCodesPanel({ tables, restaurant, setRestaurant, frontendUrl, siteSettings }) {
  const [form, setForm] = useState({
    qr_style: 'square',
    qr_cell_width_mm: restaurant?.qr_cell_width_mm || 60,
    qr_cell_height_mm: restaurant?.qr_cell_height_mm || 80,
    qr_size_mm: restaurant?.qr_size_mm || 45,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (restaurant) setForm({
      qr_style: 'square',
      qr_cell_width_mm: restaurant.qr_cell_width_mm || 60,
      qr_cell_height_mm: restaurant.qr_cell_height_mm || 80,
      qr_size_mm: restaurant.qr_size_mm || 45,
    });
  }, [restaurant?.id]);

  const save = async () => {
    try {
      setSaving(true);
      const r = await axios.put(`${API}/restaurant`, form, { withCredentials: true });
      setRestaurant(r.data);
      toast.success('QR settings saved');
    } catch (e) { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const siteName = (siteSettings?.site_name || 'OLAFE').toString();

  // A4: 210mm × 297mm, 10mm margin → 190 × 277 usable
  const usableW = 190, usableH = 277;
  const cw = Math.max(15, Number(form.qr_cell_width_mm) || 60);
  const ch = Math.max(15, Number(form.qr_cell_height_mm) || 80);
  const qm = Math.max(10, Math.min(cw, ch) - 5, Number(form.qr_size_mm) || 45);
  const cols = Math.max(1, Math.floor(usableW / cw));
  const rows = Math.max(1, Math.floor(usableH / ch));
  const perPage = cols * rows;
  const totalPages = Math.max(1, Math.ceil((tables.length || 1) / perPage));

  const pages = [];
  for (let p = 0; p < totalPages; p++) pages.push(tables.slice(p * perPage, (p + 1) * perPage));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>QR Codes</h2>
          <p className="text-sm text-stone-500 mt-1">Choose a style, set the printed size and print on A4 paper</p>
        </div>
        {tables.length > 0 && (
          <Button data-testid="qr-print-btn" onClick={() => window.print()} className="bg-stone-900 hover:bg-stone-800 text-white">
            <QrCode size={16} className="mr-2" /> Print {totalPages} A4 page{totalPages !== 1 ? 's' : ''}
          </Button>
        )}
      </div>

      <Card className="border-stone-200 print:hidden">
        <CardHeader className="pb-3"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>QR Code Size</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
            <div className="space-y-1">
              <Label className="text-xs">Cell Width (mm)</Label>
              <Input data-testid="qr-cell-width-input" type="number" min={20} max={100} step={0.5} value={form.qr_cell_width_mm} onChange={e => setForm(p => ({ ...p, qr_cell_width_mm: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cell Height (mm)</Label>
              <Input data-testid="qr-cell-height-input" type="number" min={20} max={150} step={0.5} value={form.qr_cell_height_mm} onChange={e => setForm(p => ({ ...p, qr_cell_height_mm: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">QR Code Size (mm)</Label>
              <Input data-testid="qr-size-input" type="number" min={15} max={100} step={0.5} value={form.qr_size_mm} onChange={e => setForm(p => ({ ...p, qr_size_mm: parseFloat(e.target.value) || 0 }))} />
            </div>
          </div>
          <div className="text-xs text-stone-500 bg-stone-50 rounded-lg px-3 py-2 border border-stone-200">
            A4 fits <span className="font-semibold text-stone-700">{cols} × {rows} = {perPage}</span> code{perPage !== 1 ? 's' : ''} per page. {tables.length} table{tables.length !== 1 ? 's' : ''} → <span className="font-semibold text-stone-700">{totalPages}</span> page{totalPages !== 1 ? 's' : ''}.
          </div>
          <Button data-testid="qr-save-settings-btn" onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white">
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </CardContent>
      </Card>

      {tables.length === 0 && (
        <Card className="border-stone-200 border-dashed print:hidden">
          <CardContent className="py-12 text-center">
            <QrCode className="mx-auto text-stone-300 mb-3" size={40} />
            <p className="text-stone-500">Add tables first to generate QR codes.</p>
          </CardContent>
        </Card>
      )}

      {tables.length > 0 && (
        <div className="print:hidden">
          <h3 className="text-sm font-semibold text-stone-700 mb-3">Preview</h3>
          <div className="flex flex-wrap gap-4 items-start">
            {tables.map(t => {
              const url = t.short_code ? `${frontendUrl}/q/${t.short_code}` : `${frontendUrl}/r/${restaurant?.id}/table/${t.id}`;
              return (
                <div key={t.id} data-testid={`qr-preview-${t.id}`} style={{ width: cw + 'mm', height: ch + 'mm', background: '#fff' }} className="rounded border border-stone-200 overflow-hidden">
                  <QRCell table={t} url={url} qrSizeMm={qm} siteName={siteName} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hidden print pages — shown only when printing */}
      <div className="qr-print-only">
        {pages.map((page, pi) => (
          <div key={pi} className="a4-page">
            <div className="a4-grid" style={{ gridTemplateColumns: `repeat(${cols}, ${cw}mm)`, gridAutoRows: `${ch}mm` }}>
              {page.map(t => {
                const url = t.short_code ? `${frontendUrl}/q/${t.short_code}` : `${frontendUrl}/r/${restaurant?.id}/table/${t.id}`;
                return (
                  <div key={t.id} className="a4-cell" style={{ width: cw + 'mm', height: ch + 'mm' }}>
                    <QRCell table={t} url={url} qrSizeMm={qm} siteName={siteName} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function extractTableNumber(label) {
  if (!label) return '';
  const m = String(label).match(/\d+/);
  return m ? m[0] : label;
}

function QRCell({ table, url, qrSizeMm, siteName }) {
  const num = extractTableNumber(table.label);
  // Small white square in the centre with a simple bell silhouette containing the table number
  const logoMm = Math.max(6, qrSizeMm * 0.22);
  const rawName = (siteName || 'OLAFE').toString();
  const displayName = (rawName.replace(/[^A-Za-z0-9]+$/, '').split(/[^A-Za-z0-9]+/)[0] || rawName).toUpperCase();
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1.5mm', background: '#fff', padding: '1.5mm', boxSizing: 'border-box' }}>
      <div style={{ position: 'relative', width: qrSizeMm + 'mm', height: qrSizeMm + 'mm' }}>
        <QRCodeSVG value={url} size={512} level="M" fgColor="#000000" bgColor="#ffffff" style={{ width: '100%', height: '100%' }} />
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: logoMm + 'mm', height: logoMm + 'mm', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg viewBox="0 0 60 60" width="100%" height="100%">
            <path d="M30 10 C22 10 17 15 17 23 v10 c0 3 -1.5 6 -4 8 h34 c-2.5 -2 -4 -5 -4 -8 V23 C43 15 38 10 30 10 z" fill="#ffffff" stroke="#111111" strokeWidth="2" strokeLinejoin="round"/>
            <path d="M26 41 a4 4 0 0 0 8 0 z" fill="#ffffff" stroke="#111111" strokeWidth="2" strokeLinejoin="round"/>
            <text x="30" y="26" fontFamily="Outfit,Arial,sans-serif" fontSize="18" fontWeight="800" textAnchor="middle" dominantBaseline="central" fill="#111111">{num}</text>
          </svg>
        </div>
      </div>
      <div style={{ fontFamily: 'Outfit,Arial,sans-serif', fontWeight: 700, fontSize: Math.max(2.2, qrSizeMm * 0.065) + 'mm', color: '#111', textAlign: 'center', lineHeight: 1.2, letterSpacing: '0.3px' }}>
        {displayName} &mdash; Scan for assistance
      </div>
    </div>
  );
}



// ==================== NOTIFICATIONS PANEL ====================
function NotificationsPanel({ notifications, markNotifRead }) {
  const typeStyles = {
    info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', label: 'Info' },
    alert: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', label: 'Alert' },
    feature: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', label: 'Feature' },
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Inbox</h2>
        <p className="text-sm text-stone-500 mt-1">Messages from the CallButton admin team</p>
      </div>

      {notifications.length === 0 && (
        <Card className="border-stone-200 border-dashed">
          <CardContent className="py-12 text-center">
            <Inbox className="mx-auto text-stone-300 mb-3" size={40} />
            <p className="text-stone-500">No messages yet.</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {notifications.map(n => {
          const style = typeStyles[n.message_type] || typeStyles.info;
          return (
            <Card key={n.id} data-testid={`notification-${n.id}`} className={`${!n.read ? style.border + ' ' + style.bg : 'border-stone-200'} transition-all`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className={`font-semibold text-sm ${!n.read ? 'text-stone-900' : 'text-stone-600'}`}>{n.subject}</p>
                      <Badge variant="outline" className="text-[10px]">{style.label}</Badge>
                      {!n.read && <Badge className="bg-orange-600 text-white text-[10px]">New</Badge>}
                    </div>
                    <p className="text-sm text-stone-600">{n.content}</p>
                    <p className="text-xs text-stone-400 mt-2">From {n.sender_name} &middot; {new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  {!n.read && (
                    <Button variant="ghost" size="sm" onClick={() => markNotifRead(n.id)} className="text-xs text-stone-500 hover:text-stone-700 flex-shrink-0">
                      Mark read
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ==================== SETTINGS PANEL ====================
// ==================== ACCESS CONTROL PANEL (host only) ====================
function AccessControlPanel({ staffAccess, setStaffAccess }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyToggle, setBusyToggle] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [creating, setCreating] = useState(false);
  // Per-staff edit dialog. `editTarget` = the staff row being edited;
  // `editGrants` mirrors that staff's effective grants and is what the
  // toggles bind to. We send only the user's keys on save (partial PUT)
  // so other override keys aren't blown away.
  const [editTarget, setEditTarget] = useState(null);
  const [editGrants, setEditGrants] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [staffRes, accessRes] = await Promise.all([
        axios.get(`${API}/staff`, { withCredentials: true }),
        axios.get(`${API}/access-control`, { withCredentials: true }),
      ]);
      setStaff(staffRes.data || []);
      setStaffAccess(accessRes.data?.staff_access || null);
    } catch { toast.error('Failed to load access control'); }
    setLoading(false);
  }, [setStaffAccess]);

  useEffect(() => { reload(); }, [reload]);

  const onToggle = async (key, value) => {
    setBusyToggle(key);
    try {
      const { data } = await axios.put(`${API}/access-control`, { [key]: value }, { withCredentials: true });
      setStaffAccess(data.staff_access);
      toast.success(`${TOGGLES.find(t => t.key === key)?.label} ${value ? 'enabled' : 'disabled'} for staff`);
    } catch { toast.error('Failed to update access'); }
    setBusyToggle(null);
  };

  const onCreateStaff = async (e) => {
    e.preventDefault();
    if (!form.email.includes('@') || form.password.length < 6) {
      toast.error('Email and a 6+ character password are required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/staff`, form, { withCredentials: true });
      setForm({ name: '', email: '', password: '' });
      await reload();
      toast.success('Staff account created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create staff');
    }
    setCreating(false);
  };

  const onDeleteStaff = async (id, email) => {
    if (!window.confirm(`Remove ${email}? This deletes their login.`)) return;
    try {
      await axios.delete(`${API}/staff/${id}`, { withCredentials: true });
      await reload();
      toast.success('Staff removed');
    } catch { toast.error('Failed to remove staff'); }
  };

  const onToggleActive = async (s) => {
    try {
      await axios.put(`${API}/staff/${s.id}`, { active: !s.active }, { withCredentials: true });
      await reload();
    } catch { toast.error('Failed to update staff'); }
  };

  // Open the per-staff access dialog: fetch this staff member's effective
  // grants and seed the toggles. We don't pre-populate from `staffAccess`
  // because that's the default (which is fine for the dialog initial open
  // before the network call lands).
  const openEdit = async (s) => {
    setEditTarget(s);
    setEditGrants(null);
    setEditLoading(true);
    try {
      const { data } = await axios.get(`${API}/staff/${s.id}/access`, { withCredentials: true });
      setEditGrants(data.effective || {});
    } catch {
      toast.error('Failed to load staff access');
      setEditTarget(null);
    }
    setEditLoading(false);
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditTarget(null);
    setEditGrants(null);
  };

  const onEditToggle = (key, value) => {
    setEditGrants(g => ({ ...(g || {}), [key]: value }));
  };

  const onSaveEdit = async () => {
    if (!editTarget || !editGrants) return;
    setEditSaving(true);
    try {
      // Send only the canonical capability keys (drop anything stray).
      const payload = TOGGLES.reduce((acc, t) => {
        acc[t.key] = !!editGrants[t.key];
        return acc;
      }, {});
      await axios.put(`${API}/staff/${editTarget.id}/access`, payload, { withCredentials: true });
      toast.success(`Access updated for ${editTarget.name || editTarget.email}`);
      setEditTarget(null);
      setEditGrants(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save access');
    }
    setEditSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="access-control-panel">
      <div>
        <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Access Control</h2>
        <p className="text-sm text-stone-500 mt-1">Create staff logins and choose what each staff member can see and do.</p>
      </div>

      {/* Toggles card */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <ShieldCheck size={18} className="text-emerald-600" /> Default permissions for new staff
          </CardTitle>
          <p className="text-xs text-stone-500">These are the <strong>defaults</strong> applied to every staff account. Use the <strong>Edit access</strong> button next to each staff member below to override these for individual employees. Live Calls, Tables and Chat are always available.</p>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-stone-100">
            {TOGGLES.map(t => {
              const value = !!staffAccess?.[t.key];
              return (
                <div key={t.key} className="flex items-start justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-800">{t.label}</p>
                    <p className="text-xs text-stone-500">{t.description}</p>
                  </div>
                  <Switch
                    data-testid={`access-toggle-${t.key}`}
                    checked={value}
                    disabled={busyToggle === t.key || loading}
                    onCheckedChange={(v) => onToggle(t.key, v)}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Create staff card */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <UserPlus size={18} className="text-orange-600" /> Add staff account
          </CardTitle>
          <p className="text-xs text-stone-500">Share these credentials with your team member directly. They sign in at <span className="font-mono">/login</span>.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onCreateStaff} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm(s => ({ ...s, name: e.target.value }))} placeholder="e.g. Aisha" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input data-testid="staff-email-input" type="email" value={form.email} onChange={(e) => setForm(s => ({ ...s, email: e.target.value }))} placeholder="staff@restaurant.com" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Temp password</Label>
              <Input data-testid="staff-password-input" type="text" value={form.password} onChange={(e) => setForm(s => ({ ...s, password: e.target.value }))} placeholder="min 6 characters" minLength={6} required />
            </div>
            <Button data-testid="staff-create-btn" type="submit" disabled={creating} className="bg-orange-600 hover:bg-orange-700 text-white">
              {creating ? 'Creating…' : 'Create staff'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Staff list */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>
            Staff <span className="text-stone-400 font-normal">({staff.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-stone-400">Loading…</p>
          ) : staff.length === 0 ? (
            <p className="text-sm text-stone-400">No staff yet. Add one above to share access with your team.</p>
          ) : (
            <div className="divide-y divide-stone-100">
              {staff.map(s => (
                <div key={s.id} data-testid={`staff-row-${s.id}`} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">{s.name || s.email.split('@')[0]}</p>
                    <p className="text-xs text-stone-500 font-mono truncate">{s.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={s.active ? 'bg-green-100 text-green-700 border-green-200' : 'bg-stone-100 text-stone-500 border-stone-200'}>{s.active ? 'Active' : 'Disabled'}</Badge>
                    <Switch
                      checked={s.active}
                      onCheckedChange={() => onToggleActive(s)}
                      data-testid={`staff-active-toggle-${s.id}`}
                    />
                    <Button
                      data-testid={`staff-edit-access-btn-${s.id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(s)}
                      className="border-stone-300 text-stone-700 hover:bg-stone-50 gap-1"
                      title="Override permissions for this staff member"
                    >
                      <ShieldCheck size={14} /> Edit access
                    </Button>
                    <Button data-testid={`staff-delete-btn-${s.id}`} variant="outline" size="sm" onClick={() => onDeleteStaff(s.id, s.email)} className="border-red-200 text-red-600 hover:bg-red-50">
                      <UserMinus size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-staff access dialog. Toggles are seeded from the staff member's
          *effective* grants (their personal overrides merged onto the
          restaurant default). Save persists every shown key as an override,
          so changes here always win over the default. */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl" data-testid="staff-access-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-emerald-600" />
              Edit access — {editTarget?.name || editTarget?.email}
            </DialogTitle>
            <p className="text-xs text-stone-500 mt-1">
              These settings apply only to <strong>{editTarget?.email}</strong>. They override the restaurant-wide defaults.
            </p>
          </DialogHeader>
          {editLoading || !editGrants ? (
            <p className="py-8 text-center text-sm text-stone-400">Loading…</p>
          ) : (
            <div className="divide-y divide-stone-100">
              {TOGGLES.map(t => {
                const v = !!editGrants[t.key];
                return (
                  <div key={t.key} className="flex items-start justify-between gap-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-800">{t.label}</p>
                      <p className="text-xs text-stone-500">{t.description}</p>
                    </div>
                    <Switch
                      data-testid={`staff-access-toggle-${editTarget?.id}-${t.key}`}
                      checked={v}
                      disabled={editSaving}
                      onCheckedChange={(val) => onEditToggle(t.key, val)}
                    />
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeEdit}
              disabled={editSaving}
              data-testid="staff-access-cancel-btn"
            >Cancel</Button>
            <Button
              onClick={onSaveEdit}
              disabled={editSaving || editLoading || !editGrants}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="staff-access-save-btn"
            >{editSaving ? 'Saving…' : 'Save changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TOGGLES = [
  { key: 'menu', label: 'Menu', description: 'Staff can hide/show items and categories on the customer menu. They cannot add, edit or delete menu entries.' },
  { key: 'call_reasons', label: 'Call Reasons', description: 'Staff can add, edit and remove call reasons.' },
  { key: 'performance', label: 'Performance', description: 'Staff can view the Performance dashboard, KPIs and call history.' },
  { key: 'qr_codes', label: 'QR Codes', description: 'Staff can view and download table QR codes.' },
  { key: 'inbox', label: 'Inbox', description: 'Staff can view broadcast / system messages in the Inbox tab.' },
  { key: 'settings', label: 'Settings', description: 'Staff can change restaurant settings (logo, theme, hours, timezone, etc.).' },
  // Paging-page granular controls.
  { key: 'paging_stats_today', label: 'Paging — Today stats', description: 'Staff can see today\'s totals & KPIs on the Paging tab.' },
  { key: 'paging_stats_week',  label: 'Paging — Week stats',  description: 'Staff can see this week\'s totals & KPIs.' },
  { key: 'paging_stats_month', label: 'Paging — Month stats', description: 'Staff can see this month\'s totals & KPIs.' },
  { key: 'paging_settings',    label: 'Paging — Show settings', description: 'Staff can open the Paging settings collapse (printout text, suffix defaults, reset logs).' },
  { key: 'paging_sales_log',   label: 'Paging — Sales log',  description: 'Staff can see the recent paging history table.' },
  { key: 'paging_refund',      label: 'Paging — Mark Refunded', description: 'Staff can mark a token as Refunded (a POS-reminder dialog appears before save).' },
];


function SettingsPanel({ restaurant, setRestaurant, subscription }) {
  const [form, setForm] = useState({ name: '', description: '', theme: 'warm', exit_confirm_enabled: true, exit_rating_enabled: false, customer_menu_enabled: true });
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const { user } = useAuth();
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [translating, setTranslating] = useState(false);

  const currentCurrency = restaurant?.currency || 'USD';

  // Auto-detect device timezone if host hasn't set one
  const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } })();
  const currentTz = restaurant?.timezone || browserTz;
  const tzOptions = useMemo(() => {
    const base = [
      browserTz, 'UTC',
      'Indian/Maldives', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo',
      'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
      'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'America/Sao_Paulo',
      'Australia/Sydney', 'Pacific/Auckland', 'Africa/Johannesburg',
    ];
    const seen = new Set();
    const out = [];
    [currentTz, browserTz, ...base].forEach(z => { if (z && !seen.has(z)) { seen.add(z); out.push(z); } });
    return out;
  }, [browserTz, currentTz]);
  const onTimezoneChange = async (tz) => {
    if (!tz || tz === currentTz) return;
    try {
      const { data } = await axios.put(`${API}/restaurant`, { timezone: tz }, { withCredentials: true });
      setRestaurant(data);
      toast.success(`Timezone set to ${tz}`);
    } catch { toast.error('Failed to update timezone'); }
  };

  // Build "Suggested" list: local currency (from owner mobile) + USD/EUR/GBP, deduped, in priority order
  const suggestedCodes = useMemo(() => {
    const local = currencyFromCountryCode(countryFromPhone(user?.mobile || user?.whatsapp || '').code);
    const list = [];
    if (local) list.push(local);
    ['USD', 'EUR', 'GBP'].forEach(c => { if (!list.includes(c)) list.push(c); });
    return list;
  }, [user?.mobile, user?.whatsapp]);

  const onCurrencyChange = async (code) => {
    if (!code || code === currentCurrency) return;
    setSavingCurrency(true);
    try {
      const { data } = await axios.put(`${API}/restaurant`, { currency: code }, { withCredentials: true });
      setRestaurant(data);
      toast.success(`Currency set to ${code}`);
    } catch {
      toast.error('Failed to update currency');
    }
    setSavingCurrency(false);
  };

  // Customer-view language settings. The default is what the customer sees
  // on first scan; the secondary (optional) appears as a toggle pill in the
  // customer view header so guests can flip languages on their phone.
  // Saving "none" for secondary clears the field so the toggle disappears.
  const onLanguageChange = async (kind, code) => {
    const payload = { [kind === 'default' ? 'default_language' : 'secondary_language']: code || null };
    try {
      const { data } = await axios.put(`${API}/restaurant`, payload, { withCredentials: true });
      setRestaurant(data);
    } catch {
      toast.error('Failed to update language');
    }
  };

  useEffect(() => {
    if (restaurant) {
      setForm({
        name: restaurant.name || '',
        description: restaurant.description || '',
        theme: restaurant.theme || 'warm',
        exit_confirm_enabled: restaurant.exit_confirm_enabled !== false,
        exit_rating_enabled: !!restaurant.exit_rating_enabled,
        customer_menu_enabled: restaurant.customer_menu_enabled !== false,
      });
    }
  }, [restaurant]);

  // Load logo preview
  useEffect(() => {
    if (!restaurant?.logo_path) { setLogoPreview(null); return; }
    let revoke;
    axios.get(`${API}/files/${restaurant.logo_path}`, { responseType: 'blob', withCredentials: true })
      .then(r => { const url = URL.createObjectURL(r.data); revoke = url; setLogoPreview(url); })
      .catch(() => setLogoPreview(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [restaurant?.logo_path]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: uploadData } = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Save logo_path to restaurant
      const { data } = await axios.put(`${API}/restaurant`, { logo_path: uploadData.path }, { withCredentials: true });
      setRestaurant(data);
      toast.success('Logo uploaded');
    } catch { toast.error('Logo upload failed'); }
    setLogoUploading(false);
  };

  const removeLogo = async () => {
    try {
      const { data } = await axios.put(`${API}/restaurant`, { logo_path: '' }, { withCredentials: true });
      setRestaurant(data);
      setLogoPreview(null);
      toast.success('Logo removed');
    } catch { toast.error('Failed to remove logo'); }
  };

  const themes = [
    { id: 'warm', name: 'Warm Terracotta', colors: ['#EA580C', '#FFEDD5', '#FAFAF9'] },
    { id: 'ocean', name: 'Ocean Breeze', colors: ['#0284C7', '#E0F2FE', '#F0F9FF'] },
    { id: 'forest', name: 'Forest Green', colors: ['#16A34A', '#DCFCE7', '#F0FDF4'] },
    { id: 'midnight', name: 'Midnight Blue', colors: ['#4F46E5', '#EEF2FF', '#F5F3FF'] },
    { id: 'rose', name: 'Rose Garden', colors: ['#E11D48', '#FFE4E6', '#FFF1F2'] },
    { id: 'amber', name: 'Golden Amber', colors: ['#D97706', '#FEF3C7', '#FFFBEB'] },
  ];

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/restaurant`, form, { withCredentials: true });
      setRestaurant(data);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Settings</h2>
        <p className="text-sm text-stone-500 mt-1">Customize your restaurant profile and theme</p>
      </div>

      <Card className="border-stone-200">
        <CardContent className="p-6 space-y-5">
          <div className="space-y-2">
            <Label>Restaurant Name</Label>
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Welcome to our restaurant..." />
          </div>
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Menu Currency</Label>
              {savingCurrency && <span className="text-xs text-stone-400">Saving…</span>}
            </div>
            <p className="text-xs text-stone-500">Used for menu prices on your dashboard and the customer view. Suggestions are based on your phone country.</p>
            <CurrencyPicker
              value={currentCurrency}
              onChange={onCurrencyChange}
              suggestedCodes={suggestedCodes}
              testid="menu-currency-picker"
            />
            <p className="text-xs text-stone-400">Currently selected: <span className="font-mono text-stone-600">{currentCurrency}</span></p>
          </div>
          <Separator />
          {/* Customer-view languages. Hosts pick a default plus any number
              of additional target languages to translate the menu / UI
              into. Customers see a language picker on their phone listing
              every translated language plus the default. Gated behind the
              `translation` capability — packages that don't include it
              show a locked notice instead. */}
          {(restaurant?.package_features?.translation === false) ? (
            <div className="space-y-2" data-testid="customer-languages-locked">
              <Label>Customer view languages</Label>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-semibold">Translation is not included in your plan.</p>
                <p className="text-xs mt-1 text-amber-800">Upgrade your subscription to translate the menu, call reasons, and customer-facing copy into your customers&apos; languages automatically.</p>
              </div>
            </div>
          ) : (
          <div className="space-y-2" data-testid="customer-languages-section">
            <Label>Customer view languages</Label>
            <p className="text-xs text-stone-500">Languages your customers see on the table & paging QR pages. Pick a default; click any chip below to add or remove a translation target. The customer's phone shows a language picker with every translated language plus your default.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500 font-bold">Default</Label>
                <select
                  data-testid="lang-default-select"
                  value={restaurant?.default_language || 'en'}
                  onChange={(e) => onLanguageChange('default', e.target.value)}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-stone-200 bg-white text-sm"
                >
                  {[...LANGUAGES].sort((a, b) => a.name.localeCompare(b.name)).map(l => (
                    <option key={l.code} value={l.code}>{l.name} ({l.native})</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500 font-bold">Translate into ({(restaurant?.translation_languages || []).length})</Label>
                <p className="text-[11px] text-stone-400 mt-1">Tap chips to toggle target languages.</p>
              </div>
            </div>
            {/* Chip grid — every supported language except the default. The
                selected chips are the ones the host will translate INTO.
                Saving each chip is debounced via a single PUT per click. */}
            <div className="flex flex-wrap gap-1.5 pt-1" data-testid="lang-multi-chips">
              {[...LANGUAGES]
                .filter(l => l.code !== (restaurant?.default_language || 'en'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(l => {
                  const selected = (restaurant?.translation_languages || []).includes(l.code);
                  return (
                    <button
                      key={l.code}
                      type="button"
                      data-testid={`lang-chip-${l.code}`}
                      onClick={() => {
                        const current = restaurant?.translation_languages || [];
                        const next = selected
                          ? current.filter(c => c !== l.code)
                          : [...current, l.code];
                        setRestaurant(r => ({ ...r, translation_languages: next }));
                        axios.put(`${API}/restaurant`, { translation_languages: next }, { withCredentials: true }).catch(() => {});
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                        selected
                          ? 'bg-[#0f5a78] text-white border-[#0c4a63] shadow-sm'
                          : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
                      }`}
                    >
                      <span className="font-mono uppercase mr-1.5">{l.code}</span>
                      {l.native}
                    </button>
                  );
                })}
            </div>
            {/* AI translation trigger. We only show this when at least one
                translation target language is configured (either the legacy
                secondary slot OR any chip selected). Idempotent: re-clicking
                only re-translates strings whose source text has changed
                since last run, so hitting it twice is essentially free. */}
            {((restaurant?.translation_languages || []).length > 0 || restaurant?.secondary_language) && (
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <Button
                  type="button"
                  size="sm"
                  data-testid="translate-now-btn"
                  disabled={translating}
                  onClick={async () => {
                    setTranslating(true);
                    try {
                      const { data } = await axios.post(`${API}/translations/refresh`, {}, { withCredentials: true });
                      const langs = (data.target_langs || []).map(c => c.toUpperCase()).join(', ');
                      toast.success(`Translated ${data.translated} new string${data.translated === 1 ? '' : 's'} · ${data.cached} reused · ${langs}`);
                      const { data: fresh } = await axios.get(`${API}/restaurant`, { withCredentials: true });
                      setRestaurant(fresh);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Translation failed — check your LLM key budget');
                    }
                    setTranslating(false);
                  }}
                  className="gap-1.5 bg-[#0f5a78] hover:bg-[#0e5170] text-white"
                >
                  <Languages size={14} />
                  {translating ? 'Translating…' : `Translate menu & UI now`}
                </Button>
                {restaurant.translations_updated_at && (
                  <span className="text-xs text-stone-400" data-testid="translate-last-stamp">
                    Last translated {new Date(restaurant.translations_updated_at).toLocaleString(undefined, { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                  </span>
                )}
              </div>
            )}
          </div>
          )}
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Performance Timezone</Label>
              <span className="text-xs text-stone-400" data-testid="current-timezone">{currentTz}</span>
            </div>
            <p className="text-xs text-stone-500">All Performance graphs and exit ratings are timestamped using this timezone. Defaults to your device's timezone.</p>
            <select
              data-testid="timezone-select"
              className="w-full h-10 px-3 rounded-md border border-stone-200 bg-white text-sm"
              value={currentTz}
              onChange={(e) => onTimezoneChange(e.target.value)}
            >
              {tzOptions.map(z => (
                <option key={z} value={z}>{z}{z === browserTz ? ' (your device)' : ''}</option>
              ))}
            </select>
          </div>
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Menu Auto-Collapse Delay</Label>
              <span className="text-xs text-stone-500" data-testid="auto-collapse-value">
                {(restaurant?.menu_auto_collapse_seconds ?? 8) === 0 ? 'Off' : `${restaurant?.menu_auto_collapse_seconds ?? 8}s`}
              </span>
            </div>
            <p className="text-xs text-stone-500">When a customer opens a category, any other expanded category that's scrolled out of view will close after this delay. Set to 0 to disable.</p>
            <input
              type="range"
              min={0}
              max={30}
              step={1}
              value={restaurant?.menu_auto_collapse_seconds ?? 8}
              onChange={async (e) => {
                const v = parseInt(e.target.value, 10);
                setRestaurant(prev => ({ ...(prev || {}), menu_auto_collapse_seconds: v }));
                try {
                  await axios.put(`${API}/restaurant`, { menu_auto_collapse_seconds: v }, { withCredentials: true });
                } catch { toast.error('Failed to save'); }
              }}
              className="w-full accent-orange-600"
              data-testid="auto-collapse-slider"
            />
          </div>
          <Separator />
          <div className="space-y-3">
            <Label>Restaurant Logo</Label>
            <p className="text-xs text-stone-500">Displayed on the customer-facing page when they scan the QR code.</p>
            <div className="flex items-center gap-4">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="w-20 h-20 rounded-xl object-cover border border-stone-200" />
              ) : (
                <div className="w-20 h-20 rounded-xl border-2 border-dashed border-stone-300 flex items-center justify-center bg-stone-50">
                  <UtensilsCrossed className="text-stone-300" size={24} />
                </div>
              )}
              <div className="space-y-2">
                <label className="cursor-pointer">
                  <Input data-testid="logo-upload-input" type="file" accept="image/*" onChange={handleLogoUpload} disabled={logoUploading} className="hidden" />
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm font-medium transition-colors cursor-pointer">
                    {logoUploading ? 'Uploading...' : logoPreview ? 'Change Logo' : 'Upload Logo'}
                  </span>
                </label>
                {logoPreview && (
                  <button onClick={removeLogo} className="text-xs text-red-500 hover:text-red-700 font-medium">Remove logo</button>
                )}
              </div>
            </div>
          </div>
          <Separator />
          <div className="space-y-3">
            <Label>Customer Exit Behavior</Label>
            <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50 border border-stone-200">
              <div className="flex-1">
                <p className="text-sm font-medium text-stone-800">Confirm before leaving</p>
                <p className="text-xs text-stone-500">Show "Leave this page?" popup when customer hits back</p>
              </div>
              <Switch data-testid="exit-confirm-toggle" checked={form.exit_confirm_enabled} onCheckedChange={(v) => setForm(p => ({ ...p, exit_confirm_enabled: v }))} />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50 border border-stone-200">
              <div className="flex-1">
                <p className="text-sm font-medium text-stone-800">Ask for rating on exit</p>
                <p className="text-xs text-stone-500">Show "Rate Your Experience" (Service, Speed, Food, Cleanliness) before leaving</p>
              </div>
              <Switch data-testid="exit-rating-toggle" checked={form.exit_rating_enabled} onCheckedChange={(v) => setForm(p => ({ ...p, exit_rating_enabled: v }))} />
            </div>
          </div>

          <Separator />
          <div className="space-y-3">
            <Label>Customer Menu Visibility</Label>
            <div className={`flex items-center justify-between p-3 rounded-lg border ${restaurant?.package_features?.customer_menu === false ? 'bg-stone-50/40 border-stone-100' : 'bg-stone-50 border-stone-200'}`}>
              <div className="flex-1">
                <p className={`text-sm font-medium ${restaurant?.package_features?.customer_menu === false ? 'text-stone-400' : 'text-stone-800'}`}>Show menu to customers</p>
                <p className="text-xs text-stone-500">
                  {restaurant?.package_features?.customer_menu === false
                    ? 'Not included on your current plan — contact admin to upgrade.'
                    : 'When off, the "View Menu" button is hidden on the customer page after they scan the QR code. Calls and chat still work.'}
                </p>
              </div>
              <Switch
                data-testid="customer-menu-toggle"
                checked={form.customer_menu_enabled && restaurant?.package_features?.customer_menu !== false}
                disabled={restaurant?.package_features?.customer_menu === false}
                onCheckedChange={(v) => setForm(p => ({ ...p, customer_menu_enabled: v }))}
              />
            </div>
          </div>

          <Separator />
          <CustomerEntertainmentToggles restaurant={restaurant} setRestaurant={setRestaurant} />

          <Separator />
          <CustomerBackgroundSettings restaurant={restaurant} setRestaurant={setRestaurant} />

          <Separator />
          <div className="space-y-3">
            <Label>Customer Page Theme</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {themes.map(t => (
                <button key={t.id} onClick={() => setForm(p => ({ ...p, theme: t.id }))}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    form.theme === t.id ? 'border-orange-600 shadow-md' : 'border-stone-200 hover:border-stone-300'
                  }`}>
                  <div className="flex gap-1 mb-2">
                    {t.colors.map((c, i) => <div key={i} className="w-5 h-5 rounded-full" style={{ backgroundColor: c }} />)}
                  </div>
                  <p className="text-xs font-medium text-stone-700">{t.name}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="pt-2">
            <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white">
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>

          <Separator />
          <VoiceAlertSettings restaurant={restaurant} setRestaurant={setRestaurant} />

          <Separator />
          <SubscriptionInfo subscription={subscription} />

          <Separator />
          <ResetPerformanceCard />
        </CardContent>
      </Card>
    </div>
  );
}

function CustomerBackgroundSettings({ restaurant, setRestaurant }) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Fetch a blob preview whenever the stored path changes so the host sees their
  // current background image right in the settings panel.
  useEffect(() => {
    let cancelled = false;
    const path = restaurant?.customer_bg_image_path;
    if (!path) { setPreviewUrl(null); return; }
    axios.get(`${API}/files/${path}`, { responseType: 'blob', withCredentials: true })
      .then(res => { if (!cancelled) setPreviewUrl(URL.createObjectURL(res.data)); })
      .catch(() => { if (!cancelled) setPreviewUrl(null); });
    return () => { cancelled = true; if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.customer_bg_image_path]);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('Image must be under 8 MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: up } = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const { data } = await axios.put(`${API}/restaurant`, { customer_bg_image_path: up.path }, { withCredentials: true });
      setRestaurant(data);
      toast.success('Customer background updated');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };
  const remove = async () => {
    try {
      const { data } = await axios.put(`${API}/restaurant`, { customer_bg_image_path: '' }, { withCredentials: true });
      setRestaurant(data);
      setPreviewUrl(null);
      toast.success('Background removed');
    } catch { toast.error('Failed to remove'); }
  };
  return (
    <div className="space-y-3">
      <Label>Customer Page Background</Label>
      <p className="text-xs text-stone-500 -mt-1">Upload a photo that fills the customer phone screen when they scan the QR code. Tip: pick a vertical image (e.g. food shot, brand artwork, restaurant ambience) at 1080×1920 for the sharpest fit.</p>
      <div className="flex items-start gap-3">
        <div className="w-24 h-40 rounded-xl border-2 border-dashed border-stone-300 bg-stone-50 overflow-hidden flex items-center justify-center shrink-0">
          {previewUrl ? (
            <img src={previewUrl} alt="Customer background preview" className="w-full h-full object-cover" data-testid="customer-bg-preview" />
          ) : (
            <ImageIcon size={28} className="text-stone-300" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <label className="cursor-pointer block">
            <Input type="file" accept="image/*" onChange={upload} className="hidden" disabled={uploading} />
            <span data-testid="upload-customer-bg-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 bg-white hover:bg-stone-50 text-stone-700 text-sm font-medium cursor-pointer transition-colors">
              <ImageIcon size={16} /> {uploading ? 'Uploading…' : (previewUrl ? 'Change background' : 'Upload background')}
            </span>
          </label>
          {previewUrl && (
            <Button type="button" variant="ghost" size="sm" data-testid="remove-customer-bg-btn" onClick={remove} className="text-xs text-red-600 hover:text-red-700">
              Remove background
            </Button>
          )}
          <p className="text-[11px] text-stone-400">JPG, PNG or WebP. Max 8 MB.</p>
        </div>
      </div>
    </div>
  );
}

function CustomerEntertainmentToggles({ restaurant, setRestaurant }) {
  // The package_entertainment object on the restaurant doc tells us which
  // features are unlocked on the host's current package. The host then opts
  // in to each unlocked feature for their customer page.
  const allow = restaurant?.package_entertainment || { games: false, quotes: false, poems: false, jokes: false };
  const persist = async (patch) => {
    try {
      const { data } = await axios.put(`${API}/restaurant`, patch, { withCredentials: true });
      setRestaurant(data);
    } catch { toast.error('Failed to update entertainment'); }
  };
  const rows = [
    { key: 'games',  field: 'cust_games_enabled',  label: 'Mini Games',   desc: 'Memory Match, Quiz, Math, Spelling' },
    { key: 'quotes', field: 'cust_quotes_enabled', label: 'Love Quotes',  desc: 'Curated quotes (AI integration coming)' },
    { key: 'poems',  field: 'cust_poems_enabled',  label: 'Poems',        desc: 'Themed poems (AI integration coming)' },
    { key: 'jokes',  field: 'cust_jokes_enabled',  label: 'Jokes',        desc: 'Light-hearted jokes (AI integration coming)' },
  ];
  return (
    <div className="space-y-3">
      <Label>Customer Entertainment</Label>
      <p className="text-xs text-stone-500 -mt-1">Show fun shortcut buttons under the chat icon on the customer page after they scan the QR.</p>
      <div className="space-y-2">
        {rows.map(r => {
          const purchased = !!allow[r.key];
          const enabled = !!restaurant?.[r.field];
          return (
            <div key={r.key} className={`flex items-center justify-between p-3 rounded-lg border ${purchased ? 'bg-stone-50 border-stone-200' : 'bg-stone-50/40 border-stone-100'}`}>
              <div className="flex-1 min-w-0 pr-3">
                <p className={`text-sm font-medium ${purchased ? 'text-stone-800' : 'text-stone-400'}`}>{r.label}</p>
                <p className="text-xs text-stone-500">
                  {purchased ? r.desc : 'Not included on your current plan — contact admin to upgrade.'}
                </p>
              </div>
              <Switch
                data-testid={`cust-${r.key}-toggle`}
                checked={enabled && purchased}
                disabled={!purchased}
                onCheckedChange={(v) => persist({ [r.field]: !!v })}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VoiceAlertSettings({ restaurant, setRestaurant }) {
  const [voices, setVoices] = useState(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return [];
    return window.speechSynthesis.getVoices() || [];
  });
  // Voices populate asynchronously in some browsers; subscribe to the event.
  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const refresh = () => setVoices(window.speechSynthesis.getVoices() || []);
    refresh();
    window.speechSynthesis.onvoiceschanged = refresh;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);
  const enabled = !!restaurant?.tts_enabled;
  const voice = restaurant?.tts_voice || '';
  const repeatCount = restaurant?.tts_repeat_count || 3;
  const interval = restaurant?.tts_repeat_interval || 30;
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const allowed = restaurant?.package_features?.voice_alerts !== false; // grandfathered = true
  const persist = async (patch) => {
    try {
      const { data } = await axios.put(`${API}/restaurant`, patch, { withCredentials: true });
      setRestaurant(data);
    } catch { toast.error('Failed to update voice alerts'); }
  };
  const testSpeak = (label = 'one') => {
    if (!supported) return;
    const u = new SpeechSynthesisUtterance(`Table ${label}`);
    const v = voices.find(x => x.voiceURI === voice) || voices.find(x => x.name === voice);
    if (v) u.voice = v;
    u.rate = 0.95;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Label className="text-sm font-semibold">Voice Alerts</Label>
          <p className="text-xs text-stone-500 mt-0.5">
            {!allowed
              ? 'Not included on your current plan — contact admin to upgrade.'
              : 'Read out the table number aloud when a call comes in. Uses your browser\'s built-in voices — no extra cost.'}
          </p>
          {!supported && allowed && <p className="text-xs text-red-500 mt-1">Your browser does not support voice alerts.</p>}
        </div>
        <Switch
          data-testid="tts-enabled-switch"
          checked={enabled && allowed}
          onCheckedChange={(v) => persist({ tts_enabled: !!v })}
          disabled={!supported || !allowed}
        />
      </div>
      {enabled && supported && allowed && (
        <div className="space-y-3 pl-1">
          <div className="space-y-1.5">
            <Label className="text-xs text-stone-600">Voice ({voices.length} available)</Label>
            <select
              data-testid="tts-voice-select"
              className="w-full text-sm rounded-md border border-stone-200 bg-white px-2 py-1.5"
              value={voice}
              onChange={(e) => persist({ tts_voice: e.target.value })}
            >
              <option value="">Browser default</option>
              {voices.map(v => (
                <option key={v.voiceURI} value={v.voiceURI}>
                  {v.name} {v.lang ? `· ${v.lang}` : ''} {v.default ? '(default)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-stone-600">Repeat count</Label>
              <select
                data-testid="tts-repeat-count"
                className="w-full text-sm rounded-md border border-stone-200 bg-white px-2 py-1.5"
                value={repeatCount}
                onChange={(e) => persist({ tts_repeat_count: parseInt(e.target.value, 10) })}
              >
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} time{n > 1 ? 's' : ''}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-stone-600">Repeat every</Label>
              <select
                data-testid="tts-repeat-interval"
                className="w-full text-sm rounded-md border border-stone-200 bg-white px-2 py-1.5"
                value={interval}
                onChange={(e) => persist({ tts_repeat_interval: parseInt(e.target.value, 10) })}
              >
                {[15, 30, 45, 60, 90, 120].map(s => <option key={s} value={s}>{s} seconds</option>)}
              </select>
            </div>
          </div>
          <Button
            type="button"
            data-testid="tts-test-btn"
            variant="outline"
            size="sm"
            onClick={() => testSpeak('one')}
            className="text-xs"
          >
            Test voice — say "Table one"
          </Button>
        </div>
      )}
    </div>
  );
}

function SubscriptionInfo({ subscription }) {
  const status = subscription?.status || 'trial';
  const trialDays = subscription?.trial_days_remaining || 0;
  const trialExpired = !!subscription?.trial_expired;

  let badgeCls = 'bg-stone-100 text-stone-700 border-stone-200';
  let badgeText = 'Loading…';
  let helpText = '';

  if (status === 'active') {
    badgeCls = 'bg-emerald-100 text-emerald-700 border-emerald-200';
    badgeText = 'Active';
    helpText = 'Your subscription is active. Thanks for using CallButton!';
  } else if (status === 'trial' && !trialExpired) {
    badgeCls = 'bg-amber-100 text-amber-700 border-amber-200';
    badgeText = `Trial — ${trialDays} day${trialDays === 1 ? '' : 's'} left`;
    helpText = trialDays > 0
      ? `Your free trial ends in ${trialDays} day${trialDays === 1 ? '' : 's'}. Contact your administrator to activate the subscription.`
      : 'Your trial ends today. Contact your administrator to activate the subscription.';
  } else if (status === 'trial' && trialExpired) {
    badgeCls = 'bg-red-100 text-red-700 border-red-200';
    badgeText = 'Trial expired';
    helpText = 'Your free trial has expired. Contact your administrator to activate the subscription.';
  } else if (status === 'suspended' || status === 'inactive' || status === 'cancelled') {
    badgeCls = 'bg-red-100 text-red-700 border-red-200';
    badgeText = status.charAt(0).toUpperCase() + status.slice(1);
    helpText = 'Your subscription is currently inactive. Please contact your administrator.';
  } else {
    badgeText = status;
  }

  return (
    <div data-testid="subscription-info">
      <h3 className="text-sm font-semibold text-stone-700 mb-2">Subscription</h3>
      <Badge data-testid="subscription-badge" className={badgeCls}>{badgeText}</Badge>
      {helpText && <p className="text-xs text-stone-500 mt-2">{helpText}</p>}
    </div>
  );
}

function ResetPerformanceCard() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/performance/reset`, {}, { withCredentials: true });
      toast.success(`Performance reset — removed ${data.calls_deleted} call${data.calls_deleted === 1 ? '' : 's'}, ${data.chats_deleted} chat${data.chats_deleted === 1 ? '' : 's'}, ${data.ratings_deleted} rating${data.ratings_deleted === 1 ? '' : 's'}.`);
      setOpen(false);
    } catch {
      toast.error('Failed to reset performance data');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <h3 className="text-sm font-semibold text-stone-700 mb-2">Reset Performance Data</h3>
      <p className="text-xs text-stone-500 mb-3">Removes all calls, chat messages and customer ratings for this restaurant. Tables, menu and settings are kept. Use this once before going live so test data doesn't pollute your KPIs.</p>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger asChild>
          <Button data-testid="reset-performance-btn" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
            <Trash2 size={14} className="mr-2" /> Reset Performance Data
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset all performance data?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <strong>all calls, all chat messages and all customer ratings</strong> for your restaurant. Your tables, menu items and settings will not be affected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-performance-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-performance-confirm"
              onClick={doReset}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {busy ? 'Resetting…' : 'Yes, reset everything'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// Small helper card that auto-rotates motivational / KPI push messages
// based on the host's real Today / Week / Month paging stats. Refreshes the
// message every 7s so the operator gets multiple data points throughout
// a shift. Falls back to a welcome line if there is no data yet.
function KpiPushCard({ stats, className = '' }) {
  // Fetch a 7-day daily breakdown so we can render a tiny bar chart. We
  // refresh every 2 minutes — paging stats don't change that fast and the
  // host already has the page open while running counter; pulling every
  // 30s would needlessly hammer Mongo.
  const [daily, setDaily] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await axios.get(`${API}/pagings/daily?days=7`, { withCredentials: true });
        if (!cancelled) setDaily(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    };
    pull();
    const t = setInterval(pull, 120000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Pre-compute the headline KPI numbers shared across the card. All values
  // are zero-safe so an empty restaurant still renders a friendly state.
  const t = stats.today || {};
  const w = stats.week  || {};
  const m = stats.month || {};
  const todayTotal  = t.total ?? 0;
  const todayServed = t.by_status?.served ?? 0;
  const todayActive = (t.by_status?.active ?? 0) + (t.by_status?.ready ?? 0);
  const weekTotal   = w.total ?? 0;
  const monthTotal  = m.total ?? 0;
  const avgServeSec = t.avg_serve_seconds || 0;
  const avgServeMin = avgServeSec ? Math.round(avgServeSec / 60) : null;
  const avgReadySec = t.avg_ready_seconds || 0;
  const avgReadyMin = avgReadySec ? Math.round(avgReadySec / 60) : null;
  const last6 = Math.max(0, weekTotal - todayTotal);
  const avgDaily = last6 > 0 ? last6 / 6 : 0;
  // Only compute % when today has activity — showing "-100%" on an empty
  // morning is technically true but demotivating; we want the staff to see
  // a fresh-day prompt instead.
  const pctVsAvg = (avgDaily > 0 && todayTotal > 0)
    ? Math.round(((todayTotal - avgDaily) / avgDaily) * 100)
    : null;
  const isFreshDay = todayTotal === 0 && avgDaily > 0;

  // Bar-chart geometry. We map each day's total → a height % of the tallest
  // bar in the series (min 6% so even zero-days remain perceptible).
  const maxBar = Math.max(1, ...daily.map(d => d.total || 0));
  const dayLabel = (iso) => {
    try {
      const d = new Date(iso + 'T00:00:00');
      return ['S','M','T','W','T','F','S'][d.getDay()];
    } catch { return ''; }
  };
  const isToday = (iso) => {
    const now = new Date(); const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    return iso === today;
  };

  // Rotating motivational tagline — surfaces the most impactful insight
  // first then cycles through. Picked dynamically so we never show "+0%"
  // style nonsense.
  const messages = useMemo(() => {
    const lines = [];
    if (isFreshDay) {
      lines.push(`Fresh day! Your last 6-day avg is ${avgDaily.toFixed(1)} — let's beat it!`);
    } else if (pctVsAvg != null) {
      if (pctVsAvg >= 10) lines.push(`+${pctVsAvg}% vs 7-day avg — keep crushing it!`);
      else if (pctVsAvg <= -10) lines.push(`${Math.abs(pctVsAvg)}% below avg — push for ${Math.max(1, Math.ceil(avgDaily - todayTotal))} more!`);
      else lines.push(`Right on your 7-day pace.`);
    } else if (todayTotal > 0) {
      lines.push(`${todayTotal} order${todayTotal === 1 ? '' : 's'} today — great start!`);
    }
    if (avgReadyMin != null && avgReadyMin > 0) {
      if (avgReadyMin <= 3) lines.push(`⚡ Lightning prep: ~${avgReadyMin} min avg ready time!`);
      else if (avgReadyMin <= 7) lines.push(`Solid pace: ~${avgReadyMin} min avg prep time.`);
      else lines.push(`~${avgReadyMin} min avg prep — speed up the line!`);
    }
    if (monthTotal > 0) {
      const next = Math.ceil((monthTotal + 1) / 100) * 100;
      const delta = next - monthTotal;
      if (delta > 0 && delta <= 25) lines.push(`🎯 ${delta} to go to hit ${next} this month!`);
    }
    if (todayServed > 0) lines.push(`${todayServed} happy customer${todayServed === 1 ? '' : 's'} served today.`);
    return lines.length ? lines : ['Generate a token to start tracking sales today.'];
  }, [pctVsAvg, isFreshDay, todayTotal, avgReadyMin, monthTotal, todayServed, avgDaily]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (messages.length <= 1) return;
    const i = setInterval(() => setIdx(p => (p + 1) % messages.length), 6000);
    return () => clearInterval(i);
  }, [messages.length]);
  // Keep index in range when the messages list shrinks.
  useEffect(() => { if (idx >= messages.length) setIdx(0); }, [messages.length, idx]);

  // Colour palette — soft pastel tiles on a light gradient so the card
  // doesn't feel like another dark CTA. Each tile carries one number; the
  // 35%-larger top number sits in the leftmost emerald tile.
  const tileBase = "rounded-xl px-2 py-1.5 flex flex-col items-center justify-center text-center min-w-0";
  // % tile content. We deliberately avoid showing "-100%" on a fresh day —
  // it's a motivation killer. Instead we show today's target (avgDaily) in a
  // friendly amber "Let's go!" tile to nudge staff to start the day strong.
  const pctLabel  = isFreshDay         ? "Today's goal"
                  : pctVsAvg == null   ? 'vs 7-day avg'
                  : 'vs 7-day avg';
  const pctValue  = isFreshDay         ? `${Math.ceil(avgDaily)}`
                  : pctVsAvg == null   ? '—'
                  : (pctVsAvg > 0 ? `+${pctVsAvg}%` : `${pctVsAvg}%`);
  const pctSub    = isFreshDay         ? "Let's go! 🚀"
                  : avgDaily > 0       ? `avg ${avgDaily.toFixed(1)}/day`
                  : 'no history yet';
  const pctTone   = isFreshDay
                  ? 'bg-amber-50 text-amber-800 border-amber-200'
                  : pctVsAvg == null
                  ? 'bg-sky-50 text-sky-700 border-sky-100'
                  : pctVsAvg >= 0
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                  : 'bg-rose-50 text-rose-700 border-rose-100';

  return (
    <div
      data-testid="paging-kpi-card"
      className={`bg-gradient-to-br from-amber-50 via-white to-emerald-50 text-stone-900 rounded-2xl border-2 border-stone-200 shadow-lg p-3 md:p-4 flex flex-col gap-2 ${className}`}
    >
      {/* Top row — 3 tiles. "Today" is the dominant tile; fonts are ~35%
          bigger than the previous compact card (5xl was the old big number;
          we now bump to 6xl/7xl on desktop). */}
      <div className="grid grid-cols-3 gap-2 flex-1 min-h-0">
        <div className={`${tileBase} bg-emerald-100/60 border border-emerald-200 text-emerald-800`}>
          <p className="text-[10px] md:text-xs uppercase tracking-wider font-bold text-emerald-700/80">Today</p>
          <p className="text-4xl md:text-6xl lg:text-7xl font-extrabold tabular-nums leading-none">{todayTotal}</p>
          <p className="text-[10px] md:text-xs text-emerald-700/80 mt-1 leading-tight">{todayServed} served · {todayActive} live</p>
        </div>
        <div className={`${tileBase} border ${pctTone}`}>
          <p className="text-[10px] md:text-xs uppercase tracking-wider font-bold opacity-80">{pctLabel}</p>
          <p className="text-3xl md:text-5xl lg:text-6xl font-extrabold tabular-nums leading-none">{pctValue}</p>
          <p className="text-[10px] md:text-xs opacity-80 mt-1 leading-tight">{pctSub}</p>
        </div>
        <div className={`${tileBase} bg-sky-50 border border-sky-100 text-sky-800`}>
          <p className="text-[10px] md:text-xs uppercase tracking-wider font-bold text-sky-700/80">Avg ready</p>
          <p className="text-3xl md:text-5xl lg:text-6xl font-extrabold tabular-nums leading-none">{avgReadyMin != null ? avgReadyMin : '—'}<span className="text-base md:text-xl font-bold ml-0.5">min</span></p>
          <p className="text-[10px] md:text-xs text-sky-700/80 mt-1 leading-tight">prep · {monthTotal} this mo</p>
        </div>
      </div>

      {/* Bottom row — 7-day mini bar chart + rotating motivational caption.
          Chart bars use a soft emerald gradient; today's bar is highlighted
          amber so the host can spot momentum at a glance. */}
      <div className="grid grid-cols-5 gap-2 items-end">
        <div className="col-span-2 flex items-end gap-1 h-12 md:h-16" data-testid="paging-kpi-chart">
          {(daily.length ? daily : Array.from({ length: 7 }, (_, i) => ({ date: `d${i}`, total: 0 }))).map(d => {
            const h = Math.max(6, Math.round((d.total / maxBar) * 100));
            const today = isToday(d.date);
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5 min-w-0">
                <div className="w-full flex-1 flex items-end">
                  <div
                    className={`w-full rounded-t-sm transition-all ${today ? 'bg-amber-500' : 'bg-emerald-400/70'}`}
                    style={{ height: `${h}%` }}
                    title={`${d.date}: ${d.total}`}
                  />
                </div>
                <span className={`text-[9px] md:text-[10px] font-bold leading-none ${today ? 'text-amber-700' : 'text-stone-400'}`}>{dayLabel(d.date)}</span>
              </div>
            );
          })}
        </div>
        <div className="col-span-3 flex flex-col justify-end min-w-0">
          <p className="text-[10px] md:text-xs uppercase tracking-wider font-bold text-stone-400">Insight</p>
          <p className="text-sm md:text-base font-semibold text-stone-800 leading-snug line-clamp-2" title={messages[idx] || ''}>
            {messages[idx] || messages[0]}
          </p>
          {messages.length > 1 && (
            <div className="mt-1 flex items-center gap-1">
              {messages.map((_, i) => (
                <span
                  key={i}
                  className={`block rounded-full transition-all ${i === idx ? 'w-3 h-1 bg-stone-600' : 'w-1 h-1 bg-stone-300'}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PagingPanel — Host token-pager workflow.
//
// Layout (all in one tab):
//   ┌────────────────────────────────────────────────────────────────────┐
//   │ Title + brief description                                          │
//   ├────────────────────────────────┬──────────────────────────────────-┤
//   │ Generate form (token, type)    │ Daily / Weekly / Monthly stats    │
//   ├────────────────────────────────┴──────────────────────────────────-┤
//   │ Active tokens (mark Served / Cancelled / Refunded)                 │
//   ├────────────────────────────────────────────────────────────────────┤
//   │ Recent log (last 50)                                               │
//   └────────────────────────────────────────────────────────────────────┘
//
// On "Generate & Print":
//   - POST /api/pagings -> get id
//   - open a new window with an 80mm thermal-receipt-sized print layout
//     containing the printed restaurant name + QR (encoding /p/{rid}/{id})
//     with restaurant logo embedded centred + token + message + thanks +
//     date/time. Auto-calls window.print() so the browser print dialog
//     appears immediately.
//
// Token suffix: defaults to D (dine-in) / T (take-away), each editable in
// the form's "Settings" collapse so the host can save once and forget.
// ============================================================================

function PagingPanel({ restaurant, setRestaurant, frontendUrl, isStaff, staffAccess }) {
  // Per-section visibility helper. Host sees everything; staff is gated by
  // the new paging_* keys in Access Control.
  const canSee = (key) => !isStaff || !!staffAccess?.[key];
  const [form, setForm] = useState({ token: '', order_type: 'takeaway', suffix_override: '' });
  const [submitting, setSubmitting] = useState(false);
  const [activeTokens, setActiveTokens] = useState([]);
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState({ today: null, week: null, month: null });
  const [showSettings, setShowSettings] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Sales-log filter. Default = today. "custom" reveals from/to date inputs.
  // We compute the actual ISO range from this state on every reload and pass
  // it to GET /api/pagings?start_date=...&end_date=... so the server does the
  // heavy filtering for us (no client-side slicing of huge result sets).
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const [logFilter, setLogFilter] = useState('today'); // today | week | month | custom
  const [customRange, setCustomRange] = useState({ from: todayStr(), to: todayStr() });

  // Build the ISO start/end for the current filter selection. Week = last 7
  // rolling days; Month = last 30 rolling days. We compute these in the
  // browser's local timezone, then push them out as full UTC ISO strings.
  const computeRange = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (logFilter === 'week')  start.setDate(start.getDate() - 6);
    if (logFilter === 'month') start.setDate(start.getDate() - 29);
    if (logFilter === 'custom') {
      const f = customRange.from || todayStr();
      const t = customRange.to   || todayStr();
      const [fy, fm, fd] = f.split('-').map(Number);
      const [ty, tm, td] = t.split('-').map(Number);
      start.setFullYear(fy, fm - 1, fd); start.setHours(0,0,0,0);
      end.setFullYear(ty, tm - 1, td);   end.setHours(23,59,59,999);
    }
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const reload = async () => {
    try {
      const { start, end } = computeRange();
      const [activeRes, allRes] = await Promise.all([
        axios.get(`${API}/pagings?status=open&limit=200`, { withCredentials: true }),
        axios.get(`${API}/pagings?limit=1000&start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(end)}`, { withCredentials: true }),
      ]);
      setActiveTokens(activeRes.data);
      setRecent(allRes.data);
    } catch { /* ignore */ }
  };
  const reloadStats = async () => {
    try {
      const [today, week, month] = await Promise.all([
        axios.get(`${API}/pagings/stats?period=today`, { withCredentials: true }),
        axios.get(`${API}/pagings/stats?period=week`,  { withCredentials: true }),
        axios.get(`${API}/pagings/stats?period=month`, { withCredentials: true }),
      ]);
      setStats({ today: today.data, week: week.data, month: month.data });
    } catch { /* ignore */ }
  };
  useEffect(() => { reload(); reloadStats(); /* eslint-disable-next-line */ }, []);
  // Re-fetch the sales-log whenever the filter changes. Active tokens and
  // stats are unaffected by the filter, but reload() refreshes both anyway —
  // cheap enough and keeps state simple.
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [logFilter, customRange.from, customRange.to]);

  const savePagingSettings = async (patch) => {
    try {
      const { data } = await axios.put(`${API}/restaurant`, patch, { withCredentials: true });
      setRestaurant(data);
      toast.success('Paging settings saved');
    } catch { toast.error('Save failed'); }
  };

  // Open a fresh print window sized for an 80mm thermal receipt.
  const openPrintWindow = (paging) => {
    const url = `${frontendUrl || window.location.origin}/p/${restaurant.id}/${paging.id}`;
    const restName = (restaurant.paging_title_text || restaurant.name || '').replace(/</g, '&lt;');
    const message  = (restaurant.paging_message_text || 'Please scan the QR code and wait comfortably. We will alert you on your phone once your order is ready.').replace(/</g, '&lt;');
    const thanks   = (restaurant.paging_thanks_text || 'Thank you').replace(/</g, '&lt;');
    const logoUrl  = restaurant.logo_path ? `${API}/files/${restaurant.logo_path}` : '';
    const now = new Date(paging.created_at || Date.now());
    const dateStr = now.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });

    // Convert the host's logo to a pure-black/white bitmap so it prints
    // cleanly on a 1-bit thermal printer. Grey gradients get rendered as
    // muddy dithered patterns on thermal heads — by thresholding every
    // pixel client-side we hand the printer exactly the dot pattern it
    // expects. Transparent pixels become white (background).
    const thermalizeLogo = (src) => new Promise((resolve) => {
      if (!src) { resolve(''); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          // Render at a fixed 256px square. Thermal printers at 203 DPI
          // map ~18mm to ~144 dots; 256px gives plenty of headroom for
          // sharp scaling without producing intermediate greys.
          const N = 256;
          const cv = document.createElement('canvas');
          cv.width = N; cv.height = N;
          const ctx = cv.getContext('2d');
          // White background underneath (handles transparent logos).
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, N, N);
          // Fit-contain the logo while preserving aspect ratio.
          const ratio = img.width / img.height || 1;
          let dw = N, dh = N;
          if (ratio > 1) { dh = Math.round(N / ratio); }
          else if (ratio < 1) { dw = Math.round(N * ratio); }
          const dx = Math.round((N - dw) / 2);
          const dy = Math.round((N - dh) / 2);
          ctx.drawImage(img, dx, dy, dw, dh);
          // Threshold every pixel: luminance >= 160 → white, otherwise
          // black. 160 (not 128) biases slightly toward white which is
          // what most thermal printers handle most cleanly for logos.
          const data = ctx.getImageData(0, 0, N, N);
          const px = data.data;
          for (let i = 0; i < px.length; i += 4) {
            // sRGB luma; alpha < 32 treated as background.
            const a = px[i + 3];
            const lum = a < 32 ? 255 : (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]);
            const v = lum >= 160 ? 255 : 0;
            px[i] = px[i + 1] = px[i + 2] = v;
            px[i + 3] = 255;
          }
          ctx.putImageData(data, 0, 0);
          resolve(cv.toDataURL('image/png'));
        } catch {
          // CORS or decode failure — fall back to the original URL; print
          // will still work, it just may not look great on thermal paper.
          resolve(src);
        }
      };
      img.onerror = () => resolve(src);
      img.src = src;
    });

    // Build the QR + logo overlay as inline SVG (qrcode-generator alternative
    // would also work; we go with qrcode.react via toDataURL pattern below).
    // We're using the `qrcode` npm pkg (already in deps) to get a data URL.
    Promise.all([
      import('qrcode').then(({ default: QR }) => new Promise((resolve, reject) => {
        QR.toDataURL(url, { errorCorrectionLevel: 'H', margin: 1, width: 600 }, (err, dataUrl) => err ? reject(err) : resolve(dataUrl));
      })),
      thermalizeLogo(logoUrl),
    ]).then(([dataUrl, logoDataUrl]) => {
        const html = `<!doctype html>
<html><head><meta charset="utf-8"/><title>Paging — ${restName}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; font-family: Helvetica, Arial, sans-serif; }
  .slip { width: 76mm; padding: 4mm 2mm; box-sizing: border-box; color: #000; }
  /* All text in black so a single-colour thermal printer prints every
     element at maximum contrast. Previously the title / token number /
     message rendered in blue, which on a monochrome head dithers into
     a faint grey strip. */
  .restName { font-size: 20pt; font-weight: 800; color: #000; line-height: 1.1; margin-bottom: 4mm; word-break: break-word; text-align: center; }
  .qrWrap { position: relative; width: 64mm; height: 64mm; margin: 0 auto; }
  .qrImg  { width: 100%; height: 100%; display: block; }
  .qrLogo { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 18mm; height: 18mm; background: #fff; border-radius: 2mm; display: flex; align-items: center; justify-content: center; padding: 1mm; box-sizing: border-box; }
  .qrLogo img { max-width: 100%; max-height: 100%; object-fit: contain; image-rendering: pixelated; }
  .token { font-size: 18pt; font-weight: 800; margin-top: 5mm; text-align: center; color: #000; }
  .token .num { color: #000; }
  .message { font-size: 12pt; color: #000; line-height: 1.25; margin-top: 4mm; text-align: center; }
  .datetime { font-size: 10pt; color: #000; margin-top: 4mm; text-align: center; font-weight: 600; }
  .thanks { font-size: 14pt; color: #000; font-weight: 800; margin-top: 3mm; text-align: center; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
  <div class="slip">
    <div class="restName">${restName}</div>
    <div class="qrWrap">
      <img class="qrImg" src="${dataUrl}" alt="QR" />
      ${logoDataUrl ? `<div class="qrLogo"><img src="${logoDataUrl}"/></div>` : ''}
    </div>
    <div class="token">TOKEN : <span class="num">${(paging.display_token || paging.token || '').replace(/</g, '&lt;')}</span></div>
    <div class="message">${message}</div>
    <div class="datetime">${dateStr} | ${timeStr}Hrs</div>
    <div class="thanks">${thanks}</div>
  </div>
  <script>
    // Wait one tick for the logo image to load (if any) before triggering print.
    function go() { window.focus(); window.print(); }
    const imgs = Array.from(document.images).filter(i => !i.complete);
    if (imgs.length === 0) { setTimeout(go, 200); }
    else {
      let left = imgs.length;
      imgs.forEach(i => { i.addEventListener('load', () => { if (--left === 0) setTimeout(go, 150); });
                          i.addEventListener('error', () => { if (--left === 0) setTimeout(go, 150); }); });
      // Safety fallback
      setTimeout(go, 1500);
    }
  </script>
</body></html>`;
        const w = window.open('', '_blank', 'width=380,height=820');
        if (!w) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
        w.document.open(); w.document.write(html); w.document.close();
    }).catch(() => {
      toast.error('QR generation failed');
    });
  };

  const generate = async () => {
    const tok = (form.token || '').trim();
    if (!tok) { toast.error('Enter a token number'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/pagings`, {
        token: tok,
        order_type: form.order_type,
        suffix_override: form.suffix_override || undefined,
      }, { withCredentials: true });
      toast.success(`Token ${data.display_token} generated`);
      setForm(f => ({ ...f, token: '', suffix_override: '' }));
      openPrintWindow(data);
      reload(); reloadStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Generate failed'); }
    setSubmitting(false);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/pagings/${id}`, { status }, { withCredentials: true });
      reload(); reloadStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  // Refund flow — staff (or host) press the Refund button → confirmation
  // dialog reminds them this is a RECORD-only action (the actual money
  // refund happens at the POS terminal). On confirm we PUT status=refunded.
  const [refundConfirm, setRefundConfirm] = useState(null); // { token } | null
  const confirmRefund = async () => {
    if (!refundConfirm) return;
    await setStatus(refundConfirm.id, 'refunded');
    setRefundConfirm(null);
  };

  const resetLogs = async () => {
    setResetting(true);
    try {
      const { data } = await axios.delete(`${API}/pagings/reset`, { withCredentials: true });
      toast.success(`Deleted ${data.deleted} paging records`);
      setResetOpen(false);
      reload(); reloadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
    setResetting(false);
  };

  const formatDur = (sec) => {
    if (!sec || sec <= 0) return '—';
    const m = Math.floor(sec / 60), s = sec % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  // CSV export of the currently-filtered sales log. We render the same
  // columns the on-screen table shows + raw timestamps so accountants can
  // re-sort in Excel. Generated client-side from the `recent` state so it
  // always matches what the host sees. Filename includes the active range.
  const csvCell = (val) => {
    const s = String(val ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const exportSalesLogCsv = () => {
    if (!recent || recent.length === 0) {
      toast.error('Nothing to export in the selected range');
      return;
    }
    const header = ['Token', 'Type', 'Status', 'Created', 'Ready at', 'Served at', 'Ready in (sec)', 'Served in (sec)'];
    const rows = recent.map(r => [
      r.display_token || r.token || '',
      r.order_type === 'dinein' ? 'Dine-in' : 'Take-away',
      r.status === 'no_response' ? 'no response' : (r.status || ''),
      r.created_at || '',
      r.ready_at || '',
      r.served_at || '',
      r.ready_duration_seconds ?? '',
      r.served_duration_seconds ?? '',
    ]);
    const csv = [header, ...rows].map(line => line.map(csvCell).join(',')).join('\r\n');
    // Build a friendly filename out of the current filter selection.
    const { start, end } = computeRange();
    const tag = logFilter === 'custom'
      ? `${start.slice(0,10)}_to_${end.slice(0,10)}`
      : logFilter;
    // BOM so Excel detects UTF-8 properly.
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `paging-sales-log_${tag}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${recent.length} record${recent.length === 1 ? '' : 's'}`);
  };

  const StatBlock = ({ title, d }) => (
    <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid={`paging-stat-${title.toLowerCase()}`}>
      <p className="text-xs uppercase tracking-wider font-bold text-stone-500">{title}</p>
      <p className="text-4xl font-extrabold text-stone-900 mt-1 tabular-nums">{d?.total ?? 0}</p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-3 text-sm text-stone-700">
        <span>Dine-in</span>     <span className="text-right font-bold tabular-nums">{d?.by_type?.dinein ?? 0}</span>
        <span>Take-away</span>   <span className="text-right font-bold tabular-nums">{d?.by_type?.takeaway ?? 0}</span>
        <span className="text-emerald-700">Served</span>    <span className="text-right font-bold tabular-nums text-emerald-700">{d?.by_status?.served ?? 0}</span>
        <span className="text-amber-700">Active</span>      <span className="text-right font-bold tabular-nums text-amber-700">{d?.by_status?.active ?? 0}</span>
        <span className="text-blue-700">Ready</span>        <span className="text-right font-bold tabular-nums text-blue-700">{d?.by_status?.ready ?? 0}</span>
        <span className="text-stone-600">No response</span><span className="text-right font-bold tabular-nums text-stone-700">{d?.by_status?.no_response ?? 0}</span>
        <span className="text-red-600">Cancelled</span>     <span className="text-right font-bold tabular-nums text-red-600">{d?.by_status?.cancelled ?? 0}</span>
        <span className="text-red-600">Refunded</span>      <span className="text-right font-bold tabular-nums text-red-600">{d?.by_status?.refunded ?? 0}</span>
        <span className="text-stone-500 pt-1.5 border-t border-stone-100 mt-1 col-span-1">Avg ready</span>
        <span className="text-right font-bold text-stone-800 pt-1.5 border-t border-stone-100 mt-1 col-span-1">{formatDur(d?.avg_ready_seconds)}</span>
        <span className="text-stone-400 text-xs col-span-1">Avg serve</span>
        <span className="text-right font-semibold text-stone-500 text-xs col-span-1">{formatDur(d?.avg_serve_seconds)}</span>
      </div>
    </div>
  );

  // Live "now" tick — drives the per-token delay indicator without forcing
  // a full reload of the active tokens list. Refreshes every 15s which is
  // accurate enough for an ops dashboard.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(i);
  }, []);
  // Host-configurable delay threshold (minutes) → seconds. Defaults to 5
  // minutes if the host hasn't set one. <=0 disables the indicator.
  const delayThresholdSec = Math.max(0, Number(restaurant?.paging_kpi_delay_minutes ?? 5)) * 60;
  // Active tokens sorted oldest-first so staff always works the queue head
  // (FIFO). The API returns newest-first; we re-sort on the client.
  const sortedActive = useMemo(
    () => [...activeTokens].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')),
    [activeTokens]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Paging</h2>
          <p className="text-sm text-stone-500 mt-1">Generate, print and track token QR-pagers for take-away & dine-in orders.</p>
        </div>
        {canSee('paging_settings') && (
        <Button variant="outline" size="sm" onClick={() => setShowSettings(s => !s)} className="gap-1.5" data-testid="paging-settings-toggle">
          <Settings size={14} /> {showSettings ? 'Hide' : 'Show'} settings
        </Button>
        )}
      </div>

      {/* Optional settings collapse — saves token suffix, message, thanks */}
      {showSettings && canSee('paging_settings') && (
        <Card className="border-stone-200">
          <CardHeader><CardTitle className="text-base">Paging printout — text & suffix defaults</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Header text (defaults to restaurant name)</Label>
                <Input
                  data-testid="paging-set-title"
                  value={restaurant?.paging_title_text || ''}
                  onChange={e => setRestaurant(r => ({ ...r, paging_title_text: e.target.value }))}
                  onBlur={e => savePagingSettings({ paging_title_text: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">"Thank you" footer</Label>
                <Input
                  data-testid="paging-set-thanks"
                  value={restaurant?.paging_thanks_text || ''}
                  onChange={e => setRestaurant(r => ({ ...r, paging_thanks_text: e.target.value }))}
                  onBlur={e => savePagingSettings({ paging_thanks_text: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Body message (printed in blue under QR)</Label>
              <Input
                data-testid="paging-set-message"
                value={restaurant?.paging_message_text || ''}
                onChange={e => setRestaurant(r => ({ ...r, paging_message_text: e.target.value }))}
                onBlur={e => savePagingSettings({ paging_message_text: e.target.value })}
              />
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Suffix — Dine-in (default "D")</Label>
                <Input
                  data-testid="paging-set-suffix-dinein"
                  value={restaurant?.paging_suffix_dinein || ''}
                  placeholder="D"
                  onChange={e => setRestaurant(r => ({ ...r, paging_suffix_dinein: e.target.value }))}
                  onBlur={e => savePagingSettings({ paging_suffix_dinein: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Suffix — Take-away (default "T")</Label>
                <Input
                  data-testid="paging-set-suffix-takeaway"
                  value={restaurant?.paging_suffix_takeaway || ''}
                  placeholder="T"
                  onChange={e => setRestaurant(r => ({ ...r, paging_suffix_takeaway: e.target.value }))}
                  onBlur={e => savePagingSettings({ paging_suffix_takeaway: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Suffix override per token (one-off)</Label>
                <Input
                  data-testid="paging-input-suffix"
                  value={form.suffix_override}
                  onChange={e => setForm(f => ({ ...f, suffix_override: e.target.value }))}
                  placeholder="leave empty to use defaults"
                  maxLength={4}
                />
              </div>
            </div>

            {/* Show-menu toggle — when ON, the customer's paging QR landing
                renders a "View Menu" button that opens a read-only menu
                sheet (no add-to-cart). Food ordering is a separate module
                gated by package capabilities; paging only needs the menu
                preview. */}
            <div className="flex items-start justify-between gap-4 pt-3 border-t border-stone-100">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-stone-800">Show food menu to customers</p>
                <p className="text-xs text-stone-500 mt-0.5">When ON, the paging QR landing page shows a <strong>View Menu</strong> button (view only — no ordering).</p>
              </div>
              <Switch
                data-testid="paging-set-show-menu"
                checked={!!restaurant?.paging_show_menu}
                onCheckedChange={(v) => {
                  setRestaurant(r => ({ ...r, paging_show_menu: v }));
                  savePagingSettings({ paging_show_menu: v });
                }}
              />
            </div>

            {/* KPI delay threshold (host only). Active tokens older than
                this start flashing a red "DELAYED" badge so staff can spot
                bottlenecks. Staff are intentionally not allowed to change
                this — it's an ops policy decision. Set to 0 to disable. */}
            {!isStaff && (
              <div className="flex items-start justify-between gap-4 pt-3 border-t border-stone-100">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-stone-800">Delay alert threshold (minutes)</p>
                  <p className="text-xs text-stone-500 mt-0.5">Active tokens older than this start flashing a <strong>DELAYED</strong> badge. Set to <strong>0</strong> to disable.</p>
                </div>
                <Input
                  type="number"
                  min={0}
                  max={120}
                  step={1}
                  data-testid="paging-set-kpi-delay"
                  className="w-24 text-right tabular-nums"
                  value={restaurant?.paging_kpi_delay_minutes ?? 5}
                  onChange={e => {
                    const v = Number(e.target.value);
                    setRestaurant(r => ({ ...r, paging_kpi_delay_minutes: isNaN(v) ? 0 : v }));
                  }}
                  onBlur={e => {
                    const v = Number(e.target.value);
                    savePagingSettings({ paging_kpi_delay_minutes: isNaN(v) ? 0 : v });
                  }}
                />
              </div>
            )}

            {/* Danger zone — host only. Wipes ALL paging records so the host
                can clear test data before opening the till for real. */}
            {!isStaff && (
              <div className="mt-2 pt-4 border-t border-red-100">
                <p className="text-sm font-semibold text-red-700">Danger zone</p>
                <p className="text-xs text-stone-500 mt-0.5">Use this to clear test orders before launching. Cannot be undone.</p>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="paging-reset-logs-btn"
                  onClick={() => setResetOpen(true)}
                  className="mt-2 gap-1.5 border-red-300 text-red-700 hover:bg-red-50"
                >
                  <RotateCcw size={14} /> Reset Paging Logs
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* === GIANT generate row — fast-food sized controls === */}
      {/* Layout: 12-column grid; Token (3) + Type (2) + Generate (2) + KPI (5).
          Type & Generate are intentionally narrow (50% slimmer than the old
          3-col layout) but their content fonts are unchanged — the wide KPI
          panel fills the freed space and is now the visual focus of the
          page, surfacing motivational sales insights at a glance. */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* TOKEN NUMBER pad */}
        <div className="md:col-span-3">
          <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Token Number *</Label>
          <div className="mt-1 relative">
            <Input
              data-testid="paging-input-token"
              value={form.token}
              onChange={e => setForm(f => ({ ...f, token: e.target.value }))}
              placeholder="234"
              onKeyDown={e => { if (e.key === 'Enter') generate(); }}
              autoFocus
              inputMode="numeric"
              className="w-full h-40 md:h-56 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold tabular-nums text-center bg-[#0f5a78] hover:bg-[#0e5170] focus:bg-[#0e5170] text-white border-2 border-[#0c4a63] rounded-2xl shadow-lg placeholder:text-white/35"
            />
          </div>
        </div>

        {/* ORDER TYPE — stacked vertical buttons (now 2 cols, fonts kept big) */}
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider font-bold text-stone-500">Order Type</Label>
          <div className="mt-1 grid grid-rows-2 gap-2 h-40 md:h-56">
            <button
              type="button"
              data-testid="paging-type-dinein"
              onClick={() => setForm(f => ({ ...f, order_type: 'dinein' }))}
              className={`rounded-2xl text-2xl md:text-3xl font-extrabold uppercase tracking-wider shadow-lg transition-colors border-2 px-1 ${
                form.order_type === 'dinein'
                  ? 'bg-[#0f5a78] text-white border-[#0c4a63]'
                  : 'bg-stone-300 text-stone-700 border-stone-400 hover:bg-stone-400'
              }`}
            >Dine&nbsp;In</button>
            <button
              type="button"
              data-testid="paging-type-takeaway"
              onClick={() => setForm(f => ({ ...f, order_type: 'takeaway' }))}
              className={`rounded-2xl text-2xl md:text-3xl font-extrabold uppercase tracking-wider shadow-lg transition-colors border-2 px-1 ${
                form.order_type === 'takeaway'
                  ? 'bg-[#0f5a78] text-white border-[#0c4a63]'
                  : 'bg-stone-300 text-stone-700 border-stone-400 hover:bg-stone-400'
              }`}
            >Take&nbsp;Away</button>
          </div>
        </div>

        {/* GENERATE & PRINT — orange CTA (now 2 cols, font unchanged) */}
        <div className="md:col-span-2 flex flex-col">
          <Label className="text-xs uppercase tracking-wider font-bold text-stone-500 invisible">.</Label>
          <button
            type="button"
            data-testid="paging-generate-btn"
            onClick={generate}
            disabled={submitting || !form.token.trim()}
            className={`mt-1 h-40 md:h-56 w-full rounded-2xl text-2xl md:text-3xl font-extrabold uppercase tracking-wider shadow-lg transition-colors border-2 flex flex-col items-center justify-center gap-2 px-1 ${
              submitting || !form.token.trim()
                ? 'bg-orange-300 text-white border-orange-400 cursor-not-allowed'
                : 'bg-orange-600 hover:bg-orange-700 text-white border-orange-800'
            }`}
          >
            <Printer size={32} strokeWidth={2.5} />
            {submitting ? 'Generating…' : <>Generate<br/>&amp; Print</>}
          </button>
        </div>

        {/* KPI / sales push panel — auto-generated from today/week/month stats.
            Now spans 5 of 12 cols (was 3) so we have room for a 7-day bar
            chart + multiple insight tiles. */}
        <div className="md:col-span-5 flex flex-col">
          <Label className="text-xs uppercase tracking-wider font-bold text-stone-500 invisible">.</Label>
          <KpiPushCard stats={stats} className="mt-1 h-40 md:h-56" />
        </div>
      </div>

      {/* Stats — today / week / month (placed below active tokens per UX:
          the host needs the live work-queue visible above the fold; stats
          are a secondary glance). */}
      {/* (rendered below) */}

      {/* Active tokens — hoisted ABOVE the stats so the working set is
          always the first thing the host sees after generating a token. */}
      <Card className="border-stone-200">
        <CardHeader><CardTitle className="text-base">Active tokens ({activeTokens.length})</CardTitle></CardHeader>
        <CardContent>
          {activeTokens.length === 0 && (
            <p className="text-sm text-stone-500 py-6 text-center">No active tokens. Generate one above.</p>
          )}
          {/* Auto-fill responsive grid — each card has a minimum width so
              "No Response" and 4-digit tokens never truncate, and the row
              fills with as many cards as the viewport allows (typically
              4-5 on a 1440px desktop, ~half the width of the previous
              3-col layout). */}
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {sortedActive.map(t => {
              const isReady = t.status === 'ready';
              // Token "age" since created. Once Ready is pressed the delay
              // freezes — kitchen finished its job, customer pickup is a
              // separate concern. Threshold of 0 disables the badge.
              const ageSec = Math.max(0, Math.floor((now - new Date(t.created_at).getTime()) / 1000));
              const isDelayed = delayThresholdSec > 0 && !isReady && ageSec > delayThresholdSec;
              const ageMin = Math.floor(ageSec / 60);
              // Severely delayed = past 15 min. Triggers the full chasing
              // neon perimeter so the host's eye is drawn even when the
              // dashboard is across the kitchen.
              const isSeverelyDelayed = isDelayed && ageMin >= 15;
              return (
              <div
                key={t.id}
                data-testid={`paging-active-${t.id}`}
                className={`relative rounded-xl p-3 grid grid-cols-2 gap-2 transition-colors ${
                  isDelayed
                    ? 'border-2 border-red-400 bg-red-50 animate-pulse-slow'
                    : 'border border-orange-200 bg-orange-50'
                } ${isSeverelyDelayed ? 'paging-neon-chase' : ''}`}
              >
                {/* Top-right corner neon dot — blinking red light that
                    pulses for every delayed token. Sits in the absolute
                    corner so it never crowds the token number itself. */}
                {isDelayed && (
                  <span
                    aria-hidden="true"
                    data-testid={`paging-delayed-dot-${t.id}`}
                    className="paging-neon-dot absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-red-600"
                  />
                )}
                {/* Left column: token + time + Ready / Served (vertical stack) */}
                <div className="flex flex-col gap-2 min-w-0">
                  <p className="text-3xl sm:text-4xl font-extrabold text-stone-900 tabular-nums leading-none pr-3">{t.display_token}</p>
                  <p className="text-sm text-stone-600 font-semibold">
                    {new Date(t.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    {isReady && <span className="ml-1 text-blue-700 uppercase tracking-wide text-xs">· Alerted</span>}
                  </p>
                  <Button
                    onClick={() => setStatus(t.id, 'ready')}
                    data-testid={`paging-ready-${t.id}`}
                    className={`h-10 text-base font-bold rounded-lg w-full gap-1 ${
                      isReady
                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                        : 'bg-[#0f5a78] hover:bg-[#0c4a63] text-white shadow-sm'
                    }`}
                    title={isReady ? 'Re-alert the customer (in case they missed it)' : 'Alert the customer that the order is ready'}
                  >
                    <Bell size={16} /> {isReady ? 'Re-alert' : 'Ready'}
                  </Button>
                  <Button
                    onClick={() => setStatus(t.id, 'served')}
                    disabled={!isReady}
                    data-testid={`paging-serve-${t.id}`}
                    className={`h-10 text-base font-bold rounded-lg w-full gap-1 ${
                      isReady
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm'
                        : 'bg-emerald-200 text-emerald-700 cursor-not-allowed opacity-70'
                    }`}
                    title={isReady ? 'Mark order as served' : 'Press Ready first'}
                  >
                    <Check size={16} /> Served
                  </Button>
                </div>
                {/* Right column: Cancel / Refund / No Response / Re-print */}
                <div className="flex flex-col gap-2 min-w-0">
                  <Button
                    variant="outline"
                    onClick={() => setStatus(t.id, 'cancelled')}
                    data-testid={`paging-cancel-${t.id}`}
                    className="h-10 text-base font-bold rounded-lg w-full gap-1 border-red-300 text-red-700 hover:bg-red-50 bg-white"
                  >
                    <X size={16} /> Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setRefundConfirm(t)}
                    data-testid={`paging-refund-${t.id}`}
                    className="h-10 text-base font-bold rounded-lg w-full gap-1 border-red-300 text-red-700 hover:bg-red-50 bg-white"
                  >
                    <RotateCcw size={16} /> Refund
                  </Button>
                  <Button
                    onClick={() => setStatus(t.id, 'no_response')}
                    data-testid={`paging-noresponse-${t.id}`}
                    className="h-10 text-sm font-bold rounded-lg w-full gap-1 bg-[#0f5a78] hover:bg-[#0c4a63] text-white shadow-sm whitespace-nowrap"
                  >
                    <BellOff size={14} /> No&nbsp;Response
                  </Button>
                  <button
                    type="button"
                    onClick={() => openPrintWindow(t)}
                    data-testid={`paging-reprint-${t.id}`}
                    className="h-9 text-sm text-stone-600 hover:text-stone-900 flex items-center justify-center gap-1 font-medium"
                  >
                    <Printer size={14} /> Re-print
                  </button>
                </div>
                {/* Delayed banner — moved BELOW the Re-print button so it
                    never overlaps the token number. Spans the full card so
                    the "Delayed · 17m" message is legible at a glance. */}
                {isDelayed && (
                  <div
                    data-testid={`paging-delayed-${t.id}`}
                    className={`col-span-2 mt-1 rounded-md px-2 py-1 text-[11px] font-extrabold uppercase tracking-wider text-white text-center ${
                      isSeverelyDelayed ? 'bg-red-700' : 'bg-red-600'
                    }`}
                    title={`Older than ${Math.ceil(delayThresholdSec / 60)} min`}
                  >
                    {isSeverelyDelayed ? 'Severely Delayed' : 'Delayed'} · {ageMin}m
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Stats — today / week / month (placed AFTER active tokens). Each
          column is independently gated for staff via Access Control. The
          row collapses cleanly when staff has zero permissions. */}
      {(canSee('paging_stats_today') || canSee('paging_stats_week') || canSee('paging_stats_month')) && (
        <div className="grid sm:grid-cols-3 gap-3">
          {canSee('paging_stats_today') && <StatBlock title="Today" d={stats.today} />}
          {canSee('paging_stats_week')  && <StatBlock title="Week"  d={stats.week} />}
          {canSee('paging_stats_month') && <StatBlock title="Month" d={stats.month} />}
        </div>
      )}

      {/* Recent log — gated by paging_sales_log for staff. Includes a date
          filter (Today / Week / Month / Custom range) wired straight to the
          backend, and a 20-row tall scrollable viewport with sticky header. */}
      {canSee('paging_sales_log') && (
      <Card className="border-stone-200">
        <CardHeader className="flex flex-row items-start justify-between gap-3 flex-wrap space-y-0">
          <div>
            <CardTitle className="text-base">Sales log</CardTitle>
            <p className="text-xs text-stone-500 mt-1">{recent.length} record{recent.length === 1 ? '' : 's'} in selected range</p>
          </div>
          {/* Filter bar — pill buttons + optional date-range pickers */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="paging-log-filter-bar">
            {[
              { k: 'today',  label: 'Today' },
              { k: 'week',   label: 'Week' },
              { k: 'month',  label: 'Month' },
              { k: 'custom', label: 'Custom' },
            ].map(opt => (
              <button
                key={opt.k}
                type="button"
                data-testid={`paging-log-filter-${opt.k}`}
                onClick={() => setLogFilter(opt.k)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${
                  logFilter === opt.k
                    ? 'bg-[#0f5a78] text-white border-[#0c4a63] shadow-sm'
                    : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-100'
                }`}
              >{opt.label}</button>
            ))}
            {logFilter === 'custom' && (
              <div className="flex items-center gap-1.5 ml-1">
                <Input
                  type="date"
                  data-testid="paging-log-filter-from"
                  value={customRange.from}
                  max={customRange.to}
                  onChange={e => setCustomRange(r => ({ ...r, from: e.target.value }))}
                  className="h-8 text-xs w-[140px]"
                />
                <span className="text-stone-400 text-xs">→</span>
                <Input
                  type="date"
                  data-testid="paging-log-filter-to"
                  value={customRange.to}
                  min={customRange.from}
                  onChange={e => setCustomRange(r => ({ ...r, to: e.target.value }))}
                  className="h-8 text-xs w-[140px]"
                />
              </div>
            )}
            {/* Export the currently-filtered list as CSV — host & permitted
                staff both see this. Disabled when there's nothing to export
                so we don't ship empty files. */}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={exportSalesLogCsv}
              disabled={!recent || recent.length === 0}
              data-testid="paging-log-export-csv"
              className="h-8 ml-1 gap-1.5 border-stone-300 text-stone-700 hover:bg-stone-50"
              title="Download the records in the selected range as a CSV file"
            >
              <Download size={14} /> Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* 20-row viewport: header (~44px) + 20 rows (~36px each) ≈ 760px.
              We cap at 720px so very tall screens still show "scroll affordance". */}
          <div className="overflow-x-auto overflow-y-auto max-h-[720px]" data-testid="paging-log-scroll">
            <table className="w-full text-base" data-testid="paging-recent-table">
              <thead className="bg-stone-50 text-stone-600 border-b border-stone-200 text-sm sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Token</th>
                  <th className="px-4 py-3 text-left font-bold">Type</th>
                  <th className="px-4 py-3 text-left font-bold">Status</th>
                  <th className="px-4 py-3 text-left font-bold">Created</th>
                  <th className="px-4 py-3 text-left font-bold">Ready in</th>
                  <th className="px-4 py-3 text-left font-bold">Served in</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {recent.map(r => (
                  <tr key={r.id} data-testid={`paging-row-${r.id}`} className="hover:bg-stone-50">
                    <td className="px-4 py-3 font-extrabold tabular-nums text-lg">{r.display_token}</td>
                    <td className="px-4 py-3 text-stone-700 font-medium">{r.order_type === 'dinein' ? 'Dine-in' : 'Take-away'}</td>
                    <td className="px-4 py-3">
                      <Badge className={
                        r.status === 'served' ? 'bg-emerald-100 text-emerald-700 border-emerald-200 text-sm' :
                        r.status === 'active' ? 'bg-amber-100 text-amber-700 border-amber-200 text-sm' :
                        r.status === 'ready'  ? 'bg-blue-100 text-blue-700 border-blue-200 text-sm' :
                        r.status === 'no_response' ? 'bg-stone-200 text-stone-700 border-stone-300 text-sm' :
                        'bg-red-100 text-red-700 border-red-200 text-sm'
                      }>{r.status === 'no_response' ? 'no response' : r.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-stone-600 text-sm whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString(undefined, { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                    </td>
                    <td className="px-4 py-3 text-stone-800 font-semibold">{formatDur(r.ready_duration_seconds)}</td>
                    <td className="px-4 py-3 text-stone-800 font-semibold">{formatDur(r.served_duration_seconds)}</td>
                  </tr>
                ))}
                {recent.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-stone-500">No tokens in selected range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Refund confirmation — RECORD-only action (the real money refund
          happens at the POS / card terminal). We make this explicit in the
          dialog so staff don't think pressing Refund actually moves money. */}
      <AlertDialog open={!!refundConfirm} onOpenChange={(o) => !o && setRefundConfirm(null)}>
        <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <RotateCcw size={18} /> Mark token {refundConfirm?.display_token} as refunded?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This only <strong>records</strong> the refund in the paging log for reporting.
              You still need to process the actual refund on your POS / card terminal.
              The token will be removed from the active list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="paging-refund-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="paging-refund-confirm-btn"
              onClick={confirmRefund}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, mark as refunded
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset confirmation — destructive action, host only */}
      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <RotateCcw size={18} /> Reset Paging Logs?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>every paging record</strong> for this restaurant — generated tokens, ready / served / cancelled / refunded history, push subscriptions, and all daily / weekly / monthly stats will go back to zero. Use this <em>before launch</em> to clear test orders. <strong>This cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="paging-reset-cancel-btn" disabled={resetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="paging-reset-confirm-btn"
              onClick={resetLogs}
              disabled={resetting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {resetting ? 'Resetting…' : 'Yes, reset everything'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

